import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Users, Receipt, Bell, Leaf, Sheet, CheckCircle, AlertCircle, UploadCloud, Mail, MessageSquare, CreditCard, ClipboardList, MapPin, Lock, Download, Database, ChevronDown, Plug, Wallet, Landmark, RefreshCw, Trash2, CalendarClock, Gift, Globe, Footprints, Zap, Snowflake, Search, Crosshair } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import ConnectBankButton from '../components/ConnectBankButton';
import { businessToday, fmtTs, isoPlusDays } from '../utils/time';
import { useReadOnly } from '../hooks/useReadOnly';
import { useAuth } from '../services/AuthContext';

function useSettings() {
  return useQuery('app-settings', () =>
    api.get('/settings').then(r => r.data.settings as Record<string, string>)
  );
}

// The morning "we'll be by to mow your lawn today" text (server/services/dayTexts.js).
// Its switch and send time have their own route, not PUT /settings: the server
// stamps the moment the switch moves, so On never starts on the morning it is tapped.
type DayTextMode = 'off' | 'rehearsal' | 'on';
type DayTextSettings = {
  mode: DayTextMode; send_time: string; send_time_label: string; earliest: string; latest: string;
  changed_at: string | null; starts_tomorrow: boolean; owner_phone_ending: string | null;
  modes: { value: DayTextMode; label: string; help: string }[]; example_text: string;
  // One sentence that is true for the state the switch is in right now ("On. Today's texts go at 7:30 AM.",
  // "On. Today's texts went at 7:31 AM: 12 texted, 2 left out."). The server's words — the same line the list opens with.
  today_line: string;
  // The second block of the section, "You're next". Absent from a server older than this screen: the block is then not drawn.
  // null with next_failed when the server could not read it: the block then says "could not load", never "Off".
  next?: NextTextSettings | null;
  next_failed?: boolean;
  // Stops skipped today after their customer was told we were coming, and the one line that says he is texted about them.
  told_skipped?: { about: string; today: { id: number; customer_id: number; name: string; address: string | null; line: string; owner_texted: boolean | null }[] };
  // The third block, "After the mow": the photo card and the referral offer (server/services/mowDoneText.js). Same rule as `next`.
  done?: DoneTextSettings | null;
  done_failed?: boolean;
  // The fourth block, the visit photo report (server/services/visitReport.js): the crew's photos of ANY finished job
  // and the 48-hour come-back promise, for every customer. Same rule as `next`.
  report?: VisitReportSettings | null;
  report_failed?: boolean;
};
// The visit photo report — owner, Nov 1 2026: "visit photo reports for every client (not just commercial): after a
// job is marked done, send the customer the crew's after photos plus 'Not happy with anything? Reply within 48
// hours and we'll come back free.'" Rehearsal texts each report to HIS phone, never a customer.
type VisitReportRow = {
  id: number; customer_id: number; job_id: number | null; name: string; phone_ending: string | null;
  status: 'sent' | 'would_send' | 'waiting' | 'left_out' | 'failed';
  at_label: string | null; line: string; photos: number;
};
type VisitReportSettings = {
  mode: DayTextMode; changed_at: string | null;
  about: string[]; sets_off: string;
  modes: { value: DayTextMode; label: string; help: string }[];
  example_text: string; example_text_with_offer: string; example_text_no_name: string;
  mow_done_mode: DayTextMode | null; promise_hours: number;
  wait_seconds: number; from_label: string; until_label: string; owner_phone_ending: string | null;
  photos_persistent: boolean;
  counts: DoneTextCounts; today_line: string; today: VisitReportRow[];
};
// "After the mow" — the crew's photo of the finished lawn on a branded card, a caption the AI writes, and the
// customer's own refer-a-friend link. The offer's percent and sentence are his to edit here.
type DoneTextRow = {
  id: number; customer_id: number; job_id: number | null; name: string; phone_ending: string | null;
  status: 'sent' | 'would_send' | 'waiting' | 'left_out' | 'failed';
  at_label: string | null; line: string; has_picture: boolean;
};
type DoneTextCounts = { sent: number; would_send: number; waiting: number; held_back: number; failed: number };
type DoneTextSettings = {
  mode: DayTextMode; changed_at: string | null;
  offer_pct: number; offer_words: string; offer_line: string; default_words: string; default_pct: number;
  about: string[]; sets_off: string;
  modes: { value: DayTextMode; label: string; help: string }[];
  example_text: string; example_text_no_photo: string; example_texts?: string[]; season?: string; example_caption: string;
  wait_seconds: number; from_label: string; until_label: string; owner_phone_ending: string | null;
  can_draw: boolean; ai: boolean; photos_persistent: boolean;
  counts: DoneTextCounts; today_line: string; today: DoneTextRow[];
};
// "You're our next stop" — its own switch, with no send time: a crew member's live Finish on the yard before sets
// it off. A Skip never does, and neither does a stop finished from the office or the owner's own Field page.
// `office` / `after_skip`: stops where nothing was set off because the yard before was marked done from the office
// or his Field page, or was skipped — counted apart from "held back", and shown as one line per route, not a row each.
type NextTextCounts = { sent: number; would_send: number; held_back: number; failed: number; claimed: number; office?: number; after_skip?: number };
type NextTextSettings = {
  mode: DayTextMode; changed_at: string | null;
  about: string[];                                   // the two plain sentences: what it does, and when it stays quiet
  sets_off?: string;                                 // whose tap counts: "A crew member's Finish sets it off. Skips, stops marked done from the office, and stops you finish on your own Field page never do."
  modes: { value: DayTextMode; label: string; help: string }[];
  example_text: string; example_text_no_address: string;
  hold_seconds: number; from_label: string; until_label: string; owner_phone_ending: string | null;
  counts: NextTextCounts;                            // today
  today_line: string;                                // "Rehearsal. 4 would have gone, 2 held back today. No you're-next text was sent, to customers or to you."
};
function useDayTextSettings() {
  return useQuery<DayTextSettings>('day-texts-settings', () => api.get('/day-texts/settings').then(r => r.data), { retry: false });
}

function Section({ title, icon: Icon, children, id }: { title: string; icon: React.ElementType; children: React.ReactNode; id?: string }) {
  return (
    <div className="bg-white shadow rounded-lg scroll-mt-20" id={id}>
      <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
        <Icon className="h-5 w-5 text-brand-600" />
        <h3 className="text-lg font-black uppercase tracking-tight text-gray-900">{title}</h3>
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  );
}

/** One line in the Connections card: name · state pill · what it means for Michael. */
function StatusLine({ label, ok, warn, text, note }: { label: string; ok?: boolean; warn?: boolean; text: string; note?: string }) {
  const pill = ok
    ? 'bg-green-50 text-green-700 border-green-200'
    : warn ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-500 border-gray-200';
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="w-28 flex-shrink-0 text-sm font-medium text-gray-800">{label}</span>
      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${pill}`}>
        {ok ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />} {text}
      </span>
      {note && <span className="text-xs text-gray-500">{note}</span>}
    </div>
  );
}

const SAVE_BTN = 'press inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl text-black bg-brand-600 hover:bg-brand-500 disabled:opacity-50';
const INPUT = 'block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500 text-sm';
/** '20:00' → '8:00 PM', for the quiet-hours words. */
const quietWords = (hm: string) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm);
  if (!m) return hm;
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
};

function FieldRow({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {help && <p className="text-xs text-gray-500 mt-0.5 mb-1">{help}</p>}
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, label, sublabel }: { checked: boolean; onChange: (v: boolean) => void; label: string; sublabel?: string }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <div className="relative mt-0.5">
        <input type="checkbox" className="sr-only" checked={checked} onChange={e => onChange(e.target.checked)} />
        <div className={`w-10 h-6 rounded-full transition-colors ${checked ? 'bg-brand-600' : 'bg-gray-300'}`} />
        <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </div>
      <div>
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {sublabel && <p className="text-xs text-gray-500 mt-0.5">{sublabel}</p>}
      </div>
    </label>
  );
}

// ── The page's sections ────────────────────────────────────────────────────────
// Owner, Nov 2 2026: "revamp the settings page with sections, easier understanding." Eight sections, a menu on the
// left (tabs on a phone), a search box, one Save per section. Every block that was on the page is still here;
// only where it sits changed. /settings#billing (a section) and /settings#late-fees (a block) both land where they say.
type SectionId = 'company' | 'texts' | 'bids' | 'billing' | 'crew' | 'money' | 'automations' | 'admin';
type SectionDef = { id: SectionId; label: string; icon: React.ElementType; blurb: string };
const SECTIONS: SectionDef[] = [
  { id: 'company', label: 'Company', icon: Building2, blurb: 'Who you are and where you work: the name on bills, the mowing season, where the routes start, and how far out you go.' },
  { id: 'texts', label: 'Texts & email', icon: MessageSquare, blurb: 'Every text the app sends on its own: which ones are on, when late texts wait until morning, and the texts on a mowing day.' },
  { id: 'bids', label: 'Bids & quotes', icon: ClipboardList, blurb: 'How a price is worked out and offered: automatic bids, the instant quote page, the plans on a bid, winter work, and the visits customers can book.' },
  { id: 'billing', label: 'Billing', icon: Receipt, blurb: 'When bills are made and sent, what comes off them, late fees, and whether a card on file is charged.' },
  { id: 'crew', label: 'Crew & pay', icon: Users, blurb: 'What the crew are paid and how often, the weekly lawn inspections, and who does treatment rounds.' },
  { id: 'money', label: 'Money', icon: Landmark, blurb: 'The bank and the Ramp card that feed Cash Flow, and the morning text saying how much money you have.' },
  { id: 'automations', label: 'Automations & logs', icon: Zap, blurb: 'Everything the app does by itself, whether the crew photos are being kept, and copies of your data.' },
  { id: 'admin', label: 'Admin', icon: Lock, blurb: 'Your PIN, who is signed in, what is connected, the go-live checklist, and the practice data.' },
];
// Every block on the page, the section it sits in, and its title — the search box and deep links read this.
const BLOCKS: { id: string; section: SectionId; title: string }[] = [
  { id: 'your-business', section: 'company', title: 'Your business' },
  { id: 'mowing-season', section: 'company', title: 'Mowing season' },
  { id: 'home-base', section: 'company', title: 'Where the routes start' },
  { id: 'service-area', section: 'company', title: 'Service area' },
  { id: 'field-day', section: 'company', title: 'Your day on the Field page' },
  { id: 'auto-start', section: 'company', title: 'Start stops by location' },
  { id: 'text-switches', section: 'texts', title: 'Texts the app sends' },
  { id: 'mowing-day-texts', section: 'texts', title: 'Mowing-day texts' },
  { id: 'twilio', section: 'texts', title: 'The business number (Twilio)' },
  { id: 'email', section: 'texts', title: 'Email' },
  { id: 'automatic-quotes', section: 'bids', title: 'Automatic quotes' },
  { id: 'instant-quote', section: 'bids', title: 'Instant quote page' },
  { id: 'plan-offers', section: 'bids', title: 'Plan offers on a bid' },
  { id: 'treatment-plans', section: 'bids', title: 'Treatment plans' },
  { id: 'winter-work', section: 'bids', title: 'Winter work' },
  { id: 'quote-visits', section: 'bids', title: 'Quote visits customers can book' },
  { id: 'walkthroughs', section: 'bids', title: 'Lawn walkthroughs' },
  { id: 'billing-automation', section: 'billing', title: 'Bills' },
  { id: 'late-fees', section: 'billing', title: 'Late fees' },
  { id: 'stripe', section: 'billing', title: 'Stripe (cards)' },
  { id: 'crew-pay', section: 'crew', title: 'Pay' },
  { id: 'inspections', section: 'crew', title: 'Inspections' },
  { id: 'treatment-visits', section: 'crew', title: 'Treatment visits' },
  { id: 'bank-accounts', section: 'money', title: 'Bank accounts' },
  { id: 'automations-list', section: 'automations', title: 'Automations' },
  { id: 'photo-storage', section: 'automations', title: 'Crew photos' },
  { id: 'backup', section: 'automations', title: 'Backup' },
  { id: 'google-sheets', section: 'automations', title: 'Google Sheets' },
  { id: 'admin-pin', section: 'admin', title: 'Admin PIN' },
  { id: 'security', section: 'admin', title: 'Security' },
  { id: 'connections', section: 'admin', title: 'Connections' },
  { id: 'go-live', section: 'admin', title: 'Go-live checklist & tools' },
  { id: 'practice', section: 'admin', title: 'Practice data' },
];
const SECTION_OF: Record<string, SectionId> = Object.fromEntries(BLOCKS.map(b => [b.id, b.section]));
const BLOCK_NAME: Record<string, string> = Object.fromEntries(BLOCKS.map(b => [b.id, b.title]));
const SECTION_LABEL: Record<string, string> = Object.fromEntries(SECTIONS.map(s => [s.id, s.label]));
/** A section id, a block id, or neither — what a hash or a search pick resolves to. */
function sectionFor(id: string): SectionId | null {
  if (SECTIONS.some(s => s.id === id)) return id as SectionId;
  return SECTION_OF[id] || null;
}
// What the search box finds: a setting's name, and the block it is in. `words` are other things people call it.
const SETTING_INDEX: { label: string; at: string; words?: string }[] = [
  { label: 'Business name', at: 'your-business', words: 'company' }, { label: 'Phone', at: 'your-business', words: 'business number' },
  { label: 'Email', at: 'your-business', words: 'business email' }, { label: 'Address', at: 'your-business', words: 'invoices quotes printed' },
  { label: 'Season starts / ends', at: 'mowing-season', words: 'mowing season dates' }, { label: 'Mowing days', at: 'mowing-season', words: 'days of the week' },
  { label: 'Never schedule on Sundays', at: 'mowing-season', words: 'sunday' },
  { label: 'Where the routes start', at: 'home-base', words: 'home base start point CR 175 shop drive home' },
  { label: 'Charge for travel outside the area', at: 'service-area', words: 'travel line surcharge' },
  { label: 'Ring around each yard (miles)', at: 'service-area', words: 'service area radius' }, { label: 'Travel: every … miles adds $ per mow', at: 'service-area', words: 'travel step price' },
  { label: 'Grass share of the green area', at: 'service-area', words: 'turf county map lawn size square feet' }, { label: 'Size every yard again from the county map', at: 'service-area', words: 'turf restamp' },
  { label: 'Measure yards never measured', at: 'service-area', words: 'distance' },
  { label: 'Your day ends at', at: 'field-day', words: 'field page plan day end time optimize' },
  { label: 'Start stops by location', at: 'auto-start', words: 'auto start geofence gps arrive dwell radius zero taps hands free' },
  { label: 'When I leave a yard', at: 'auto-start', words: 'auto complete done prompt finish geofence leave' },
  { label: 'Text customers when a visit moves', at: 'text-switches', words: 'reschedule rain moved' }, { label: 'Text me every crew note', at: 'text-switches', words: 'crew notes photo' },
  { label: 'Ask for a Google review a day after a visit', at: 'text-switches', words: 'review ask $10' }, { label: 'Treatment texts', at: 'text-switches', words: 'water it in mow before' },
  { label: 'Instant-price reply to new leads', at: 'text-switches', words: 'lead reply website form' }, { label: 'Text me when a lead has no reply after 1 business hour', at: 'text-switches', words: 'lead alert' },
  { label: 'Text a website visitor a confirmation', at: 'text-switches', words: 'lead confirm thank you' }, { label: 'Forward customer texts to my cell', at: 'text-switches', words: 'inbound forward copy' },
  { label: 'Hold my late texts until morning', at: 'text-switches', words: 'quiet hours held texts night' }, { label: 'Quiet hours (from / until)', at: 'text-switches', words: 'quiet from until time' },
  { label: 'The morning "we\'ll mow today" text', at: 'mowing-day-texts', words: 'mow today morning text send time rehearsal' }, { label: 'Rain delay text', at: 'mowing-day-texts', words: 'rain makeup saturday' },
  { label: '"You\'re next" text', at: 'mowing-day-texts', words: 'next stop on the way' }, { label: 'After the mow: photo card + referral', at: 'mowing-day-texts', words: 'done text picture refer a friend offer' },
  { label: 'Visit photo report to the customer', at: 'mowing-day-texts', words: '48 hours come back free after photos' }, { label: 'Who gets which texts', at: 'mowing-day-texts', words: 'STOP opt out per customer' },
  { label: 'Twilio (the business number)', at: 'twilio', words: 'sms configured' }, { label: 'Send a test text', at: 'twilio', words: 'test sms' },
  { label: 'Email provider', at: 'email', words: 'smtp resend configured' }, { label: 'Send a test email', at: 'email' },
  { label: 'Draft a mowing bid for new website leads', at: 'automatic-quotes', words: 'auto quote' }, { label: 'Also text the lead their bid link right away', at: 'automatic-quotes', words: 'auto send bid' },
  { label: 'Text me when a customer opens a bid', at: 'automatic-quotes', words: 'bid opened' }, { label: 'Offer lawn plans on new bids', at: 'automatic-quotes', words: 'plans default' },
  { label: 'One-off work needs a signed scope', at: 'automatic-quotes', words: 'one-off gate scope signed' }, { label: 'Photos before a one-off bill goes out', at: 'automatic-quotes', words: 'before after photo gate' },
  { label: 'Follow up on bids I send', at: 'automatic-quotes', words: 'bid follow-ups days call to-do' }, { label: 'Mowing price by lot size', at: 'automatic-quotes', words: 'price bands square feet per visit' },
  { label: 'Weekly discount %', at: 'automatic-quotes' }, { label: 'Biweekly adds $', at: 'automatic-quotes', words: 'every other week extra' },
  { label: 'Instant quote page is on', at: 'instant-quote', words: 'get-quote public page paused' }, { label: 'Book straight onto the schedule', at: 'instant-quote', words: 'autobook' },
  { label: 'Offer "front yard only"', at: 'instant-quote' }, { label: 'Only these zips', at: 'instant-quote', words: 'zip codes' },
  { label: 'Miles past your area', at: 'instant-quote' }, { label: 'Free visit: miles from the shop', at: 'instant-quote' }, { label: 'Crew day (minutes)', at: 'instant-quote', words: 'day cap' },
  { label: 'Days before the first mow', at: 'instant-quote', words: 'lead days' }, { label: 'One-time mow, % more', at: 'instant-quote' }, { label: 'Overgrown first cut, × the mow', at: 'instant-quote' },
  { label: 'Free visit, crossed-out $', at: 'instant-quote', words: '$99' },
  { label: 'Local prices (what the neighbours charge)', at: 'plan-offers', words: 'market going rate per application' }, { label: 'Offers on a bid (first month free, $ off, % off)', at: 'plan-offers', words: 'plan offer discount' },
  { label: 'Treatment plans (steps and prices)', at: 'treatment-plans', words: 'program template offered on customer pages' },
  { label: 'Offer winter work on bids', at: 'winter-work', words: 'winter discount' }, { label: 'Winter percent off / dates / text me from', at: 'winter-work', words: 'winter starts ends nudge' },
  { label: 'Windows to offer (quote visits)', at: 'quote-visits', words: 'booking slots times' }, { label: 'Quote visit days / length / gap', at: 'quote-visits' },
  { label: 'How far ahead / earliest hours (quote visits)', at: 'quote-visits', words: 'horizon' }, { label: 'Your phone\'s calendar (private link)', at: 'quote-visits', words: 'ical ics busy calendar link' },
  { label: 'Who comes out for a lawn walkthrough', at: 'walkthroughs', words: 'walkthrough bidder manager' },
  { label: 'Monthly billing', at: 'billing-automation' }, { label: 'Bill on the last day of the month', at: 'billing-automation', words: 'month-end generate monthly' },
  { label: 'Send month-end bills without my review', at: 'billing-automation', words: 'review & send auto' }, { label: 'Bill one-off work the day it\'s finished', at: 'billing-automation', words: 'bill on done one big invoice' },
  { label: 'Auto-charge autopay customers', at: 'billing-automation', words: 'card on file charge' }, { label: 'Billing cutoff day', at: 'billing-automation' },
  { label: 'Military discount %', at: 'billing-automation', words: 'veteran' }, { label: 'Invoice due days', at: 'billing-automation', words: 'net 30' },
  { label: 'Charge late fees', at: 'late-fees', words: 'late fee on off' }, { label: 'Late fee $ / grace days / interest % / start date', at: 'late-fees', words: 'late fee amount interest' },
  { label: 'Stripe: test or live', at: 'stripe', words: 'cards payments webhook' },
  { label: 'Default hourly rate', at: 'crew-pay', words: 'pay rate $18 $20' }, { label: 'Crew pay window', at: 'crew-pay', words: 'every two weeks monthly pay period' },
  { label: 'How pay works', at: 'crew-pay', words: 'drive time ledger' }, { label: 'Crew PINs', at: 'crew-pay', words: 'pin employees sign-in links' },
  { label: 'Lawns inspected per week', at: 'inspections', words: 'inspection sample' }, { label: 'Perfect lawns to pass', at: 'inspections' }, { label: 'Crew bonus $', at: 'inspections', words: 'inspection bonus $590' },
  { label: 'Applicator', at: 'treatment-visits', words: 'licence holder treatment rounds' }, { label: 'Minutes per treatment round', at: 'treatment-visits' },
  { label: 'Text me every morning how much money I have', at: 'bank-accounts', words: 'money morning text 7 AM' }, { label: 'Bank connection (Plaid)', at: 'bank-accounts', words: 'frost bank sync connect disconnect' },
  { label: 'Ramp card', at: 'bank-accounts', words: 'ramp purchases' },
  { label: 'Automations list', at: 'automations-list', words: 'everything the app does by itself switches' },
  { label: 'Crew photos storage', at: 'photo-storage', words: 'uploads volume persistent' },
  { label: 'Download everything (.xlsx)', at: 'backup', words: 'backup export excel' },
  { label: 'Google Sheets sync', at: 'google-sheets', words: 'spreadsheet id sheet names export' },
  { label: 'Change admin PIN', at: 'admin-pin', words: 'password login' },
  { label: 'Sign everyone out', at: 'security', words: 'lost phone tokens' }, { label: 'Security log (wrong PINs)', at: 'security', words: 'pin guessing twilio rejected' },
  { label: 'Connections at a glance', at: 'connections', words: 'status bank payments invoicing texts email sheets gps' },
  { label: 'Go-live checklist', at: 'go-live', words: 'stripe live keys twilio email' }, { label: 'Find missing map pins', at: 'go-live', words: 'geocode properties' },
  { label: 'Auto-correct all addresses', at: 'go-live', words: 'normalize addresses' }, { label: 'Crew GPS', at: 'go-live', words: 'location tracking geofence' },
  { label: 'Reset practice data', at: 'practice', words: 'practice test fake' },
];

// ── One Save per section ───────────────────────────────────────────────────────
// Every block tells the section it sits in "I have unsaved changes" and "here is how to save them". The section draws
// the one Save button and, when tapped, calls every dirty block's own save — the same call, the same payload, as the
// block's own button made before. A block with no Save (a switch that saves on tap) registers nothing.
type SaveReg = { dirty: boolean; busy: boolean; save: () => void };
const SectionSaveContext = React.createContext<((key: string, reg: SaveReg | null) => void) | null>(null);
function useSectionSave(key: string, dirty: boolean, busy: boolean, save: () => void) {
  const register = useContext(SectionSaveContext);
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!register) return;
    register(key, { dirty, busy, save: () => saveRef.current() });
    return () => register(key, null);
  }, [register, key, dirty, busy]);
}
/** For a block written inline in the page itself: registers its save with the section around it. Draws nothing. */
function RegisterSave({ k, dirty, busy, save }: { k: string; dirty: boolean; busy: boolean; save: () => void }) {
  useSectionSave(k, dirty, busy, save);
  return null;
}

function SectionPanel({ section, active, children }: { section: SectionDef; active: boolean; children: React.ReactNode }) {
  const readOnly = useReadOnly();
  const regs = useRef(new Map<string, SaveReg>());
  const [flags, setFlags] = useState<Record<string, { dirty: boolean; busy: boolean }>>({});
  const register = useCallback((key: string, reg: SaveReg | null) => {
    if (reg) regs.current.set(key, reg); else regs.current.delete(key);
    setFlags(f => {
      const was = f[key];
      if (!reg) { if (!was) return f; const next = { ...f }; delete next[key]; return next; }
      if (was && was.dirty === reg.dirty && was.busy === reg.busy) return f;
      return { ...f, [key]: { dirty: reg.dirty, busy: reg.busy } };
    });
  }, []);
  const list = Object.values(flags);
  const dirty = list.filter(x => x.dirty).length;
  const busy = list.some(x => x.busy);
  const saveAll = () => { regs.current.forEach(r => { if (r.dirty) r.save(); }); };
  const Icon = section.icon;
  return (
    <section id={section.id} hidden={!active} className="scroll-mt-20 space-y-5" aria-labelledby={`settings-${section.id}-title`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 h-10 w-10 flex-shrink-0 rounded-2xl bg-brand-600 flex items-center justify-center"><Icon className="h-5 w-5 text-black" /></span>
        <div>
          <h2 id={`settings-${section.id}-title`} className="text-xl font-black uppercase tracking-tight text-gray-900">{section.label}</h2>
          <p className="text-sm text-gray-600">{section.blurb}</p>
        </div>
      </div>
      <SectionSaveContext.Provider value={register}>
        <div className="space-y-5">{children}</div>
      </SectionSaveContext.Provider>
      {/* The one Save. On a phone it floats up only once something changed (the screen is small); on a laptop it is always there. */}
      {list.length > 0 && (
        <div className={`sticky bottom-20 lg:bottom-3 z-30 rounded-xl border border-gray-200 bg-white/95 backdrop-blur shadow-soft px-4 py-3 items-center gap-3 flex-wrap ${dirty || busy ? 'flex' : 'hidden lg:flex'}`}>
          <button type="button" onClick={saveAll} disabled={!dirty || busy || readOnly} className={SAVE_BTN}>{busy ? 'Saving…' : `Save ${section.label}`}</button>
          <span className="text-xs text-gray-500">
            {busy ? 'Saving…' : dirty ? `${dirty} block${dirty === 1 ? '' : 's'} changed and not saved yet.` : 'Nothing changed yet. A switch with no Save saves the moment you tap it.'}
          </span>
        </div>
      )}
    </section>
  );
}

/** The section menu: a column on the left from lg up, a row of tabs that scrolls sideways on a phone. */
function SectionNav({ active, onPick }: { active: SectionId; onPick: (id: string) => void }) {
  return (
    <nav aria-label="Settings sections" className="lg:w-52 lg:flex-shrink-0 lg:sticky lg:top-20">
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 lg:flex-col lg:overflow-visible lg:pb-0">
        {SECTIONS.map(s => {
          const on = s.id === active;
          return (
            <button key={s.id} type="button" onClick={() => onPick(s.id)} aria-current={on ? 'page' : undefined}
              className={`press flex-shrink-0 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold whitespace-nowrap lg:w-full ${on ? 'bg-brand-600 text-black' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
              <s.icon className="h-4 w-4 flex-shrink-0" /> {s.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/** Find a setting by name: a pick opens its section and scrolls to its block. */
function SettingsSearch({ onPick }: { onPick: (id: string) => void }) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const hits = needle
    ? SETTING_INDEX.filter(e => `${e.label} ${e.words || ''} ${BLOCK_NAME[e.at] || ''} ${SECTION_LABEL[SECTION_OF[e.at]] || ''}`.toLowerCase().includes(needle)).slice(0, 12)
    : [];
  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input type="search" className={`${INPUT} pl-9`} placeholder="Find a setting by name: late fees, quiet hours, price bands…" value={q} onChange={e => setQ(e.target.value)} aria-label="Find a setting" />
      </div>
      {needle && (
        <ul className="mt-2 rounded-xl border border-gray-200 bg-white divide-y divide-gray-100 shadow-soft overflow-hidden">
          {hits.length === 0
            ? <li className="px-3 py-2 text-sm text-gray-500">Nothing by that name. Try another word, or pick a section.</li>
            : hits.map(h => (
              <li key={`${h.at}-${h.label}`}>
                <button type="button" onClick={() => { setQ(''); onPick(h.at); }} className="w-full text-left px-3 py-2 hover:bg-gray-50">
                  <span className="text-sm font-medium text-gray-900">{h.label}</span>
                  <span className="block text-xs text-gray-500">{SECTION_LABEL[SECTION_OF[h.at]]} → {BLOCK_NAME[h.at]}</span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const { data: settings, isLoading } = useSettings();

  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyLoaded, setCompanyLoaded] = useState(false);

  const [defaultHourlyRate, setDefaultHourlyRate] = useState('');
  const [employeeLoaded, setEmployeeLoaded] = useState(false);

  const [monthlyBillingEnabled, setMonthlyBillingEnabled] = useState(true);
  // Month-end bills go out without his review (month_end_send = 'auto'). Off by default — owner, Oct 1 2026: "review first".
  const [sendWithoutReview, setSendWithoutReview] = useState(false);
  const [monthEndBilling, setMonthEndBilling] = useState(true);
  const [rescheduleTexts, setRescheduleTexts] = useState(true);
  const [crewNoteTexts, setCrewNoteTexts] = useState(true);
  const [reviewAskTexts, setReviewAskTexts] = useState(false);   // the Google review ask a day after a visit (services/reviewAsk) — off until he turns it on
  const [treatmentTexts, setTreatmentTexts] = useState(true);
  // The care note after a new planting (services/bushFollowUp) — off until he turns it on — and the two schedules it
  // sends, which are his words to edit. An empty box goes back to the shipped wording.
  const [bushCareOn, setBushCareOn] = useState(false);
  const [bushCare, setBushCare] = useState({ water_first: '', water_weeks: '', water_established: '', maintenance: '' });
  const [bushCareOpen, setBushCareOpen] = useState(false);
  const [leadConfirmTexts, setLeadConfirmTexts] = useState(false);
  const [leadInstantReply, setLeadInstantReply] = useState(true);
  const [leadNoReplyAlert, setLeadNoReplyAlert] = useState(true);
  // A copy of every text to the business number goes to his cell until he lives in Texts (owner, Oct 14 2026).
  const [forwardTexts, setForwardTexts] = useState(true);
  // Late texts wait until morning (server/services/heldTexts): the switch and the quiet hours.
  const [quietHold, setQuietHold] = useState(true);
  const [quietFrom, setQuietFrom] = useState('20:00');
  const [quietUntil, setQuietUntil] = useState('06:00');
  const [autoChargeAutopay, setAutoChargeAutopay] = useState(false);
  // "One big invoice instead of a couple smaller ones" (owner, Oct 26 2026): off = finished one-off work waits for the month-end bill.
  const [billOnDone, setBillOnDone] = useState(true);
  const [militaryPct, setMilitaryPct] = useState('10');
  const [lateFeesOn, setLateFeesOn] = useState(false);
  const [lateFeeAmount, setLateFeeAmount] = useState('25');
  const [lateFeeGrace, setLateFeeGrace] = useState('10');
  const [lateFeeRate, setLateFeeRate] = useState('1.5');
  const [lateFeeStart, setLateFeeStart] = useState('2026-12-01');
  const [moneyText, setMoneyText] = useState(false);   // the morning "how much money do I have" text (services/money)
  const [billingCutoffDay, setBillingCutoffDay] = useState('28');
  const [invoiceDueDays, setInvoiceDueDays] = useState('30');
  const [billingLoaded, setBillingLoaded] = useState(false);

  const [mowingSeasonStart, setMowingSeasonStart] = useState('03-01');
  const [mowingSeasonEnd, setMowingSeasonEnd] = useState('11-30');
  const [defaultMowingDays, setDefaultMowingDays] = useState('1,2,3,4,5');
  const [avoidSundays, setAvoidSundays] = useState(true);
  const [mowingLoaded, setMowingLoaded] = useState(false);

  // Email
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailMsg, setTestEmailMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: emailStatus } = useQuery(
    'email-status',
    () => api.get('/messages/email-status').then(r => r.data as { configured: boolean; provider?: string; from?: string }),
    { retry: false }
  );

  // SMS
  const [testSmsTo, setTestSmsTo] = useState('');
  const [testSmsSending, setTestSmsSending] = useState(false);
  const [testSmsMsg, setTestSmsMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: smsStatus } = useQuery(
    'sms-status',
    () => api.get('/integrations/sms-status').then(r => r.data as { configured: boolean; provider?: string; phoneNumber?: string }),
    { retry: false }
  );
  // Mowing-day texts: only for the lines in Connections. The section itself is under Texts & email.
  const { data: dayTexts } = useDayTextSettings();

  // Stripe
  // Autopay, in numbers (services/autopay): who has a card, who chose autopay, what the card would not pay.
  const { data: autopayInfo, refetch: refetchAutopay } = useQuery('autopay-summary', () => api.get('/billing/autopay/summary').then(r => r.data), { retry: false });
  const enableAutopayAll = useMutation(() => api.post('/billing/autopay/enable-cards-on-file').then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Done'); refetchAutopay(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not turn autopay on'); },
  });
  const textMeNow = useMutation(() => api.post('/money/text-me').then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Sent', { autoClose: 12000 }); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not send that'); },
  });
  const { data: stripeStatus } = useQuery(
    'stripe-status',
    () => api.get('/integrations/stripe-status').then(r => r.data as {
      configured: boolean; mode: string; webhookConfigured: boolean;
      publicUrlConfigured: boolean; appPublicUrl?: string; publishableModeWarning?: string;
    }),
    { retry: false }
  );

  // Location
  const { data: locationStatus } = useQuery(
    'location-status',
    () => api.get('/integrations/location-status').then(r => r.data as {
      trackingEnabled: boolean; geofenceRequired: boolean;
      geofenceRadiusMeters: number; bypassCodeConfigured: boolean;
    }),
    { retry: false }
  );

  // Bank accounts (Plaid) — connected here, used by Cash Flow
  const { data: bankStatus, refetch: refetchBank } = useQuery(
    'banking-status',
    () => api.get('/banking/status').then(r => r.data as {
      configured: boolean; connections: any[];
      ramp?: { configured: boolean; transaction_count: number; latest_transaction: string | null; setup_url: string };
    }),
    { retry: false }
  );
  const bankSync = useMutation(() => api.post('/banking/sync'), {
    onSuccess: (r: any) => { toast.success(r.data?.message || 'Synced'); refetchBank(); qc.invalidateQueries(['cashflow']); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Sync failed'); },
  });
  // Ramp card purchases land in the same Cash Flow table as the bank's.
  const rampSync = useMutation(() => api.post('/banking/ramp/sync'), {
    onSuccess: (r: any) => { toast.success(r.data?.message || 'Ramp synced'); refetchBank(); qc.invalidateQueries(['cashflow']); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Ramp sync failed'); },
  });
  const bankDisconnect = useMutation((id: number) => api.delete(`/banking/connections/${id}`), {
    onSuccess: (r: any) => { toast.success(r.data?.message || 'Disconnected'); refetchBank(); qc.invalidateQueries(['cashflow']); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not disconnect'); },
  });
  const fmtSync = (ts?: string | null) => {
    if (!ts) return 'never synced';
    const iso = String(ts).replace(' ', 'T') + (String(ts).includes('+') || String(ts).endsWith('Z') ? '' : 'Z');
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? 'synced' : `synced ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' })}`;
  };

  const [geocodingAll, setGeocodingAll] = useState(false);
  const { isPractice } = useAuth();
  const [resettingPractice, setResettingPractice] = useState(false);
  const [practiceMsg, setPracticeMsg] = useState<string | null>(null);
  const [fixingAddresses, setFixingAddresses] = useState(false);
  const [fixResult, setFixResult] = useState<{ total: number; changed: number; details: { from: string; to: string }[] } | null>(null);
  const [geocodeResult, setGeocodeResult] = useState<{ total: number; succeeded: number; failed: number } | null>(null);

  // Which section is open: the hash (/settings#billing, or a block like /settings#late-fees), the older
  // ?open=billing / ?open=texts links (a customer's Payment & autopay block; his own alert texts), else Company.
  const [search] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const hash = location.hash.replace(/^#/, '');
  const open = search.get('open');
  const target = hash || (open === 'billing' ? 'billing-automation' : open === 'texts' ? 'mowing-day-texts' : '');
  const active: SectionId = sectionFor(target) || 'company';
  const goTo = (id: string) => navigate({ pathname: location.pathname, search: '', hash: `#${id}` });
  // Scroll to the block or section the link names, once the settings are in (the sections render after the load).
  const loaded = !!settings;
  useEffect(() => {
    if (!target || !loaded) return;
    const t = setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    return () => clearTimeout(t);
  }, [target, loaded]);
  const [showPin, setShowPin] = useState(false);
  const thisYear = new Date().getFullYear();

  // Change Admin PIN
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [pinMsg, setPinMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  async function changeAdminPin() {
    setPinMsg(null);
    if (!/^\d{6}$/.test(currentPin)) { setPinMsg({ type: 'error', text: 'Current PIN must be exactly 6 digits.' }); return; }
    if (!/^\d{6}$/.test(newPin))     { setPinMsg({ type: 'error', text: 'New PIN must be exactly 6 digits.' }); return; }
    if (newPin !== confirmPin)        { setPinMsg({ type: 'error', text: 'New PINs do not match.' }); return; }
    setPinLoading(true);
    try {
      await api.put('/auth/change-pin', { current_pin: currentPin, new_pin: newPin });
      setPinMsg({ type: 'success', text: 'PIN updated. Use your new PIN next time you log in.' });
      setCurrentPin(''); setNewPin(''); setConfirmPin('');
    } catch (err: any) {
      setPinMsg({ type: 'error', text: err.response?.data?.message || 'Failed to update PIN.' });
    } finally {
      setPinLoading(false);
    }
  }

  async function geocodeAllProperties() {
    setGeocodingAll(true);
    setGeocodeResult(null);
    try {
      const res = await api.post('/properties/geocode-all');
      setGeocodeResult({ total: res.data.total, succeeded: res.data.succeeded, failed: res.data.failed });
    } catch {
      setGeocodeResult({ total: 0, succeeded: 0, failed: -1 });
    } finally {
      setGeocodingAll(false);
    }
  }

  async function resetPractice() {
    if (!window.confirm('Reset the practice data? Everything you changed here is wiped and the fake jobs are re-dated to today.')) return;
    setResettingPractice(true);
    setPracticeMsg(null);
    try {
      const res = await api.post('/practice/reset');
      setPracticeMsg(res.data?.message || 'Practice data reset.');
      qc.invalidateQueries();
    } catch (err: any) {
      setPracticeMsg(err?.response?.data?.message || 'Could not reset practice data.');
    } finally {
      setResettingPractice(false);
    }
  }

  async function fixAllAddresses() {
    setFixingAddresses(true);
    setFixResult(null);
    try {
      const res = await api.post('/properties/normalize-all');
      setFixResult({ total: res.data.total, changed: res.data.changed, details: res.data.details || [] });
    } catch {
      setFixResult({ total: 0, changed: -1, details: [] });
    } finally {
      setFixingAddresses(false);
    }
  }

  async function sendTestSms() {
    setTestSmsSending(true);
    setTestSmsMsg(null);
    try {
      if (!testSmsTo) { setTestSmsMsg({ type: 'error', text: 'Enter a phone number.' }); return; }
      const res = await api.post('/messages/test-sms', { to: testSmsTo });
      setTestSmsMsg({ type: 'success', text: res.data.message || 'Test SMS sent!' });
    } catch (err: any) {
      setTestSmsMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to send SMS.' });
    } finally {
      setTestSmsSending(false);
    }
  }

  async function sendTestEmail() {
    setTestEmailSending(true);
    setTestEmailMsg(null);
    try {
      const res = await api.post('/messages/test-email', testEmailTo ? { to: testEmailTo } : {});
      setTestEmailMsg({ type: 'success', text: res.data.message || 'Test email sent!' });
    } catch (err: any) {
      setTestEmailMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to send test email.' });
    } finally {
      setTestEmailSending(false);
    }
  }

  // Google Sheets
  const [sheetsEnabled, setSheetsEnabled] = useState(false);
  const [sheetsSpreadsheetId, setSheetsSpreadsheetId] = useState('');
  const [sheetsJobsSheet, setSheetsJobsSheet] = useState('Jobs');
  const [sheetsCompletedSheet, setSheetsCompletedSheet] = useState('Completed Jobs');
  const [sheetsInvoicesSheet, setSheetsInvoicesSheet] = useState('Invoices');
  const [sheetsQuotesSheet, setSheetsQuotesSheet] = useState('Quotes');
  const [sheetsRoutesSheet, setSheetsRoutesSheet] = useState('Route Stops');
  const [sheetsLoaded, setSheetsLoaded] = useState(false);
  const [sheetsExporting, setSheetsExporting] = useState<string | null>(null);
  const [dataExporting, setDataExporting] = useState(false);
  const [dataExportMsg, setDataExportMsg] = useState<string | null>(null);
  const [sheetsMsg, setSheetsMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: integrationStatus } = useQuery(
    'integration-status',
    () => api.get('/integrations/status').then(r => r.data.google_sheets as any),
    { retry: false }
  );

  React.useEffect(() => {
    if (settings && !companyLoaded) {
      setCompanyName(settings.company_name ?? 'Keep Austin Mowed');
      setCompanyAddress(settings.company_address ?? '');
      setCompanyPhone(settings.company_phone ?? '');
      setCompanyEmail(settings.company_email ?? '');
      setCompanyLoaded(true);
    }
    if (settings && !employeeLoaded) {
      setDefaultHourlyRate(settings.default_hourly_rate ?? '20');
      setEmployeeLoaded(true);
    }
    if (settings && !billingLoaded) {
      setMonthlyBillingEnabled(settings.monthly_billing_enabled !== 'false');
      setSendWithoutReview(settings.month_end_send === 'auto');
      setMonthEndBilling(settings.monthly_bill_last_day !== 'false');
      setRescheduleTexts(settings.reschedule_texts !== 'false');
      setCrewNoteTexts(settings.crew_note_texts !== 'false');
      setReviewAskTexts(['on', 'true', '1', 'yes'].includes(String(settings.review_ask_texts ?? '').trim().toLowerCase()));
      setBushCareOn(['on', 'true', '1', 'yes'].includes(String(settings.bush_followup_on ?? '').trim().toLowerCase()));
      setBushCare({
        water_first: String(settings.bush_care_water_first ?? ''),
        water_weeks: String(settings.bush_care_water_weeks ?? ''),
        water_established: String(settings.bush_care_water_established ?? ''),
        maintenance: String(settings.bush_care_maintenance ?? ''),
      });
      setTreatmentTexts(settings.treatment_texts !== 'false');
      setLeadConfirmTexts(settings.lead_confirm_texts === 'true');
      // services/leadReply.on: on unless '0', 'false', 'off' or 'no' is stored.
      setLeadInstantReply(!['false', 'off', '0', 'no'].includes(String(settings.lead_instant_reply ?? '').trim().toLowerCase()));
      setLeadNoReplyAlert(!['false', 'off', '0', 'no'].includes(String(settings.lead_no_reply_alert ?? '').trim().toLowerCase()));
      setForwardTexts(!['false', 'off', '0', 'no'].includes(String(settings.inbound_forward_texts ?? '').toLowerCase()));
      setQuietHold(!['false', 'off', '0', 'no'].includes(String(settings.texts_quiet_hold ?? '').toLowerCase()));
      setQuietFrom(/^\d{2}:\d{2}$/.test(String(settings.texts_quiet_from ?? '')) ? settings.texts_quiet_from : '20:00');
      setQuietUntil(/^\d{2}:\d{2}$/.test(String(settings.texts_quiet_until ?? '')) ? settings.texts_quiet_until : '06:00');
      setAutoChargeAutopay(settings.auto_charge_autopay === 'true');
      setBillOnDone(settings.bill_on_done !== 'false');
      setMilitaryPct(settings.military_discount_pct ?? '10');
      setLateFeesOn(!['', undefined, null, '0', 'false', 'off', 'no'].includes(settings.late_fees_on as any) && settings.late_fees_on !== undefined);
      setLateFeeAmount(settings.late_fee_amount ?? '25');
      setLateFeeGrace(settings.late_fee_grace_days ?? '10');
      setLateFeeRate(settings.late_fee_monthly_rate ?? '1.5');
      setLateFeeStart(settings.late_fee_start_date ?? '2026-12-01');
      setMoneyText(settings.money_morning_text === 'true');
      setBillingCutoffDay(settings.billing_cutoff_day ?? '28');
      setInvoiceDueDays(settings.invoice_due_days ?? '30');
      setBillingLoaded(true);
    }
    if (settings && !mowingLoaded) {
      setMowingSeasonStart(settings.mowing_season_start ?? '03-01');
      setMowingSeasonEnd(settings.mowing_season_end ?? '11-30');
      setDefaultMowingDays(settings.default_mowing_days ?? '1,2,3,4,5');
      setAvoidSundays(settings.avoid_sundays !== 'false');
      setMowingLoaded(true);
    }
    if (settings && !sheetsLoaded) {
      setSheetsEnabled(settings.sheets_enabled === 'true');
      setSheetsSpreadsheetId(settings.sheets_spreadsheet_id ?? '');
      setSheetsJobsSheet(settings.sheets_jobs_sheet ?? 'Jobs');
      setSheetsCompletedSheet(settings.sheets_completed_jobs_sheet ?? 'Completed Jobs');
      setSheetsInvoicesSheet(settings.sheets_invoices_sheet ?? 'Invoices');
      setSheetsQuotesSheet(settings.sheets_quotes_sheet ?? 'Quotes');
      setSheetsRoutesSheet(settings.sheets_routes_sheet ?? 'Route Stops');
      setSheetsLoaded(true);
    }
  }, [settings, companyLoaded, employeeLoaded, billingLoaded, mowingLoaded, sheetsLoaded]);

  const saveMutation = useMutation(
    (updates: Record<string, string>) => api.put('/settings', updates).then(r => r.data),
    { onSuccess: () => qc.invalidateQueries('app-settings') }
  );

  function saveCompany() {
    saveMutation.mutate({
      company_name: companyName,
      company_address: companyAddress,
      company_phone: companyPhone,
      company_email: companyEmail,
    });
  }

  function saveEmployeeDefaults() {
    const rateNum = parseFloat(defaultHourlyRate);
    if (isNaN(rateNum) || rateNum < 0) {
      alert('Hourly rate must be a positive number.');
      return;
    }
    saveMutation.mutate({ default_hourly_rate: String(rateNum) });
  }

  function saveMowing() {
    const startRe = /^\d{2}-\d{2}$/;
    if (!startRe.test(mowingSeasonStart) || !startRe.test(mowingSeasonEnd)) {
      alert('Season dates must be in MM-DD format (e.g. 03-01).');
      return;
    }
    saveMutation.mutate({
      mowing_season_start: mowingSeasonStart,
      mowing_season_end: mowingSeasonEnd,
      default_mowing_days: defaultMowingDays,
      avoid_sundays: String(avoidSundays),
    });
  }

  function saveBilling() {
    const cutoff = parseInt(billingCutoffDay);
    const due = parseInt(invoiceDueDays);
    if (isNaN(cutoff) || cutoff < 1 || cutoff > 28) {
      alert('Billing cutoff day must be between 1 and 28.');
      return;
    }
    if (isNaN(due) || due < 1) {
      alert('Invoice due days must be a positive number.');
      return;
    }
    saveMutation.mutate({
      monthly_billing_enabled: String(monthlyBillingEnabled),
      auto_charge_autopay: String(autoChargeAutopay),
      bill_on_done: String(billOnDone),
      military_discount_pct: String(militaryPct),
      late_fees_on: lateFeesOn ? 'on' : 'off',
      late_fee_amount: String(lateFeeAmount),
      late_fee_grace_days: String(lateFeeGrace),
      late_fee_monthly_rate: String(lateFeeRate),
      late_fee_start_date: String(lateFeeStart),
      billing_cutoff_day: String(cutoff),
      invoice_due_days: String(due),
    });
  }

  function saveSheets() {
    saveMutation.mutate({
      sheets_enabled: String(sheetsEnabled),
      sheets_spreadsheet_id: sheetsSpreadsheetId,
      sheets_jobs_sheet: sheetsJobsSheet,
      sheets_completed_jobs_sheet: sheetsCompletedSheet,
      sheets_invoices_sheet: sheetsInvoicesSheet,
      sheets_quotes_sheet: sheetsQuotesSheet,
      sheets_routes_sheet: sheetsRoutesSheet,
    });
  }

  /**
   * Download the whole database as one workbook.
   *
   * responseType 'blob' matters: without it axios parses the bytes as text and
   * the saved .xlsx is corrupt. The object URL is revoked after the click or
   * the page leaks it for as long as the tab is open.
   */
  async function exportAllData() {
    setDataExporting(true);
    setDataExportMsg(null);
    try {
      const res = await api.get('/export/all.xlsx', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `keep-austin-mowed-export-${businessToday()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDataExportMsg('Downloaded.');
    } catch (err: any) {
      setDataExportMsg(err?.response?.data?.message || err?.message || 'Export failed');
    } finally {
      setDataExporting(false);
    }
  }

  async function sheetsExport(endpoint: string, label: string) {
    setSheetsExporting(endpoint);
    setSheetsMsg(null);
    try {
      const res = await api.post(`/integrations/sheets/export/${endpoint}`);
      setSheetsMsg({ type: 'success', text: res.data.message });
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Export failed';
      setSheetsMsg({ type: 'error', text: msg });
    } finally {
      setSheetsExporting(null);
    }
  }

  const isSaving = saveMutation.isLoading;
  // A view-only login has every write refused server-side; grey the controls
  // rather than letting them look live and fail.
  const readOnly = useReadOnly();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  const stripeText = !stripeStatus?.configured ? 'Not connected' : stripeStatus.mode === 'test' ? 'Test mode' : 'Live';
  const billingOff = !monthlyBillingEnabled && !sendWithoutReview && !autoChargeAutopay;
  // "Changed and not saved": what each block written in this file would send, against what is saved, so the
  // section's one Save knows which blocks to call. The sub-sections below keep their own `edit` state for the same.
  const sv: Record<string, string> = settings || {};
  const companyDirty = companyName !== (sv.company_name ?? 'Keep Austin Mowed') || companyAddress !== (sv.company_address ?? '') || companyPhone !== (sv.company_phone ?? '') || companyEmail !== (sv.company_email ?? '');
  const employeeDirty = defaultHourlyRate !== (sv.default_hourly_rate ?? '20');
  const mowingDirty = mowingSeasonStart !== (sv.mowing_season_start ?? '03-01') || mowingSeasonEnd !== (sv.mowing_season_end ?? '11-30')
    || defaultMowingDays !== (sv.default_mowing_days ?? '1,2,3,4,5') || avoidSundays !== (sv.avoid_sundays !== 'false');
  const billingDirty = monthlyBillingEnabled !== (sv.monthly_billing_enabled !== 'false') || autoChargeAutopay !== (sv.auto_charge_autopay === 'true') || billOnDone !== (sv.bill_on_done !== 'false')
    || militaryPct !== (sv.military_discount_pct ?? '10')
    || lateFeesOn !== (!['', undefined, null, '0', 'false', 'off', 'no'].includes(sv.late_fees_on as any) && sv.late_fees_on !== undefined)
    || lateFeeAmount !== (sv.late_fee_amount ?? '25') || lateFeeGrace !== (sv.late_fee_grace_days ?? '10') || lateFeeRate !== (sv.late_fee_monthly_rate ?? '1.5') || lateFeeStart !== (sv.late_fee_start_date ?? '2026-12-01')
    || billingCutoffDay !== (sv.billing_cutoff_day ?? '28') || invoiceDueDays !== (sv.invoice_due_days ?? '30');
  const sheetsDirty = sheetsEnabled !== (sv.sheets_enabled === 'true') || sheetsSpreadsheetId !== (sv.sheets_spreadsheet_id ?? '') || sheetsJobsSheet !== (sv.sheets_jobs_sheet ?? 'Jobs')
    || sheetsCompletedSheet !== (sv.sheets_completed_jobs_sheet ?? 'Completed Jobs') || sheetsInvoicesSheet !== (sv.sheets_invoices_sheet ?? 'Invoices')
    || sheetsQuotesSheet !== (sv.sheets_quotes_sheet ?? 'Quotes') || sheetsRoutesSheet !== (sv.sheets_routes_sheet ?? 'Route Stops');
  const quietValid = /^\d{2}:\d{2}$/.test(quietFrom) && /^\d{2}:\d{2}$/.test(quietUntil) && quietFrom !== quietUntil;
  const quietDirty = quietFrom !== (/^\d{2}:\d{2}$/.test(String(sv.texts_quiet_from ?? '')) ? sv.texts_quiet_from : '20:00')
    || quietUntil !== (/^\d{2}:\d{2}$/.test(String(sv.texts_quiet_until ?? '')) ? sv.texts_quiet_until : '06:00');

  return (
    <div className="space-y-5 max-w-5xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">Pick a section, or find a setting by name. Each section has one Save at the bottom; a switch with no Save saves the moment you tap it.</p>
      </div>

      <SettingsSearch onPick={goTo} />

      {isPractice && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">Practice: every client, job and dollar on this server is fake. Reset it under <button type="button" onClick={() => goTo('practice')} className="underline font-semibold">Admin → Practice data</button>.</p>
      )}

      {saveMutation.isSuccess && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-2.5 text-sm text-green-800">Saved.</div>
      )}
      {saveMutation.isError && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-800">Could not save. Try again.</div>
      )}

      <div className="lg:flex lg:items-start lg:gap-6">
        <SectionNav active={active} onPick={goTo} />
        <div className="flex-1 min-w-0 mt-4 lg:mt-0">

      {/* ══ Company ══ */}
      <SectionPanel section={SECTIONS[0]} active={active === 'company'}>
      <Section title="Your business" icon={Building2} id="your-business">
        <p className="text-sm text-gray-500">The name, number and address printed on every bill, bid and text.</p>
        <RegisterSave k="company" dirty={companyDirty} busy={isSaving} save={saveCompany} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldRow label="Business name">
            <input type="text" className={INPUT} value={companyName} onChange={e => setCompanyName(e.target.value)} />
          </FieldRow>
          <FieldRow label="Phone">
            <input type="tel" className={INPUT} value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} placeholder="(512) 555-0100" />
          </FieldRow>
          <FieldRow label="Email">
            <input type="email" className={INPUT} value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} placeholder="billing@example.com" />
          </FieldRow>
          <FieldRow label="Address" help="Printed on invoices and quotes.">
            <input type="text" className={INPUT} value={companyAddress} onChange={e => setCompanyAddress(e.target.value)} placeholder="123 Main St, Austin TX 78701" />
          </FieldRow>
        </div>
        <p className="text-xs text-gray-500 mt-2">Times everywhere in the app are shown in America/Chicago (business time), whatever the phone or laptop is set to.</p>
      </Section>

      <Section title="Mowing season" icon={Leaf} id="mowing-season">
        <p className="text-sm text-gray-500">Recurring mowing stops generating after the season ends. Pick the days you mow.</p>
        <RegisterSave k="mowing" dirty={mowingDirty} busy={isSaving} save={saveMowing} />
        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Season starts">
            <input type="date" className={INPUT} value={`${thisYear}-${mowingSeasonStart}`} onChange={e => setMowingSeasonStart(e.target.value.slice(5))} />
          </FieldRow>
          <FieldRow label="Season ends">
            <input type="date" className={INPUT} value={`${thisYear}-${mowingSeasonEnd}`} onChange={e => setMowingSeasonEnd(e.target.value.slice(5))} />
          </FieldRow>
        </div>
        <FieldRow label="Mowing days">
          <div className="flex gap-1 flex-wrap mt-1">
            {[
              { label: 'Su', value: '0' }, { label: 'M', value: '1' }, { label: 'Tu', value: '2' }, { label: 'W', value: '3' },
              { label: 'Th', value: '4' }, { label: 'F', value: '5' }, { label: 'Sa', value: '6' },
            ].map(({ label, value }) => {
              const days = defaultMowingDays ? defaultMowingDays.split(',') : [];
              const active = days.includes(value);
              return (
                <button key={value} type="button"
                  onClick={() => { const next = active ? days.filter(d => d !== value) : [...days, value].sort(); setDefaultMowingDays(next.join(',')); }}
                  className={`w-9 h-9 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-600 border-gray-300 hover:border-brand-400'}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </FieldRow>
        <Toggle checked={avoidSundays} onChange={setAvoidSundays} label="Never schedule on Sundays" />
      </Section>

      {/* Where the routes start + the service area (travel charge, turf from the county map) */}
      <ServiceAreaSection />

      <FieldDaySection />

      <AutoStartSection />
      </SectionPanel>

      {/* ══ Texts & email ══ */}
      <SectionPanel section={SECTIONS[1]} active={active === 'texts'}>
        <Section title="Texts the app sends" icon={MessageSquare} id="text-switches">
          <p className="text-sm text-gray-500">Each switch is one kind of text the app sends by itself. They save the moment you tap them. A customer who replies STOP is taken off every text; who gets which text per customer is on <Link to="/customers/texts" className="text-brand-700 underline">Who gets which texts</Link>.</p>
          <div className="mb-4">
            <Toggle checked={rescheduleTexts} onChange={v => { setRescheduleTexts(v); saveMutation.mutate({ reschedule_texts: String(v) }); }}
              label="Text customers when a visit moves"
              sublabel="Drag a stop to another day, push a route for rain, or move a series — the customer gets one text with the new day (two minutes later, so a drag-and-drag-back sends nothing). Opted-out customers and to-dos are never texted." />
            <div className="mt-3">
              <Toggle checked={crewNoteTexts} onChange={v => { setCrewNoteTexts(v); saveMutation.mutate({ crew_note_texts: String(v) }); }}
                label="Text me every crew note"
                sublabel="A note at Finish, why a stop ran late, a skip reason, a note typed in the yard, a note photo, or a winter-upsell photo after Complete — one text to your cell each, from the business number, with the picture when there is one. Only crew logins set it off; a part-done yard has its own text either way." />
            </div>
            <div className="mt-3">
              <Toggle checked={reviewAskTexts} onChange={v => { setReviewAskTexts(v); saveMutation.mutate({ review_ask_texts: v ? 'on' : 'off' }); }}
                label="Ask for a Google review a day after a visit"
                sublabel={'24 to 72 hours after a visit is finished, 9 AM to 7 PM: "{First name}, thanks for having us out. If you\'re happy with the work, a quick Google review would mean a lot — and it\'s $10 off your next bill: {link}". Once per customer per 90 days. Never after a complaint, never a business, a to-do or a quote visit, never anyone who replied STOP, has Check-ins texts off, already left a review or used the review $10, or got the second-mow check-in in the last 90 days. The link is the one the second-mow check-in sends. Off until you turn it on.'} />
            </div>
            <div className="mt-3">
              <Toggle checked={bushCareOn} onChange={v => { setBushCareOn(v); saveMutation.mutate({ bush_followup_on: v ? 'on' : 'off' }); }}
                label="Care note after a new planting"
                sublabel={'When a bush or shrub installation is marked complete — by the crew, by your tick, or by an edit — the customer gets one text (an email when there is no phone) with a link to their own care page: how to water a new planting for the first 2 weeks, weeks 3 to 8 and once it is established, the maintenance you recommend, and the care-plan prices that bid carried (every 3, 4 or 6 months). A plan they take there lands on Needs Attention for you to book — nothing is charged. One per planting job, ever; 8 AM to 8 PM, and one finished at night goes the next morning. Never a business, an archived customer, anyone who replied STOP or has Check-ins texts off. Off until you turn it on.'} />
              <button type="button" onClick={() => setBushCareOpen(o => !o)} className="mt-2 text-xs font-semibold text-brand-700 underline">
                {bushCareOpen ? 'Hide the care page wording' : 'Edit the care page wording'}
              </button>
              {bushCareOpen && (
                <div className="mt-2 space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
                  <p className="text-xs text-gray-600">Your words, on every care page. Leave a box empty to go back to the wording the app ships with. One line per point; a line starting with "-" reads as a bullet.</p>
                  {([
                    ['water_first', 'Watering — the first 2 weeks'],
                    ['water_weeks', 'Watering — weeks 3 to 8'],
                    ['water_established', 'Watering — once established'],
                    ['maintenance', 'The maintenance you recommend'],
                  ] as [keyof typeof bushCare, string][]).map(([key, label]) => (
                    <label key={key} className="block">
                      <span className="text-xs font-semibold text-gray-800">{label}</span>
                      <textarea className="input text-sm w-full mt-1" rows={key === 'maintenance' ? 5 : 3} maxLength={1200}
                        value={bushCare[key]} placeholder="Leave empty for the app's own wording"
                        onChange={e => setBushCare(c => ({ ...c, [key]: e.target.value }))} />
                    </label>
                  ))}
                  <button type="button" disabled={saveMutation.isLoading}
                    onClick={() => saveMutation.mutate({
                      bush_care_water_first: bushCare.water_first,
                      bush_care_water_weeks: bushCare.water_weeks,
                      bush_care_water_established: bushCare.water_established,
                      bush_care_maintenance: bushCare.maintenance,
                    })}
                    className="btn-primary text-xs py-1 px-3">{saveMutation.isLoading ? 'Saving…' : 'Save the wording'}</button>
                </div>
              )}
            </div>
            <div className="mt-3">
              <Toggle checked={treatmentTexts} onChange={v => { setTreatmentTexts(v); saveMutation.mutate({ treatment_texts: String(v) }); }}
                label="Treatment texts"
                sublabel={'Two texts around a lawn treatment. When a visit is finished: "we put down fertilizer and pre-emergent on your lawn today, please water it in as soon as you can" (a spray says hold off a day). The day before a treatment at a yard we do not mow: "please mow before we get there". Customers who turned Treatments texts off, businesses and opted-out numbers are never texted.'} />
            </div>
            <div className="mt-3">
              <Toggle checked={leadInstantReply} onChange={v => { setLeadInstantReply(v); saveMutation.mutate({ lead_instant_reply: String(v) }); }}
                label="Instant-price reply to new leads"
                sublabel={'The moment the website form or the referral page lands a lead, they get a text (and an email when they gave one): "Thanks Kyle, this is Michael at Keep Austin Mowed. Your instant price: <the instant-price page with their address filled in>. I\'ll follow up within 1 business hour." A number that said STOP is never texted. The lead stays on Needs Attention until you answer. You always get the "KAM quote: …" text with who filled it out.'} />
            </div>
            <div className="mt-3">
              <Toggle checked={leadNoReplyAlert} onChange={v => { setLeadNoReplyAlert(v); saveMutation.mutate({ lead_no_reply_alert: String(v) }); }}
                label="Text me when a lead has no reply after 1 business hour"
                sublabel={'Business hours are Mon–Sat 8 AM–6 PM; a lead that lands outside them starts its hour the next business morning. One text per lead: "No reply yet to Kyle Watson ((512) 555-0100) — they asked 73 min ago: \'…\'. Texts → their thread". A text you typed, a bid link, or a reply waiting for the morning counts as your reply; the automatic texts do not.'} />
            </div>
            <div className="mt-3">
              <Toggle checked={leadConfirmTexts} onChange={v => { setLeadConfirmTexts(v); saveMutation.mutate({ lead_confirm_texts: String(v) }); }}
                label="Text a website visitor a confirmation when they fill out the form (older thank-you text with two visit days)"
                sublabel={'Only goes while the instant-price reply above is off — nobody gets two texts. Off = the visitor hears from you, not the app. You always get the "KAM quote: …" text with who filled it out, either way.'} />
            </div>
            <div className="mt-3">
              <Toggle checked={forwardTexts} onChange={v => { setForwardTexts(v); saveMutation.mutate({ inbound_forward_texts: String(v) }); }}
                label="Forward customer texts to my cell"
                sublabel="Every text to the business number lands on Texts in the app either way, and you reply from there. On = a copy also goes to your cell, the way it always has. Switch it off once you live in Texts." />
            </div>
            <div className="mt-3">
              <Toggle checked={quietHold} onChange={v => { setQuietHold(v); saveMutation.mutate({ texts_quiet_hold: String(v) }); }}
                label="Hold my late texts until morning"
                sublabel={`A text you type between ${quietWords(quietFrom)} and ${quietWords(quietUntil)} — a reply on Texts, a quick text from a job or Approvals, a plan bid's "Text them now" — waits on the thread and goes at ${quietWords(quietUntil)} exactly as typed, picture included. "Send now" on it sends it right away. Bids and bills sent by text, the crew blast and the automatic texts are not held.`} />
              {quietHold && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-700">
                  <RegisterSave k="quiet-hours" dirty={quietDirty && quietValid} busy={isSaving} save={() => saveMutation.mutate({ texts_quiet_from: quietFrom, texts_quiet_until: quietUntil })} />
                  <span>Quiet from</span>
                  <input type="time" value={quietFrom} onChange={e => setQuietFrom(e.target.value)} className={`${INPUT} w-32`} />
                  <span>until</span>
                  <input type="time" value={quietUntil} onChange={e => setQuietUntil(e.target.value)} className={`${INPUT} w-32`} />
                  <span className="text-xs text-gray-500">Austin time. The hours save with the section's Save button.</span>
                  {quietDirty && !quietValid && <span className="text-xs text-red-600">Pick two different times.</span>}
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* ── Mowing-day texts: the morning text, rain delay, "you're next", after the mow, the visit photo report ── */}
        <MowingDayTextsSection />

        <Section title="The business number (Twilio)" icon={MessageSquare} id="twilio">
          <p className="text-sm text-gray-500">The number every text goes out from, and a way to send yourself a test.</p>
          <div className="flex items-center gap-2 mb-2">
            {smsStatus?.configured ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5"><CheckCircle className="h-3.5 w-3.5" /> Configured</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5"><AlertCircle className="h-3.5 w-3.5" /> Not configured</span>
            )}
            {smsStatus?.phoneNumber && <span className="text-xs text-gray-500 font-mono">{smsStatus.phoneNumber}</span>}
          </div>
          {!smsStatus?.configured && (
            <div className="rounded-md bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-600">
              Needs <code className="font-mono bg-gray-100 px-1 rounded">TWILIO_ACCOUNT_SID</code>, <code className="font-mono bg-gray-100 px-1 rounded">TWILIO_AUTH_TOKEN</code>, <code className="font-mono bg-gray-100 px-1 rounded">TWILIO_PHONE_NUMBER</code> on Railway, plus A2P registration with Twilio.
            </div>
          )}
          {smsStatus?.configured && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <p className="text-sm font-medium text-gray-700">Send a test text</p>
              {testSmsMsg && (
                <div className={`rounded-md px-3 py-2 text-sm ${testSmsMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>{testSmsMsg.text}</div>
              )}
              <div className="flex gap-2 items-center">
                <input type="tel" placeholder="+15121234567" className={`${INPUT} w-52`} value={testSmsTo} onChange={e => setTestSmsTo(e.target.value)} />
                <button onClick={sendTestSms} disabled={testSmsSending} className={`${SAVE_BTN} gap-1.5`}><MessageSquare className="h-4 w-4" />{testSmsSending ? 'Sending…' : 'Send test'}</button>
              </div>
            </div>
          )}
        </Section>

        <Section title="Email" icon={Mail} id="email">
          <p className="text-sm text-gray-500">Bills and bids go by email too when a customer gave one. This is whether email is set up on the server.</p>
          <div className="flex items-center gap-2 mb-2">
            {emailStatus?.configured ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5"><CheckCircle className="h-3.5 w-3.5" /> Configured{emailStatus.provider && <span className="ml-1 font-mono text-green-600">({emailStatus.provider})</span>}</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5"><AlertCircle className="h-3.5 w-3.5" /> Not configured</span>
            )}
            {emailStatus?.from && <span className="text-xs text-gray-500 font-mono">{emailStatus.from}</span>}
          </div>
          {!emailStatus?.configured && (
            <div className="rounded-md bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-600 space-y-2">
              <p className="font-medium text-gray-700">Option A — Google Workspace SMTP</p>
              <p><code className="font-mono bg-gray-100 px-1 rounded">EMAIL_PROVIDER=smtp</code>, <code className="font-mono bg-gray-100 px-1 rounded">SMTP_HOST</code>, <code className="font-mono bg-gray-100 px-1 rounded">SMTP_PORT</code>, <code className="font-mono bg-gray-100 px-1 rounded">SMTP_USER</code>, <code className="font-mono bg-gray-100 px-1 rounded">SMTP_PASS</code>, <code className="font-mono bg-gray-100 px-1 rounded">EMAIL_FROM</code>.</p>
              <p className="font-medium text-gray-700 pt-1">Option B — Resend</p>
              <p><code className="font-mono bg-gray-100 px-1 rounded">EMAIL_PROVIDER=resend</code>, <code className="font-mono bg-gray-100 px-1 rounded">RESEND_API_KEY</code>, <code className="font-mono bg-gray-100 px-1 rounded">EMAIL_FROM</code>.</p>
            </div>
          )}
          {emailStatus?.configured && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <p className="text-sm font-medium text-gray-700">Send a test email</p>
              {testEmailMsg && (
                <div className={`rounded-md px-3 py-2 text-sm ${testEmailMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>{testEmailMsg.text}</div>
              )}
              <div className="flex gap-2 items-center">
                <input type="email" placeholder="Recipient (defaults to EMAIL_REPLY_TO)" className={`${INPUT} w-72`} value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)} />
                <button onClick={sendTestEmail} disabled={testEmailSending} className={`${SAVE_BTN} gap-1.5`}><Mail className="h-4 w-4" />{testEmailSending ? 'Sending…' : 'Send test'}</button>
              </div>
            </div>
          )}
        </Section>
      </SectionPanel>

      {/* ══ Bids & quotes ══ */}
      <SectionPanel section={SECTIONS[2]} active={active === 'bids'}>
        {/* ── Automatic quotes ── */}
        <AutoQuoteSection />

        {/* ── The public instant-quote page ── */}
        <InstantQuoteSection />

        {/* ── Plan offers on a bid ── */}
        <PlanOffersSection />

        {/* ── Treatment plans ── */}
        <TreatmentPlansSection />

        {/* ── Winter work: "do it in winter, save 10%" on bids ── */}
        <WinterWorkSection />

        {/* ── Quote visits customers can book ── */}
        <QuoteVisitsSection />
        <WalkthroughSection />
      </SectionPanel>

      {/* ══ Billing ══ */}
      <SectionPanel section={SECTIONS[3]} active={active === 'billing'}>
        <Section title="Bills" icon={Receipt} id="billing-automation">
          <p className="text-sm text-gray-500">When the month-end bills are made, whether you check them first, and what comes off. The first two switches save when tapped; the rest save with the section's Save button.</p>
          <RegisterSave k="billing" dirty={billingDirty} busy={isSaving} save={saveBilling} />
          <div className="space-y-4">
            <Toggle checked={monthEndBilling} onChange={v => { setMonthEndBilling(v); saveMutation.mutate({ monthly_bill_last_day: String(v) }); }} label="Bill on the last day of the month" sublabel="After 5 PM on the last day, every customer's finished visits for the month become one draft bill, and you get a text to check them on Billing → Review & send. Same as pressing Generate monthly." />
            <Toggle checked={sendWithoutReview}
              onChange={v => {
                if (v && !window.confirm('Send month-end bills to customers without you checking them first? Autopay customers are charged right away too.')) return;
                setSendWithoutReview(v);
                saveMutation.mutate({ month_end_send: v ? 'auto' : 'review' });
              }}
              label="Send month-end bills without my review"
              sublabel="Off (recommended): month-end makes the bills and texts you; nothing goes to customers until you tap Send on Billing → Review & send. On: the last-day run sends them (and charges autopay cards) on its own. Generate monthly never sends." />
            <Toggle checked={monthlyBillingEnabled} onChange={setMonthlyBillingEnabled} label="Monthly billing" sublabel="Enables the Billing dashboard and monthly invoice runs." />
            <Toggle checked={billOnDone} onChange={setBillOnDone} label="Bill one-off work the day it's finished" sublabel="On: a cleanup or project drafts its own bill when the crew finishes it (you still send it). Off: everything a customer had done waits for their one month-end bill, so they get one bill and one payment. Either way, a customer with several open bills can pay them all at once from any bill page." />
            <Toggle checked={autoChargeAutopay} onChange={setAutoChargeAutopay} label="Auto-charge autopay customers" sublabel="When the month-end bill is sent, a customer who chose autopay is charged on their card on file and gets a receipt instead of a bill. One try — a decline goes to Needs Attention and they get the normal bill. Needs Stripe live." />
          </div>
          <div className="border-t border-gray-100 pt-4 grid grid-cols-2 gap-4">
            <FieldRow label="Billing cutoff day" help="Last day of month (1–28) in each run.">
              <input type="number" min="1" max="28" className={INPUT} value={billingCutoffDay} onChange={e => setBillingCutoffDay(e.target.value)} />
            </FieldRow>
            <FieldRow label="Invoice due days" help="Net 30 = 30.">
              <input type="number" min="1" className={INPUT} value={invoiceDueDays} onChange={e => setInvoiceDueDays(e.target.value)} />
            </FieldRow>
            <FieldRow label="Military discount %" help="Off every draft bill of a customer whose military status is verified on their profile. 0 switches it off.">
              <input type="number" min="0" max="100" step="0.5" className={INPUT} value={militaryPct} onChange={e => setMilitaryPct(e.target.value)} />
            </FieldRow>
          </div>
          {autoChargeAutopay && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 flex gap-2">
              <Bell className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800 space-y-1">
                <p>Auto-charge is on. Only customers who chose autopay are charged — never a card that is merely on file — and only when the bill is sent (after your review, unless sending without review is on).{autopayInfo?.stripe && autopayInfo.stripe !== 'live' ? ` Stripe is ${autopayInfo.stripe === 'test' ? 'in test mode' : 'not set up'}, so no real card is charged yet.` : ''}</p>
                {autopayInfo && (
                  <p>
                    <b>{autopayInfo.on_autopay}</b> on autopay · <b>{autopayInfo.cards_on_file}</b> with a card on file
                    {autopayInfo.declined_open ? <> · <b>{autopayInfo.declined_open}</b> open bill{autopayInfo.declined_open === 1 ? '' : 's'} the card would not pay</> : null}
                    {autopayInfo.cards_on_file > autopayInfo.on_autopay && (
                      <button type="button" disabled={enableAutopayAll.isLoading || readOnly}
                        onClick={() => { if (window.confirm(`Turn autopay on for the ${autopayInfo.cards_on_file - autopayInfo.on_autopay} customer(s) who saved a card but have not chosen autopay? Their page told them the card is billed automatically.`)) enableAutopayAll.mutate(); }}
                        className="ml-2 px-2 py-0.5 rounded border border-amber-400 bg-white text-amber-900 font-semibold disabled:opacity-50">
                        Turn on for everyone with a card ({autopayInfo.cards_on_file - autopayInfo.on_autopay})
                      </button>
                    )}
                  </p>
                )}
              </div>
            </div>
          )}
        </Section>

        <Section title="Late fees" icon={Receipt} id="late-fees">
          <p className="text-sm text-gray-500">A daily run: a one-time late fee once a bill is this many days past its send day with money owed, then interest on the work still owed for each 30-day period after 30 days. Only bills made on or after the start date. Fees are their own lines; waive one on the bill. Nobody is texted. Saves with the section's Save button, together with the bill settings above.</p>
          <Toggle checked={lateFeesOn} onChange={setLateFeesOn} label="Charge late fees" sublabel="Off: nothing is added. On: the daily run adds fees to bills made on or after the start date below." />
          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="Late fee $" help="One-time, after the grace days.">
              <input type="number" min="0" max="1000" step="1" className={INPUT} value={lateFeeAmount} onChange={e => setLateFeeAmount(e.target.value)} />
            </FieldRow>
            <FieldRow label="Grace days" help="Days after the send day before the late fee.">
              <input type="number" min="0" max="365" step="1" className={INPUT} value={lateFeeGrace} onChange={e => setLateFeeGrace(e.target.value)} />
            </FieldRow>
            <FieldRow label="Interest % per month" help="On the work still owed, each 30-day period past 30 days; never on earlier fees.">
              <input type="number" min="0" max="50" step="0.1" className={INPUT} value={lateFeeRate} onChange={e => setLateFeeRate(e.target.value)} />
            </FieldRow>
            <FieldRow label="Late fees start" help="Only bills made on or after this day get fees — tell customers first.">
              <input type="date" className={INPUT} value={lateFeeStart} onChange={e => setLateFeeStart(e.target.value)} />
            </FieldRow>
          </div>
        </Section>

        <Section title="Stripe (cards)" icon={CreditCard} id="stripe">
          <p className="text-sm text-gray-500">Whether cards can be charged for real. Nothing to type here: the keys live on the server.</p>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {!stripeStatus?.configured ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2.5 py-0.5"><AlertCircle className="h-3.5 w-3.5" /> Not configured</span>
            ) : stripeStatus.mode === 'test' ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-0.5"><AlertCircle className="h-3.5 w-3.5" /> TEST MODE</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5"><CheckCircle className="h-3.5 w-3.5" /> LIVE MODE</span>
            )}
            {stripeStatus?.webhookConfigured ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5"><CheckCircle className="h-3.5 w-3.5" /> Webhook configured</span>
            ) : stripeStatus?.configured ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5"><AlertCircle className="h-3.5 w-3.5" /> Webhook not configured</span>
            ) : null}
          </div>
          {stripeStatus?.mode === 'test' && (
            <div className="rounded-md bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-800">
              <strong>Test mode moves no real money.</strong> Going live is a separate checklist: live keys on Railway, webhook secret, bank account in the Stripe dashboard. See <button type="button" onClick={() => goTo('go-live')} className="underline font-semibold">Admin → Go-live checklist</button>.
            </div>
          )}
          {stripeStatus?.mode === 'live' && (
            <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800"><strong>Live mode is active.</strong> Real cards will be charged.</div>
          )}
          {!stripeStatus?.configured && (
            <div className="rounded-md bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-600">
              Set <code className="font-mono bg-gray-100 px-1 rounded">STRIPE_SECRET_KEY</code> and <code className="font-mono bg-gray-100 px-1 rounded">STRIPE_WEBHOOK_SECRET</code> on the server.
            </div>
          )}
        </Section>
      </SectionPanel>

      {/* ══ Crew & pay ══ */}
      <SectionPanel section={SECTIONS[4]} active={active === 'crew'}>
      <Section title="Pay" icon={Users} id="crew-pay">
        <p className="text-sm text-gray-500">The rate for anyone without one of their own, and how often the crew are paid.</p>
        <RegisterSave k="employee" dirty={employeeDirty} busy={isSaving} save={saveEmployeeDefaults} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldRow label="Default hourly rate" help="For an employee with no rate of their own.">
            <div className="relative w-40">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
              <input type="number" min="0" step="0.50" className={`${INPUT} pl-7`} value={defaultHourlyRate} onChange={e => setDefaultHourlyRate(e.target.value)} />
            </div>
          </FieldRow>
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5 text-xs text-gray-600 space-y-1">
            <p className="font-semibold text-gray-800 flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> How pay works</p>
            <p>The ledger is weekly, Monday to Sunday. A stop pays its mowing estimate plus the planned drive to reach it (the first stop carries the drive out from home, the last the drive back), never the clock. You pay outside the app (Venmo, Zelle, cash) and record it on <Link to="/pay" className="text-brand-700 underline">Crew Pay</Link>, by the pay window below.</p>
          </div>
        </div>

        <PayWindowRow settings={settings} />

        <div className="border-t border-gray-100 pt-4 text-xs text-gray-500">
          Crew PINs and sign-in links are on the <Link to="/employees" className="text-brand-700 underline">Employees</Link> page. Your own PIN is under <button type="button" onClick={() => goTo('admin-pin')} className="text-brand-700 underline">Admin</button>.
        </div>
      </Section>

      {/* ── Inspections ── */}
      <InspectionsSection />

      {/* ── Treatment visits: the applicator and the minutes per round ── */}
      <TreatmentDefaultsSection />
      </SectionPanel>

      {/* ══ Money ══ */}
      <SectionPanel section={SECTIONS[5]} active={active === 'money'}>
      <Section title="Bank accounts" icon={Landmark} id="bank-accounts">
        <p className="text-sm text-gray-500">The bank and the Ramp card feed <Link to="/cash-flow" className="text-brand-700 underline">Cash Flow</Link>. Nothing here moves money; it only reads what happened.</p>
        <div className="rounded-xl border border-gray-200 p-3 mb-4 space-y-2">
          <Toggle checked={moneyText} onChange={v => { setMoneyText(v); saveMutation.mutate({ money_morning_text: String(v) }); }}
            label="Text me every morning how much money I have"
            sublabel="After 7 AM: in the bank · owed to you · you owe the crew · the month so far. The bank and the Ramp card are pulled after 6 AM first. Saves when tapped." />
          <button type="button" onClick={() => textMeNow.mutate()} disabled={textMeNow.isLoading || readOnly}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-semibold disabled:opacity-50">
            {textMeNow.isLoading ? 'Sending…' : 'Text me now'}
          </button>
        </div>
        {!bankStatus ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !bankStatus.configured ? (
          <div className="text-sm text-gray-600 space-y-1">
            <p>Bank sync isn't set up on the server yet. It needs a free Plaid account and three keys on Railway:</p>
            <pre className="text-xs bg-gray-50 rounded-lg p-3 inline-block">{`PLAID_CLIENT_ID=…
PLAID_SECRET=…
PLAID_ENV=production`}</pre>
            <p className="text-xs text-gray-500">Ask me when you're ready and I'll walk you through it.</p>
          </div>
        ) : (
          <>
            {bankStatus.connections.length === 0 ? (
              <p className="text-sm text-gray-600">No bank connected. Connect your business checking to see real cash flow.</p>
            ) : (
              <ul className="divide-y divide-gray-100 -mt-1">
                {bankStatus.connections.map((c: any) => (
                  <li key={c.id} className="py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="text-lg">🏦</span>
                    <div className="flex-1 min-w-[10rem]">
                      <p className="text-sm font-semibold text-gray-900">{c.institution_name || 'Bank'}</p>
                      <p className="text-xs text-gray-500">{c.transaction_count ?? 0} transactions{c.latest_transaction ? ` through ${c.latest_transaction}` : ''} · {fmtSync(c.last_synced_at)}</p>
                      {c.last_error && (
                        <p className="text-xs text-red-700 mt-0.5">
                          Last sync failed: {String(c.last_error).split(':')[0]}
                          {/ITEM_LOGIN_REQUIRED|INVALID_CREDENTIALS|ITEM_LOCKED|PENDING_EXPIRATION/.test(String(c.last_error)) ? ' — disconnect and connect it again to re-authorize.' : ''}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => { if (window.confirm(`Disconnect ${c.institution_name || 'this bank'}? Its ${c.transaction_count ?? 0} synced transactions leave Cash Flow too.`)) bankDisconnect.mutate(c.id); }}
                      disabled={bankDisconnect.isLoading}
                      className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 disabled:opacity-50">
                      <Trash2 className="h-3.5 w-3.5" /> Disconnect
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {bankStatus.connections.length > 0 && (
                <button onClick={() => bankSync.mutate()} disabled={bankSync.isLoading || readOnly} className={`${SAVE_BTN} gap-1.5`}>
                  <RefreshCw className={`h-4 w-4 ${bankSync.isLoading ? 'animate-spin' : ''}`} /> {bankSync.isLoading ? 'Syncing…' : 'Sync now'}
                </button>
              )}
              <ConnectBankButton
                label={bankStatus.connections.length > 0 ? 'Connect another bank' : 'Connect bank account'}
                className={bankStatus.connections.length > 0 ? 'btn-secondary text-sm' : `${SAVE_BTN} gap-1.5`}
                onConnected={() => { refetchBank(); qc.invalidateQueries(['cashflow']); }} />
            </div>
            <p className="text-xs text-gray-500">Transactions sync each time you open Cash Flow's Sync now, or here. Nothing is ever sent to your bank.</p>
          </>
        )}
        {/* Ramp card — purchases flow into the same Cash Flow / job-expense matcher */}
        {bankStatus && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-medium text-gray-800">Ramp card</p>
                {bankStatus.ramp?.configured ? (
                  <p className="text-xs text-gray-500">
                    Connected · {bankStatus.ramp.transaction_count} purchase{bankStatus.ramp.transaction_count === 1 ? '' : 's'} synced
                    {bankStatus.ramp.latest_transaction ? ` · latest ${bankStatus.ramp.latest_transaction}` : ''}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">
                    Not connected. In Ramp go to Settings → Developer API, create an app with the <code>transactions:read</code> scope,
                    then add <code>RAMP_CLIENT_ID</code> and <code>RAMP_CLIENT_SECRET</code> to the server.
                  </p>
                )}
              </div>
              {bankStatus.ramp?.configured && (
                <button onClick={() => rampSync.mutate()} disabled={rampSync.isLoading || readOnly} className={`${SAVE_BTN} gap-1.5`}>
                  <RefreshCw className={`h-4 w-4 ${rampSync.isLoading ? 'animate-spin' : ''}`} /> {rampSync.isLoading ? 'Syncing…' : 'Sync Ramp'}
                </button>
              )}
            </div>
          </div>
        )}
      </Section>
      </SectionPanel>

      {/* ══ Automations & logs ══ */}
      <SectionPanel section={SECTIONS[6]} active={active === 'automations'}>
      {/* Everything the app does by itself, read only (owner, Oct 20 2026: "a list of all my automations just so I know"). */}
      <Link to="/automations" id="automations-list" className="block rounded-xl border-2 border-brand-200 bg-brand-50 px-4 py-3 hover:bg-brand-100 scroll-mt-20">
        <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5"><Zap className="h-4 w-4 text-brand-700" /> Automations — everything the app does by itself</p>
        <p className="text-xs text-gray-600 mt-0.5">Every text, bill, route and bank job that runs on its own: what it does, who it reaches, whether it is on, and where its switch is.</p>
      </Link>

      <Section title="Crew photos" icon={Database} id="photo-storage">
        <p className="text-sm text-gray-500">Are the crew's photos being kept, or is the disk wiped every deploy?</p>
        <PhotoStorageStatus />
      </Section>

      {/* ── Backup ── */}
      <Section title="Backup" icon={Download} id="backup">
        <p className="text-sm text-gray-600">Every table in the app as one Excel workbook: customers, properties, jobs, routes, invoices, pay, expenses. No setup needed.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={exportAllData} disabled={dataExporting} className={`${SAVE_BTN} gap-1.5`}>
            <Download className={`h-4 w-4 ${dataExporting ? 'animate-bounce' : ''}`} />
            {dataExporting ? 'Building…' : 'Download everything (.xlsx)'}
          </button>
          {dataExportMsg && <span className={`text-sm ${dataExportMsg === 'Downloaded.' ? 'text-green-700' : 'text-red-600'}`}>{dataExportMsg}</span>}
        </div>
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          It holds every customer name, address, phone number and price. PINs and share links are redacted. Treat it like the business record it is.
        </p>
      </Section>

        <Section title="Google Sheets" icon={Sheet} id="google-sheets">
          <p className="text-sm text-gray-500">Optional: copy jobs, bills, quotes and route stops into a Google spreadsheet. The Backup above needs none of this.</p>
          <RegisterSave k="sheets" dirty={sheetsDirty} busy={isSaving} save={saveSheets} />
          <div className="flex items-center gap-2 mb-2">
            {integrationStatus?.configured ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5"><CheckCircle className="h-3.5 w-3.5" /> Credentials configured</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5"><AlertCircle className="h-3.5 w-3.5" /> Not configured</span>
            )}
            {integrationStatus?.configured && <span className="text-xs text-gray-500">{integrationStatus.service_account_email}</span>}
          </div>
          {!integrationStatus?.configured && (
            <div className="rounded-md bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-600 mb-4">
              Set <code className="font-mono bg-gray-100 px-1 rounded">GOOGLE_SERVICE_ACCOUNT_EMAIL</code> and <code className="font-mono bg-gray-100 px-1 rounded">GOOGLE_PRIVATE_KEY</code> on the server to enable exports. The private key is never shown here.
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4">
              <Toggle checked={sheetsEnabled} onChange={setSheetsEnabled} label="Enable Google Sheets sync" sublabel="Shows export buttons around the app." />
              <FieldRow label="Spreadsheet ID" help="From the sheet URL (…/spreadsheets/d/SPREADSHEET_ID/…)">
                <input type="text" className={`${INPUT} font-mono`} value={sheetsSpreadsheetId} onChange={e => setSheetsSpreadsheetId(e.target.value)} placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms" />
              </FieldRow>
              <FieldRow label="Private key">
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-gray-500 font-mono tracking-widest">●●●●●●●●●●●●</span>
                  <span className="text-xs text-gray-500">set via GOOGLE_PRIVATE_KEY</span>
                </div>
              </FieldRow>
            </div>
            <div className="space-y-4">
              <FieldRow label="Jobs sheet name"><input type="text" className={INPUT} value={sheetsJobsSheet} onChange={e => setSheetsJobsSheet(e.target.value)} /></FieldRow>
              <FieldRow label="Completed jobs sheet name"><input type="text" className={INPUT} value={sheetsCompletedSheet} onChange={e => setSheetsCompletedSheet(e.target.value)} /></FieldRow>
              <FieldRow label="Invoices sheet name"><input type="text" className={INPUT} value={sheetsInvoicesSheet} onChange={e => setSheetsInvoicesSheet(e.target.value)} /></FieldRow>
              <FieldRow label="Quotes sheet name"><input type="text" className={INPUT} value={sheetsQuotesSheet} onChange={e => setSheetsQuotesSheet(e.target.value)} /></FieldRow>
              <FieldRow label="Route stops sheet name"><input type="text" className={INPUT} value={sheetsRoutesSheet} onChange={e => setSheetsRoutesSheet(e.target.value)} /></FieldRow>
            </div>
          </div>
          {integrationStatus?.configured && (
            <div className="border-t border-gray-100 pt-4 mt-2">
              <p className="text-sm font-medium text-gray-700 mb-3">Manual export</p>
              {sheetsMsg && (
                <div className={`rounded-md px-3 py-2 text-sm mb-3 leading-relaxed break-words ${sheetsMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>{sheetsMsg.text}</div>
              )}
              <div className="flex flex-wrap gap-2">
                {[
                  { endpoint: 'jobs', label: 'All Jobs' }, { endpoint: 'jobs/completed', label: 'Completed Jobs' },
                  { endpoint: 'invoices', label: 'Invoices' }, { endpoint: 'quotes', label: 'Quotes' }, { endpoint: 'routes', label: 'Route Stops' },
                ].map(({ endpoint, label }) => (
                  <button key={endpoint} onClick={() => sheetsExport(endpoint, label)} disabled={!!sheetsExporting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    <UploadCloud className={`h-4 w-4 ${sheetsExporting === endpoint ? 'animate-bounce' : ''}`} />
                    {sheetsExporting === endpoint ? 'Exporting…' : label}
                  </button>
                ))}
              </div>
              {integrationStatus?.last_sync && Object.keys(integrationStatus.last_sync).length > 0 && (
                <div className="mt-3 text-xs text-gray-500 space-y-0.5">
                  {Object.entries(integrationStatus.last_sync).map(([type, log]: [string, any]) => (
                    <div key={type}>Last {type.replace('_', ' ')} export: {new Date(log.created_at).toLocaleString()} — {log.rows_exported} rows</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Section>
      </SectionPanel>

      {/* ══ Admin ══ */}
      <SectionPanel section={SECTIONS[7]} active={active === 'admin'}>
      <Section title="Admin PIN" icon={Lock} id="admin-pin">
        <p className="text-sm text-gray-500">The six digits you sign in with. Crew PINs are changed on the <Link to="/employees" className="text-brand-700 underline">Employees</Link> page.</p>
        <div>
          <button onClick={() => setShowPin(v => !v)} className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
            <Lock className="h-4 w-4 text-gray-500" /> Change admin PIN
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${showPin ? '' : '-rotate-90'}`} />
          </button>
          {showPin && (
            <div className="mt-3 space-y-3">
              {pinMsg && (
                <div className={`rounded-md px-4 py-2.5 text-sm ${pinMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>{pinMsg.text}</div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <FieldRow label="Current">
                  <input type="password" inputMode="numeric" maxLength={6} placeholder="••••••" className={`${INPUT} tracking-widest`} value={currentPin} onChange={e => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))} />
                </FieldRow>
                <FieldRow label="New">
                  <input type="password" inputMode="numeric" maxLength={6} placeholder="••••••" className={`${INPUT} tracking-widest`} value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))} />
                </FieldRow>
                <FieldRow label="Confirm">
                  <input type="password" inputMode="numeric" maxLength={6} placeholder="••••••" className={`${INPUT} tracking-widest`} value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))} />
                </FieldRow>
              </div>
              <button onClick={changeAdminPin} disabled={pinLoading || currentPin.length !== 6 || newPin.length !== 6 || confirmPin.length !== 6} className={SAVE_BTN}>
                {pinLoading ? 'Updating…' : 'Update PIN'}
              </button>
              <p className="text-xs text-gray-500">Six digits, no obvious sequences.</p>
            </div>
          )}
        </div>
      </Section>

      {/* ── Security ── */}
      <SecuritySection />

      {/* ── Connections at a glance ── */}
      <Section title="Connections" icon={Plug} id="connections">
        <p className="text-sm text-gray-500">What is hooked up and what is on, in one look.</p>
        <div className="divide-y divide-gray-100">
          <StatusLine label="Bank" ok={(bankStatus?.connections?.length || 0) > 0} warn={!!bankStatus?.configured && (bankStatus?.connections?.length || 0) === 0}
            text={!bankStatus?.configured ? 'Not set up' : (bankStatus?.connections?.length || 0) > 0 ? `${bankStatus!.connections.length} connected` : 'None connected'}
            note={(bankStatus?.connections?.length || 0) > 0 ? bankStatus!.connections.map((c: any) => c.institution_name).join(', ') : undefined} />
          <StatusLine label="Payments" ok={stripeStatus?.mode === 'live'} warn={stripeStatus?.mode === 'test'} text={stripeText}
            note={stripeStatus?.mode === 'test' ? 'You collect payments outside the app for now. Nothing here charges a card.' : undefined} />
          <StatusLine label="Invoicing" ok={!billingOff} warn={billingOff} text={billingOff ? 'Manual' : sendWithoutReview ? 'Sent without review' : 'Review first'}
            note={billingOff ? 'Monthly billing, sending and auto-charge are off. Change under Billing.'
              : sendWithoutReview ? 'Month-end bills go to customers without you checking them. Change under Billing.'
              : 'Month-end makes the bills and texts you. Nothing goes out until you tap Send on Billing → Review & send.'} />
          <StatusLine label="Texts" ok={!!smsStatus?.configured} text={smsStatus?.configured ? `On · ${smsStatus.phoneNumber || 'Twilio'}` : 'Not set up'}
            note={smsStatus?.configured ? undefined : 'Customer texts (job scheduled, cancel notices) wait on Twilio. Ask me when you want it.'} />
          {/* The one switch here that texts customers every mowing morning, so its state shows without opening Texts & email. */}
          {dayTexts && (
            <StatusLine label="Morning mow text" ok={dayTexts.mode === 'on'} warn={dayTexts.mode === 'rehearsal'}
              text={dayTexts.mode === 'on' ? `On · ${dayTexts.send_time_label}` : dayTexts.mode === 'rehearsal' ? 'Rehearsal' : 'Off'}
              note={`${dayTexts.today_line} ${dayTexts.mode === 'off' ? 'Turn it on under Texts & email, Mowing-day texts.' : 'Change under Texts & email, Mowing-day texts.'}`} />
          )}
          {/* Its second switch, "You're next", texts customers all through a mowing day, so its state shows here the same way. */}
          {dayTexts?.next && (
            <StatusLine label="You're next texts" ok={dayTexts.next.mode === 'on'} warn={dayTexts.next.mode === 'rehearsal'}
              text={dayTexts.next.mode === 'on' ? 'On' : dayTexts.next.mode === 'rehearsal' ? 'Rehearsal' : 'Off'}
              note={`${dayTexts.next.today_line} ${dayTexts.next.mode === 'off' ? 'Turn it on under Texts & email, Mowing-day texts.' : 'Change under Texts & email, Mowing-day texts.'}`} />
          )}
          {/* The third switch, the after-mow photo + referral text, the same way. */}
          {dayTexts?.done && (
            <StatusLine label="After-mow texts" ok={dayTexts.done.mode === 'on'} warn={dayTexts.done.mode === 'rehearsal'}
              text={dayTexts.done.mode === 'on' ? 'On' : dayTexts.done.mode === 'rehearsal' ? 'Rehearsal' : 'Off'}
              note={`${dayTexts.done.today_line} ${dayTexts.done.mode === 'off' ? 'Turn it on under Texts & email, Mowing-day texts.' : 'Change under Texts & email, Mowing-day texts.'}`} />
          )}
          {/* The fourth switch, the visit photo report (every customer, every job type), the same way. */}
          {dayTexts?.report && (
            <StatusLine label="Visit photo reports" ok={dayTexts.report.mode === 'on'} warn={dayTexts.report.mode === 'rehearsal'}
              text={dayTexts.report.mode === 'on' ? 'On' : dayTexts.report.mode === 'rehearsal' ? 'Rehearsal' : 'Off'}
              note={`${dayTexts.report.today_line} ${dayTexts.report.mode === 'off' ? 'Turn it on under Texts & email, Mowing-day texts.' : 'Change under Texts & email, Mowing-day texts.'}`} />
          )}
          {/* Its settings could not be read: say so. "Off" here could be untrue while texts are going. */}
          {dayTexts && !dayTexts.next && dayTexts.next_failed && (
            <StatusLine label="You're next texts" warn text="Could not load" note="Refresh the page to see whether it is on." />
          )}
          <StatusLine label="Email" ok={!!emailStatus?.configured} text={emailStatus?.configured ? `On · ${emailStatus.provider || ''}` : 'Not set up'} />
          <StatusLine label="Google Sheets" ok={!!integrationStatus?.configured && sheetsEnabled} text={integrationStatus?.configured ? (sheetsEnabled ? 'On' : 'Off') : 'Not set up'}
            note="Optional. The Backup under Automations & logs needs none of this." />
          <StatusLine label="Crew GPS" ok={!!locationStatus?.trackingEnabled} text={locationStatus?.trackingEnabled ? `On · ${locationStatus.geofenceRadiusMeters ?? 150} m` : 'Off'}
            note="Auto-starts a stop's timer when the crew arrives." />
        </div>
      </Section>

        <Section title="Go-live checklist & tools" icon={ClipboardList} id="go-live">
          <p className="text-sm text-gray-500 mb-2">Before switching from test mode to real money, and two one-tap tools for the addresses.</p>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Stripe</p>
              <div className="space-y-2">
                <CheckItem ok={stripeStatus?.mode === 'live'} label="Live secret key configured" note="Set STRIPE_SECRET_KEY=sk_live_..." />
                <CheckItem ok={stripeStatus?.webhookConfigured} label="Webhook secret configured" note="Set STRIPE_WEBHOOK_SECRET" />
                <CheckItem ok={stripeStatus?.publicUrlConfigured} label="Public URL configured (not localhost)" note={`APP_PUBLIC_URL=${stripeStatus?.appPublicUrl || '(not set)'}`} />
                <CheckItem ok={false} label="Bank account connected in Stripe Dashboard" note="dashboard.stripe.com → Settings → Payouts" manual />
                <CheckItem ok={false} label="Test payment completed in live mode" note="Send a real $1 invoice to yourself" manual />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Email & texts</p>
              <div className="space-y-2">
                <CheckItem ok={emailStatus?.configured} label="Email provider configured" note={emailStatus?.configured ? `Provider: ${emailStatus.provider}` : 'Set EMAIL_PROVIDER + credentials'} />
                <CheckItem ok={smsStatus?.configured} label="Twilio configured" note={smsStatus?.configured ? `From: ${smsStatus.phoneNumber}` : 'Set TWILIO_* on Railway'} />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Crew GPS</p>
              <div className="space-y-2">
                <CheckItem ok={locationStatus?.trackingEnabled} label="Location tracking enabled" note="LOCATION_TRACKING_ENABLED=true" />
                <CheckItem ok={locationStatus?.geofenceRequired !== undefined} label={`Geofence radius set (${locationStatus?.geofenceRadiusMeters ?? 150} m)`} note="GEOFENCE_RADIUS_METERS=150" />
                <CheckItem ok={locationStatus?.bypassCodeConfigured} label="Bypass code configured" note="Set LOCATION_BYPASS_CODE for crew override" />
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-700 mb-2">Map pins</p>
                <p className="text-xs text-gray-500 mb-3">Every property needs a pin for auto-arrival. New addresses get one when saved; this backfills the rest.</p>
                <button onClick={geocodeAllProperties} disabled={geocodingAll} className={`${SAVE_BTN} gap-2`}>
                  <MapPin className="h-4 w-4" />{geocodingAll ? 'Looking up…' : 'Find missing pins'}
                </button>
                {geocodeResult && geocodeResult.failed === -1 && <p className="mt-2 text-xs text-red-600">Lookup failed — check server logs.</p>}
                {geocodeResult && geocodeResult.failed !== -1 && (
                  <p className="mt-2 text-xs text-gray-600">
                    {geocodeResult.total === 0 ? 'Every property already has a pin.' : `${geocodeResult.succeeded} of ${geocodeResult.total} found${geocodeResult.failed > 0 ? ` · ${geocodeResult.failed} could not be found (check the address)` : ''}.`}
                  </p>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-700 mb-2">Address auto-correct</p>
                <p className="text-xs text-gray-500 mb-3">Addresses are tidied as they're typed ("118 n winecup trail" → "118 N Winecup Trail"). This re-checks every saved address. It only fixes capitals and spacing, never the street itself.</p>
                <button onClick={fixAllAddresses} disabled={fixingAddresses} className={`${SAVE_BTN} gap-2`}>
                  <MapPin className="h-4 w-4" />{fixingAddresses ? 'Checking…' : 'Auto-correct all addresses'}
                </button>
                {fixResult && fixResult.changed === -1 && <p className="mt-2 text-xs text-red-600">Could not run — check server logs.</p>}
                {fixResult && fixResult.changed !== -1 && (
                  <div className="mt-2 text-xs text-gray-600">
                    <p>{fixResult.changed === 0 ? `All ${fixResult.total} addresses already look right.` : `${fixResult.changed} of ${fixResult.total} addresses tidied.`}</p>
                    {fixResult.details.slice(0, 8).map((d, i) => <p key={i} className="text-gray-500">{d.from} → <span className="text-gray-800">{d.to}</span></p>)}
                    {fixResult.details.length > 8 && <p className="text-gray-500">…and {fixResult.details.length - 8} more</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Section>

      {isPractice && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 scroll-mt-20" id="practice">
          <p className="text-sm font-bold text-amber-900">Practice data</p>
          <p className="text-xs text-amber-800 mt-1">Every client, job and dollar on this server is fake. Reset wipes anything you changed and re-dates the fake jobs to today: yesterday done, today to run (one never started, one already in progress), tomorrow waiting.</p>
          <button onClick={resetPractice} disabled={resettingPractice} className={`${SAVE_BTN} mt-3`}>{resettingPractice ? 'Resetting…' : 'Reset practice data'}</button>
          {practiceMsg && <p className="mt-2 text-xs text-amber-900">{practiceMsg}</p>}
        </div>
      )}
      </SectionPanel>

        </div>
      </div>
    </div>
  );
}

/** Are the crew's photos actually being kept, or is the disk wiped every deploy? */
function PhotoStorageStatus() {
  const { data, isLoading } = useQuery('photo-storage', () => api.get('/settings/photo-storage').then(r => r.data));
  if (isLoading) return <p className="text-xs text-gray-500">Checking…</p>;
  if (!data) return <p className="text-xs text-gray-500">Could not check.</p>;
  const bad = !data.healthy;
  return (
    <div className={`rounded-lg border p-3 ${bad ? 'border-red-300 bg-red-50' : 'border-green-200 bg-green-50'}`}>
      <p className={`text-sm font-semibold ${bad ? 'text-red-800' : 'text-green-800'}`}>
        {!data.writable ? 'Photos cannot be saved at all'
          : !data.persistent ? 'Photos are on temporary storage — every deploy erases them'
          : 'Photos are being kept safely'}
      </p>
      <p className="text-xs text-gray-600 mt-1">
        {data.files_present} of the last {data.photos_checked} photos still have their file{data.files_missing > 0 ? ` · ${data.files_missing} lost` : ''}.
      </p>
      {!data.persistent && (
        <p className="text-xs text-gray-600 mt-1">
          Fix: in Railway add a Volume to this service, mount it at <code className="bg-white px-1 rounded">/data</code>, then set the variable <code className="bg-white px-1 rounded">UPLOAD_DIR=/data/uploads</code> and redeploy.
        </p>
      )}
      {data.write_error && <p className="text-xs text-red-700 mt-1">{data.write_error}</p>}
    </div>
  );
}

function CheckItem({ ok, label, note, manual }: { ok?: boolean; label: string; note?: string; manual?: boolean }) {
  return (
    <div className="flex items-start gap-2">
      {ok ? (
        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
      ) : manual ? (
        <div className="h-4 w-4 mt-0.5 flex-shrink-0 rounded-full border-2 border-gray-300" />
      ) : (
        <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
      )}
      <div>
        <span className="text-sm text-gray-700">{label}</span>
        {note && <p className="text-xs text-gray-500 mt-0.5 font-mono">{note}</p>}
        {manual && !ok && <p className="text-xs text-gray-500 mt-0.5 italic">Manual step — verify outside this app</p>}
      </div>
    </div>
  );
}

// ── Treatment plans: reusable treatment programs (templates) ──────────────────
function TreatmentPlansSection() {
  const qc = useQueryClient();
  const { data } = useQuery('treatment-plans', () => api.get('/treatments/plans').then(r => r.data.plans as any[]));
  const plans = data || [];
  const [newPlan, setNewPlan] = useState('');
  const [stepDraft, setStepDraft] = useState<Record<number, { label: string; price: string; timing: string }>>({});
  const refresh = () => qc.invalidateQueries('treatment-plans');
  const setDraft = (id: number, patch: any) =>
    setStepDraft(s => ({ ...s, [id]: { label: '', price: '', timing: '', ...(s[id] || {}), ...patch } }));

  const addPlan = async () => {
    const name = newPlan.trim(); if (!name) return;
    try { await api.post('/treatments/plans', { name }); setNewPlan(''); refresh(); } catch { toast.error('Could not add plan'); }
  };
  const delPlan = async (id: number) => {
    if (!window.confirm('Delete this plan?')) return;
    try { await api.delete(`/treatments/plans/${id}`); refresh(); } catch { toast.error('Could not delete'); }
  };
  const addStep = async (planId: number) => {
    const d = stepDraft[planId] || { label: '', price: '', timing: '' };
    if (!d.label.trim()) return;
    try {
      await api.post(`/treatments/plans/${planId}/steps`, { label: d.label.trim(), price: d.price ? Number(d.price) : null, timing: d.timing || null });
      setStepDraft(s => ({ ...s, [planId]: { label: '', price: '', timing: '' } })); refresh();
    } catch { toast.error('Could not add step'); }
  };
  const delStep = async (stepId: number) => {
    try { await api.delete(`/treatments/plan-steps/${stepId}`); refresh(); } catch { toast.error('Could not remove'); }
  };

  return (
    <Section title="Treatment plans" icon={Leaf} id="treatment-plans">
      <p className="text-sm text-gray-500 mb-3">Reusable treatment programs — define the steps once, then use them when quoting or offering a plan.</p>
      <div className="space-y-3">
        {plans.map((p: any) => (
          <div key={p.id} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <p className="font-medium text-gray-900">{p.name}{p.is_active === 0 && <span className="ml-2 text-[10px] font-semibold uppercase text-gray-500">not offered</span>}</p>
              <span className="flex items-center gap-2">
                {/* Offered = shown on every customer's page with an "I'm interested" button, and pickable when starting a program. */}
                <button
                  onClick={async () => { try { await api.put(`/treatments/plans/${p.id}`, { is_active: p.is_active === 0 ? 1 : 0 }); refresh(); } catch { toast.error('Could not update the plan'); } }}
                  className={`text-xs px-2 py-0.5 rounded-full border ${p.is_active === 0 ? 'text-gray-500 border-gray-300 hover:border-emerald-400' : 'text-emerald-700 border-emerald-300 bg-emerald-50'}`}>
                  {p.is_active === 0 ? 'Offer to customers' : 'Offered on customer pages'}
                </button>
                <button onClick={() => delPlan(p.id)} className="text-gray-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </span>
            </div>
            {p.description && <p className="text-xs text-gray-500">{p.description}</p>}
            {p.steps?.length > 0 && (
              <ul className="mt-2 space-y-1">
                {p.steps.map((st: any) => (
                  <li key={st.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-800">{st.label}{st.timing ? ` · ${st.timing}` : ''}</span>
                    <span className="flex items-center gap-2">
                      {st.price != null && <span className="text-gray-500">${Number(st.price).toFixed(0)}</span>}
                      <button onClick={() => delStep(st.id)} className="text-gray-300 hover:text-red-500 text-xs">✕</button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <input value={stepDraft[p.id]?.label || ''} onChange={e => setDraft(p.id, { label: e.target.value })} placeholder="Step (e.g. Pre-emergent)" className="input py-1.5 text-sm w-48" />
              <input value={stepDraft[p.id]?.timing || ''} onChange={e => setDraft(p.id, { timing: e.target.value })} placeholder="When (e.g. Mar)" className="input py-1.5 text-sm w-28" />
              <input value={stepDraft[p.id]?.price || ''} onChange={e => setDraft(p.id, { price: e.target.value.replace(/[^0-9.]/g, '') })} placeholder="$" inputMode="decimal" className="input py-1.5 text-sm w-20" />
              <button onClick={() => addStep(p.id)} className="text-sm text-brand-700 border border-brand-200 rounded-lg px-2 py-1 hover:bg-brand-50">Add step</button>
            </div>
          </div>
        ))}
        {plans.length === 0 && <p className="text-sm text-gray-500">No plans yet.</p>}
      </div>
      <div className="mt-3 flex gap-2">
        <input value={newPlan} onChange={e => setNewPlan(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPlan(); } }}
          placeholder="New plan name (e.g. 6-step lawn care)" className="input py-2 text-sm flex-1" />
        <button onClick={addPlan} disabled={!newPlan.trim()} className="btn-primary text-sm disabled:opacity-50">Add plan</button>
      </div>
    </Section>
  );
}

// What GET /quotes/followups/upcoming answers (server/services/bidFollowUps.upcoming).
type FollowUpsComing = {
  on: boolean; days: number[]; call_day: number; since: string | null;
  items: { quote_id: number; customer: string; what: string; step: 'text' | 'call'; n: number | null; of: number | null; due: string; due_words: string; texts_skipped: string | null; waiting: string | null }[];
  wording: { first: string; second: string; call: string };
};

// ── Winter work ────────────────────────────────────────────────────────────────
// "Do it in winter, save 10%" (server/services/winterWork; owner, Oct 26 2026): on a bid with one-time work, each line
// gets a Now / Winter choice on the customer's page; winter lines become a winter job to date between Dec 1 and Mar 1.
function WinterWorkSection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const cur = edit || {
    winter_discount_on: settings?.winter_discount_on ?? 'on',
    winter_discount_pct: settings?.winter_discount_pct ?? '10',
    winter_from: settings?.winter_from ?? '12-01',
    winter_to: settings?.winter_to ?? '03-01',
    winter_nudge_from: settings?.winter_nudge_from ?? '11-15',
    winter_offer_default: settings?.winter_offer_default ?? 'on',
  };
  const set = (k: string, v: string) => setEdit({ ...cur, [k]: v });
  const on = !['off', '0', 'false', 'no'].includes(String(cur.winter_discount_on).trim().toLowerCase());
  const byDefault = !['off', '0', 'false', 'no'].includes(String(cur.winter_offer_default).trim().toLowerCase());
  const save = useMutation((u: Record<string, string>) => api.put('/settings', u).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); qc.invalidateQueries('winter-list'); qc.invalidateQueries('winter-summary'); setEdit(null); toast.success('Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('winter-work', !!edit, save.isLoading, () => { if (edit) save.mutate(edit); });
  return (
    <Section title="Winter work" icon={Snowflake} id="winter-work">
      <p className="text-sm text-gray-500">Winter is slow, so one-time work a customer is happy to wait for (mulch, a cleanup, trimming, plantings) is cheaper done between the dates below. On the customer's bid page every one-time line shows its price now and its winter price, and they choose line by line — some now, some in winter. A line whose start day is already in the window gets the discount with no choice to make. When they sign, the winter lines become one winter job at the winter price, on the Calendar as "date to be set"; from the nudge day you get a weekly text and a Needs Attention item per job, and <Link to="/winter" className="text-brand-700 font-semibold hover:underline">Winter work</Link> lists them all. Never the mow itself, a business or a contract bid. No customer is texted without your tap.</p>
      <Toggle checked={on} onChange={v => set('winter_discount_on', v ? 'on' : 'off')} label="Offer winter work on bids" sublabel="Off: no bid offers it and the November text stops. Winter jobs already signed stay as they are." />
      <Toggle checked={byDefault} onChange={v => set('winter_offer_default', v ? 'on' : 'off')} label="Every one-time line carries the winter choice by itself" sublabel="On (since Oct 29): a new bid's mulch, cleanup, trimming and planting lines show Now / In winter without you tapping anything; the snowflake on a line, or the switch on the bid, takes it off. Off: only lines you mark with the snowflake carry it. Lines written before Oct 29 keep what they had either way." />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <FieldRow label="Percent off" help="10 = 10% off"><input type="number" min="1" max="90" inputMode="decimal" className={INPUT} value={cur.winter_discount_pct} onChange={e => set('winter_discount_pct', e.target.value)} /></FieldRow>
        <FieldRow label="Winter starts" help="Month-day, e.g. 12-01"><input className={INPUT} value={cur.winter_from} onChange={e => set('winter_from', e.target.value)} placeholder="12-01" /></FieldRow>
        <FieldRow label="Winter ends" help="Month-day, e.g. 03-01"><input className={INPUT} value={cur.winter_to} onChange={e => set('winter_to', e.target.value)} placeholder="03-01" /></FieldRow>
        <FieldRow label="Text me from" help="Month-day, e.g. 11-15"><input className={INPUT} value={cur.winter_nudge_from} onChange={e => set('winter_nudge_from', e.target.value)} placeholder="11-15" /></FieldRow>
      </div>
    </Section>
  );
}

// ── Crew pay window ────────────────────────────────────────────────────────────
// How often the crew are paid — what Crew Pay and the crew's phone add the weekly
// ledger up into (app_settings.pay_window; services/crewPay.windowFor and
// utils/payWindow.ts share the rule). Every two weeks is the rule since Sep 2026
// and the default; monthly is "1st – 1st" (owner, Oct 27 2026). The ledger rows
// stay weekly either way; a week that straddles the 1st counts in the month its
// Monday falls in. Saves on pick.
function PayWindowRow({ settings }: { settings?: Record<string, string> }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const mode = settings?.pay_window === 'monthly' ? 'monthly' : 'biweekly';
  const save = useMutation((pay_window: string) => api.put('/settings', { pay_window }).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); qc.invalidateQueries('pay-window'); qc.invalidateQueries('crew-pay'); toast.success('Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  return (
    <div className="border-t border-gray-100 pt-4">
      <FieldRow label="Crew pay window" help={mode === 'monthly'
        ? 'The 1st of a month through its last day, paid the 1st of the next. Crew Pay shows the month; the crew see one monthly total.'
        : 'Two Monday-to-Sunday pay weeks, paid the Monday after. Crew Pay shows the two weeks; the crew see their week.'}>
        <select className={`${INPUT} w-full sm:w-72`} value={mode} disabled={save.isLoading || readOnly} onChange={e => save.mutate(e.target.value)}>
          <option value="biweekly">Every two weeks</option>
          <option value="monthly">Monthly (1st to 1st)</option>
        </select>
      </FieldRow>
      <p className="text-xs text-gray-500 mt-1">The ledger stays weekly whichever you pick — a pay week that crosses the 1st counts in the month its Monday falls in, so nothing is paid twice or missed.</p>
    </div>
  );
}

// ── Automatic quotes ───────────────────────────────────────────────────────────
// A website lead who asks for mowing and gives an address gets a drafted bid at
// these prices (size read from their note; medium when unsure). Texting it to
// them is a separate switch, off until the owner turns it on.
function AutoQuoteSection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const cur = edit || {
    auto_quote_enabled: settings?.auto_quote_enabled ?? '1',
    auto_quote_send: settings?.auto_quote_send ?? '0',
    auto_quote_biweekly_extra: settings?.auto_quote_biweekly_extra ?? '0',
    // A text to him the first time a customer opens a bid he sent (server/services/bidOpened). On unless he turns it off.
    bid_opened_text: settings?.bid_opened_text ?? '1',
    // Bid follow-ups (server/services/bidFollowUps): a text 1 and 3 days after a bid went, then a call to-do on day 4.
    bid_followup_texts: settings?.bid_followup_texts ?? 'on',
    bid_followup_days: settings?.bid_followup_days ?? '1,3',
    bid_followup_call_day: settings?.bid_followup_call_day ?? '4',
    // "Offer lawn plans on new bids" (owner, Oct 22 2026): what every new bid's own switch starts from (quotes.offer_plans).
    bid_offer_plans_default: settings?.bid_offer_plans_default ?? '1',
    // One-off work (owner, Nov 1 2026; server/services/oneOffGates): a signed scope before it is scheduled, before and
    // after photos before its bill goes out. Both on unless he turns them off.
    one_off_scope_gate: settings?.one_off_scope_gate ?? '1',
    one_off_photo_gate: settings?.one_off_photo_gate ?? '1',
    weekly_discount_pct: settings?.weekly_discount_pct ?? '0',
    mow_price_bands: settings?.mow_price_bands ?? '[]',
  };
  const set = (k: string, v: string) => setEdit({ ...cur, [k]: v });
  // Price bands by lot size — the only place a mowing price is ever worked out from.
  let bands: { max_sqft: string; price: string }[] = [];
  try { bands = (JSON.parse(cur.mow_price_bands || '[]') as any[]).map(b => ({ max_sqft: b.max_sqft == null ? '' : String(b.max_sqft), price: String(b.price ?? '') })); } catch { bands = []; }
  const setBands = (next: { max_sqft: string; price: string }[]) => set('mow_price_bands', JSON.stringify(next.map(b => ({ max_sqft: b.max_sqft === '' ? null : Number(b.max_sqft), price: Number(b.price) || 0 }))));
  // What the follow-ups will do in the next week, and their exact words (GET /quotes/followups/upcoming).
  const { data: coming } = useQuery<FollowUpsComing>('bid-followups-upcoming', () => api.get('/quotes/followups/upcoming').then(r => r.data), { retry: false });
  const followDays = String(cur.bid_followup_days || '').split(',').map(x => x.trim());
  const setFollowDay = (i: number, v: string) => { const d = [followDays[0] || '', followDays[1] || '']; d[i] = v.replace(/\D/g, '').slice(0, 2); set('bid_followup_days', d[0] || d[1] ? d.join(',') : ''); };
  const followOn = !['off', '0', 'false', 'no'].includes(String(cur.bid_followup_texts).trim().toLowerCase());
  const save = useMutation((u: Record<string, string>) => api.put('/settings', u).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); qc.invalidateQueries('bid-followups-upcoming'); setEdit(null); toast.success('Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('automatic-quotes', !!edit, save.isLoading, () => save.mutate(cur));
  return (
    <Section title="Automatic quotes" icon={ClipboardList} id="automatic-quotes">
      <p className="text-sm text-gray-500">No estimated prices. A mowing price comes from the lot's square footage through your bands below, or the bid says "price to confirm" until you type it. A website mowing lead with an address gets a drafted bid; accepting it puts the yard on the schedule on the day the truck already comes closest.</p>
      <Toggle checked={cur.auto_quote_enabled === '1'} onChange={v => set('auto_quote_enabled', v ? '1' : '0')} label="Draft a mowing bid for new website leads" sublabel="You get a text that a draft is waiting." />
      <Toggle checked={cur.auto_quote_send === '1'} onChange={v => set('auto_quote_send', v ? '1' : '0')} label="Also text the lead their bid link right away" sublabel="Only a bid with a price from your bands is ever texted. Off = you read the draft first." />
      <Toggle checked={!['0', 'false', 'off', 'no'].includes(String(cur.bid_opened_text).trim().toLowerCase())} onChange={v => set('bid_opened_text', v ? '1' : '0')}
        label="Text me when a customer opens a bid"
        sublabel={'The first time a customer opens a bid you sent, you get one text: who, which bid, the price and when it went out. Opening it again is only counted on the bid ("Opened 3 times · last Tue 7:40 PM") — no second text. A browser signed in to the app, or a link opened from the app, never counts.'} />
      <Toggle checked={!['0', 'false', 'off', 'no'].includes(String(cur.bid_offer_plans_default).trim().toLowerCase())} onChange={v => set('bid_offer_plans_default', v ? '1' : '0')}
        label="Offer lawn plans on new bids"
        sublabel="What a new bid starts from. On: the customer's bid page offers the three lawn plans, an upgrade and extra applications above Accept. Off: just the work on the bid (the bed weed spray add-on still shows). Every bid has its own switch on the New Bid form, the on-site sheet and the bid itself, so you can flip it for one bid either way. A lawn-plan bid always offers the plans; a business bid never does." />
      <div className="rounded-xl border border-gray-200 p-3 space-y-3">
        <p className="text-xs font-semibold text-gray-700">One-off work</p>
        <Toggle checked={!['0', 'false', 'off', 'no'].includes(String(cur.one_off_scope_gate).trim().toLowerCase())} onChange={v => set('one_off_scope_gate', v ? '1' : '0')}
          label="One-off work needs a signed scope before it is scheduled"
          sublabel={'A one-off job (anything that is not a mow, a to-do or a quote visit) goes on the calendar only from a bid with a written scope that the customer signed — on the bid page, or by texting "I accept", or a plain YES when that is their one open bid and no "want a mow?" ask is waiting. The Create Job modal and "Make the job" on a bid both stop and say so. Day 2 of a job already booked passes.'} />
        <Toggle checked={!['0', 'false', 'off', 'no'].includes(String(cur.one_off_photo_gate).trim().toLowerCase())} onChange={v => set('one_off_photo_gate', v ? '1' : '0')}
          label="Photos before a one-off bill goes out"
          sublabel={'The crew\'s Start button wants a before photo and Complete an after photo on one-off work (the button says which is missing; a photo taken with no signal counts once it uploads). A bill with a one-off job on it that has no before or no after photo is not sent — Review & send, Needs Attention, the Send buttons and the month-end auto send all hold it and say "Before and after photos are needed before this bill goes out — {job}: missing before/after" — and the card on file is not charged for it. Work finished before Nov 1 2026 is not held.'} />
        <Toggle checked={followOn} onChange={v => set('bid_followup_texts', v ? 'on' : 'off')}
          label="Follow up on bids I send"
          sublabel="A bid the app sent (by text or email) that nobody has answered gets a text a day after it went and another a couple of days later, then a to-do on your day to call them. A bid you only marked sent, or texted from your own phone, gets none — their answer would come to your cell. 10 AM to 6 PM, Monday to Saturday, never on a holiday. Never a business, never once they text back, book a quote visit, accept another bid or pay the deposit, never after you tap Stop follow-ups on the bid. Someone who can't be texted still gets the call to-do." />
        <div className="grid grid-cols-3 gap-3">
          <FieldRow label="First text" help="Days after it went"><input type="number" min="1" max="30" inputMode="numeric" className={INPUT} value={followDays[0] || ''} onChange={e => setFollowDay(0, e.target.value)} /></FieldRow>
          <FieldRow label="Second text" help="Days after (blank: none)"><input type="number" min="1" max="30" inputMode="numeric" className={INPUT} value={followDays[1] || ''} onChange={e => setFollowDay(1, e.target.value)} /></FieldRow>
          <FieldRow label="Call to-do" help="On day (0: none)"><input type="number" min="0" max="45" inputMode="numeric" className={INPUT} value={cur.bid_followup_call_day} onChange={e => set('bid_followup_call_day', e.target.value.replace(/\D/g, '').slice(0, 2))} /></FieldRow>
        </div>
        <p className="text-[11px] text-gray-500">The first text is at least 20 hours after the bid went, the second at least 2 days after the first, the call to-do at least a day after the last text. A text more than a day late is skipped, never sent in a pile. Sending the bid again starts them over. Only bids sent since follow-ups started are ever followed up.</p>
        {coming?.wording && (
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-700 space-y-1.5">
            <p><span className="font-semibold">First text:</span> {coming.wording.first}</p>
            <p><span className="font-semibold">Second text:</span> {coming.wording.second}</p>
            <p><span className="font-semibold">Then:</span> {coming.wording.call}</p>
          </div>
        )}
        {coming && (
          <div>
            <p className="text-xs font-semibold text-gray-700">Coming up in the next week</p>
            {!coming.on ? <p className="text-xs text-gray-500 mt-1">Follow-ups are off.</p>
              : !coming.items.length ? <p className="text-xs text-gray-500 mt-1">Nothing — no open bid has a follow-up due this week.</p>
              : (
                <ul className="mt-1 divide-y divide-gray-100 text-xs">
                  {coming.items.map(it => (
                    <li key={`${it.quote_id}-${it.step}-${it.n || 0}`} className="py-1.5 flex items-baseline gap-2">
                      <span className="w-20 flex-shrink-0 font-semibold text-gray-800">{it.due_words}</span>
                      <span className="flex-1 min-w-0 text-gray-700">
                        <Link to={`/quotes?open=${it.quote_id}`} className="text-brand-700 underline">{it.customer}</Link> · {it.what} · {it.step === 'call' ? 'call to-do' : `text ${it.n} of ${it.of}`}
                        {it.texts_skipped && it.step === 'call' ? <span className="text-gray-500"> (no texts: {it.texts_skipped})</span> : null}
                        {it.waiting ? <span className="text-gray-500"> (waiting: {it.waiting})</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-800">Mowing price by lot size (per visit)</p>
        <p className="text-xs text-gray-500 mb-2">Each row: lots up to this many square feet cost this much. Leave the last size blank for "anything bigger". No row that fits = no price.</p>
        <div className="space-y-2">
          {bands.map((b, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Up to</span>
              <input type="number" className={`${INPUT} w-32`} placeholder="any size" value={b.max_sqft} onChange={e => setBands(bands.map((x, j) => j === i ? { ...x, max_sqft: e.target.value } : x))} />
              <span className="text-gray-500">sq ft →</span>
              <span className="text-gray-500">$</span>
              <input type="number" className={`${INPUT} w-24`} value={b.price} onChange={e => setBands(bands.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} />
              <button type="button" onClick={() => setBands(bands.filter((_, j) => j !== i))} className="text-xs text-gray-500 hover:text-red-600">remove</button>
            </div>
          ))}
          <button type="button" onClick={() => setBands([...bands, { max_sqft: '', price: '' }])} className="text-xs font-medium text-brand-700">+ Add a band</button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <FieldRow label="Weekly discount %" help="Off each mow when a yard goes weekly. Every new bid starts with this; change it per bid in the Schedule row."><input type="number" min="0" max="100" className={INPUT} value={cur.weekly_discount_pct} onChange={e => set('weekly_discount_pct', e.target.value)} /></FieldRow>
        <FieldRow label="Biweekly adds $" help="Every-other-week yards grow more between cuts. 0 = same price."><input type="number" className={INPUT} value={cur.auto_quote_biweekly_extra} onChange={e => set('auto_quote_biweekly_extra', e.target.value)} /></FieldRow>
      </div>
    </Section>
  );
}

// ── The public instant-quote page (get-quote.html) ──────────────────────
// Owner, Oct 3 2026: day cap 8 h, automatic booking off for the first weeks,
// $99 crossed off, anything within 10 miles, one-time 25% more, overgrown 2×.
// Every key is read by server/services/instantQuote.js; a blank box is the default.
const IQ_DEFAULTS: Record<string, string> = {
  instant_quote_enabled: '1', instant_quote_autobook: '0', instant_quote_front_only: '1', instant_quote_zips: '',
  instant_quote_max_miles_out: '10', instant_quote_visit_max_miles: '30', instant_quote_day_cap_minutes: '480',
  instant_quote_lead_days: '', instant_quote_one_time_pct: '25', instant_quote_overgrown_x: '2', instant_quote_consult_list_price: '99',
};
function InstantQuoteSection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const cur = edit || Object.fromEntries(Object.entries(IQ_DEFAULTS).map(([k, v]) => [k, settings?.[k] ?? v]));
  const set = (k: string, v: string) => setEdit({ ...cur, [k]: v });
  const save = useMutation((u: Record<string, string>) => api.put('/settings', u).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); setEdit(null); toast.success('Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('instant-quote', !!edit, save.isLoading, () => save.mutate(cur));
  const num = (k: string, label: string, help: string, placeholder?: string) => (
    <FieldRow label={label} help={help}><input type="number" min="0" className={INPUT} value={cur[k]} placeholder={placeholder} onChange={e => set(k, e.target.value)} /></FieldRow>
  );
  return (
    <Section title="Instant quote page" icon={Globe} id="instant-quote">
      <p className="text-sm text-gray-500">
        The public page at <a href="/get-quote.html" target="_blank" rel="noreferrer" className="text-brand-700 font-medium">app.kamtx.com/get-quote.html</a>: the lawn measured off the county map, your price bands on the grass, the weekly discount above for weekly yards, a day with room, and a free visit when it can't price. Every booking lands on Needs Attention as "booked online".
      </p>
      <Toggle checked={cur.instant_quote_enabled !== '0'} onChange={v => set('instant_quote_enabled', v ? '1' : '0')} label="The page is on" sublabel="Off: every step says online quotes are paused and gives your number. No redeploy." />
      <Toggle checked={cur.instant_quote_autobook === '1'} onChange={v => set('instant_quote_autobook', v ? '1' : '0')} label="Book straight onto the schedule" sublabel="Off: every booking waits for you to accept it. On: a booking with nothing flagged goes onto the route with no check by you." />
      <Toggle checked={cur.instant_quote_front_only !== '0'} onChange={v => set('instant_quote_front_only', v ? '1' : '0')} label='Offer "front yard only"' sublabel="Shown when the lot has one street side, priced on the front's square feet." />
      <FieldRow label="Only these zips (optional)" help="Comma or space separated. Empty = anywhere within the miles below.">
        <input className={INPUT} value={cur.instant_quote_zips} placeholder="78641, 78613" onChange={e => set('instant_quote_zips', e.target.value)} />
      </FieldRow>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {num('instant_quote_max_miles_out', 'Miles past your area', 'Farther than this from your yards: no instant price, a free visit instead.')}
        {num('instant_quote_visit_max_miles', 'Free visit: miles from the shop', 'Farther than this: "text us".')}
        {num('instant_quote_day_cap_minutes', 'Crew day (minutes)', 'A date is not offered once it would pass this. 480 = 8 hours.')}
        {num('instant_quote_lead_days', 'Days before the first mow', 'Blank = 3 while you accept each booking, 2 with booking straight on.', '3')}
        {num('instant_quote_one_time_pct', 'One-time mow, % more', 'Also the first mow of a schedule, credited back after the third.')}
        {num('instant_quote_overgrown_x', 'Overgrown first cut, × the mow', 'Grass consistently over 8 inches. 2 = double.')}
        {num('instant_quote_consult_list_price', 'Free visit, crossed-out $', '0 = no crossed-out price.')}
      </div>
    </Section>
  );
}

// ── Plan offers on a bid ────────────────────────────────────────────────
// Owner, Sep 18 2026: tapping a plan on a bid shows apps a year, $/app against
// the local going rate, and offers he adds here. The standing one: mowing
// customers get these prices, because the truck is already there.
function PlanOffersSection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const { data: offersData } = useQuery('plan-offers', () => api.get('/settings/plan-offers').then(r => r.data), { staleTime: 60_000 });
  const [local, setLocal] = useState<string | null>(null);
  const [offers, setOffers] = useState<any[] | null>(null);
  // What the neighbours charge — his own market table (services/localPrices).
  const { data: localData } = useQuery('local-prices', () => api.get('/settings/local-prices').then(r => r.data), { staleTime: 60_000 });
  const [table, setTable] = useState<any[] | null>(null);
  const rows: any[] = table ?? localData?.table ?? [];
  const updRow = (i: number, patch: any) => setTable(rows.map((r: any, k: number) => (k === i ? { ...r, ...patch } : r)));
  const cur = offers ?? offersData?.offers ?? [];
  const localVal = local ?? (settings?.local_treatment_price_per_app ?? '');
  const save = useMutation(async () => {
    await api.put('/settings', { local_treatment_price_per_app: localVal });
    if (table) await api.put('/settings/local-prices', { table });
    const r = await api.put('/settings/plan-offers', { offers: cur });
    return r.data;
  }, {
    onSuccess: (d: any) => { qc.invalidateQueries('app-settings'); qc.invalidateQueries('plan-offers'); qc.invalidateQueries('plan-options'); qc.invalidateQueries('local-prices'); setOffers(null); setLocal(null); setTable(null); toast.success(d?.message || 'Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  const upd = (i: number, patch: any) => setOffers(cur.map((o: any, k: number) => (k === i ? { ...o, ...patch } : o)));
  useSectionSave('plan-offers', offers !== null || local !== null || table !== null, save.isLoading, () => save.mutate());
  const PLAN_KEYS = [['full', 'Estate'], ['plan', 'The Plan'], ['simple', 'Simple']];
  return (
    <Section title="Plan offers on a bid" icon={Gift} id="plan-offers">
      <p className="text-sm text-gray-500">When you tap a plan on a bid it shows apps a year and the price per app. Put the local going rate here so it sits next to yours, and add any offers you want to be able to put on a bid.</p>
      {/* What the neighbours charge: the first per-application row sits beside his per-app price on a bid, the yearly row beside his year. */}
      <div>
        <p className="text-sm font-semibold text-gray-800">Local prices</p>
        <p className="text-xs text-gray-500 mb-2">Your own market numbers. The first "per application" row shows next to your price per app when you tap a plan on a bid; the "per year" row shows next to your yearly price. Only you see these.</p>
        <div className="space-y-1.5">
          {rows.map((r: any, i: number) => (
            <div key={r.key || i} className="flex flex-wrap items-center gap-1.5">
              <input className={`${INPUT} flex-1 min-w-[10rem]`} value={r.label} onChange={e => updRow(i, { label: e.target.value })} placeholder="Fert + weed (one application)" />
              <span className="text-xs text-gray-500">$</span>
              <input type="number" min="0" className={`${INPUT} w-20`} value={r.low ?? ''} onChange={e => updRow(i, { low: e.target.value })} placeholder="low" />
              <span className="text-xs text-gray-500">to $</span>
              <input type="number" min="0" className={`${INPUT} w-20`} value={r.high ?? ''} onChange={e => updRow(i, { high: e.target.value })} placeholder="high" />
              <input className={`${INPUT} w-28`} value={r.sweet || ''} onChange={e => updRow(i, { sweet: e.target.value })} placeholder="sweet spot" />
              <select className={INPUT} value={r.per === 'year' ? 'year' : 'app'} onChange={e => updRow(i, { per: e.target.value })}>
                <option value="app">per application</option>
                <option value="year">per year</option>
              </select>
              <button type="button" onClick={() => setTable(rows.filter((_: any, k: number) => k !== i))} className="text-xs text-gray-500 hover:text-red-600">remove</button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setTable([...rows, { label: '', low: '', high: '', sweet: '', per: 'app' }])} className="mt-2 text-xs font-semibold text-brand-700">+ Add a row</button>
      </div>
      <FieldRow label="Or one number to show instead, per application ($)" help="Optional. '95' or '75–115'. Blank = the first per-application row above.">
        <input className={INPUT} value={localVal} onChange={e => setLocal(e.target.value)} placeholder="blank = use the table" />
      </FieldRow>
      <div className="space-y-2">
        {cur.map((o: any, i: number) => (
          <div key={o.key || i} className="rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input className={`${INPUT} flex-1`} value={o.title} onChange={e => upd(i, { title: e.target.value })} placeholder="First month free" />
              <label className="text-xs text-gray-600 flex items-center gap-1"><input type="checkbox" checked={o.active !== false} onChange={e => upd(i, { active: e.target.checked })} /> on</label>
              <button type="button" onClick={() => setOffers(cur.filter((_: any, k: number) => k !== i))} className="text-xs text-gray-500 hover:text-red-600">remove</button>
            </div>
            <input className={`${INPUT} w-full`} value={o.blurb || ''} onChange={e => upd(i, { blurb: e.target.value })} placeholder="What the customer hears" />
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <select className={INPUT} value={o.discount?.kind || ''} onChange={e => upd(i, { discount: e.target.value ? { kind: e.target.value, value: o.discount?.value || 0 } : null })}>
                <option value="">Words only, no discount</option>
                <option value="first_month_free">First month free</option>
                <option value="amount">$ off, one time</option>
                <option value="percent">% off the first month</option>
              </select>
              {o.discount && o.discount.kind !== 'first_month_free' && (
                <input type="number" min="0" className={`${INPUT} w-24`} value={o.discount.value ?? ''} onChange={e => upd(i, { discount: { ...o.discount, value: e.target.value } })} placeholder={o.discount.kind === 'percent' ? '%' : '$'} />
              )}
              <span className="text-gray-500 ml-1">on:</span>
              {PLAN_KEYS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => upd(i, { plans: (o.plans || []).includes(k) ? (o.plans || []).filter((x: string) => x !== k) : [...(o.plans || []), k] })}
                  className={`press px-2 py-1 rounded-full border ${(o.plans || []).includes(k) ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}>{l}</button>
              ))}
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setOffers([...cur, { key: '', title: '', blurb: '', active: true, plans: ['full', 'plan', 'simple'], discount: null }])} className="text-xs font-medium text-brand-700">+ Add an offer</button>
      </div>
    </Section>
  );
}

// ── When his own day ends (the Field page) ─────────────────────────────
// Owner, Oct 5 2026: "I want the optimize and re route button to actually
// work". A plan for his day could run to 11:26 PM — a mow at 7:46 PM, a quote
// visit at 10:46 PM — without a word. The planner now lists every stop it only
// reaches after this hour as "won't happen today" (it moves nothing; that is
// his call). 9 PM unless he says otherwise: app_settings field_day_end.
function FieldDaySection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<string | null>(null);
  const cur = edit ?? (settings?.field_day_end || '21:00');
  const tooEarly = cur < '12:00';
  const save = useMutation(() => api.put('/settings', { field_day_end: cur }).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); qc.invalidateQueries('day-clock'); setEdit(null); toast.success('Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('field-day', edit !== null && !!cur && !tooEarly, save.isLoading, () => save.mutate());
  return (
    <Section title="Your day on the Field page" icon={CalendarClock} id="field-day">
      <FieldRow label="Your day ends at" help={'When you plan your day on Field, any stop the plan only reaches after this time is listed as "won\'t happen today". Nothing is moved for you — you decide what to do with it.'}>
        <input type="time" className={`${INPUT} max-w-[10rem]`} value={cur} onChange={e => setEdit(e.target.value)} />
      </FieldRow>
      {tooEarly && <p className="text-xs text-red-600">Pick a time from noon on.</p>}
    </Section>
  );
}

// ── Stops that start themselves when he gets there ─────────────────────
// Michael, Nov 3 2026: "Field / My Day auto-start jobs by location … zero taps required." The rules
// are on the phone (client/src/utils/geoAutoStop.ts, pure and tested); these are the five numbers
// behind them (server/services/autoStart.js). What leaving does defaults to the one-tap question on
// purpose: finishing a stop writes the clock that the day's pay and efficiency read, and a GPS drift
// out of the fence must never bill a lawn as mowed.
function AutoStartSection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const cur = edit || {
    auto_start_on: settings?.auto_start_on ?? 'on',
    auto_start_radius_m: settings?.auto_start_radius_m ?? '75',
    auto_start_dwell_s: settings?.auto_start_dwell_s ?? '60',
    auto_start_leave_s: settings?.auto_start_leave_s ?? '90',
    auto_start_on_leave: settings?.auto_start_on_leave ?? 'prompt',
  };
  const set = (k: string, v: string) => setEdit({ ...cur, [k]: v });
  const on = !['0', 'false', 'off', 'no'].includes(String(cur.auto_start_on).trim().toLowerCase());
  const num = (k: string) => Number(cur[k]);
  const bad = !(num('auto_start_radius_m') >= 25 && num('auto_start_radius_m') <= 150)
    || !(num('auto_start_dwell_s') >= 15 && num('auto_start_dwell_s') <= 600)
    || !(num('auto_start_leave_s') >= 30 && num('auto_start_leave_s') <= 1800);
  const save = useMutation((u: Record<string, string>) => api.put('/settings', u).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); qc.invalidateQueries('auto-start-settings'); setEdit(null); toast.success('Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('auto-start', !!edit && !bad, save.isLoading, () => save.mutate(cur));
  return (
    <Section title="Start stops by location" icon={Crosshair} id="auto-start">
      <p className="text-sm text-gray-500">
        On the <Link to="/today#field" className="text-brand-700 underline">Field</Link> page, a stop starts its own
        clock once your phone has been at the yard long enough — no tap. The page has to be open and on screen:
        a browser gets no location in the background, and an iPhone stops sharing it the moment the screen locks.
      </p>
      <Toggle checked={on} onChange={v => set('auto_start_on', v ? 'on' : 'off')}
        label="Start a stop when I get there"
        sublabel="Off means nothing happens on its own — Start and Done stay the taps they are today." />
      {on && (
        <>
          <FieldRow label="How close counts as here (metres)" help="25 to 150. Above about 50 m two stops on one street can both hold your phone; the app then starts whichever is next in your order unless another is clearly closer. A reading your phone is less sure of than this is thrown away rather than used.">
            <input type="number" min={25} max={150} step={5} className={`${INPUT} max-w-[8rem]`} value={cur.auto_start_radius_m} onChange={e => set('auto_start_radius_m', e.target.value)} />
          </FieldRow>
          <FieldRow label="Wait there this long first (seconds)" help="15 to 600. The phone has to stay inside the ring this whole time, so driving past a yard starts nothing.">
            <input type="number" min={15} max={600} step={5} className={`${INPUT} max-w-[8rem]`} value={cur.auto_start_dwell_s} onChange={e => set('auto_start_dwell_s', e.target.value)} />
          </FieldRow>
          <FieldRow label="When I leave a yard" help="A stop whose clock is running, once your phone has been outside the ring for the wait below. Walking next door normally keeps you inside the ring, so nothing fires.">
            <select className={`${INPUT} max-w-[18rem]`} value={cur.auto_start_on_leave} onChange={e => set('auto_start_on_leave', e.target.value)}>
              <option value="prompt">Ask me once — "done there?" (safer)</option>
              <option value="complete">Mark it done for me</option>
            </select>
          </FieldRow>
          <FieldRow label="Wait after leaving (seconds)" help="30 to 1800. Long enough that walking to the truck for a trimmer is not leaving.">
            <input type="number" min={30} max={1800} step={10} className={`${INPUT} max-w-[8rem]`} value={cur.auto_start_leave_s} onChange={e => set('auto_start_leave_s', e.target.value)} />
          </FieldRow>
          {cur.auto_start_on_leave === 'complete' && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Marking it done for you writes the finish time the day's pay, efficiency and the after-mow text all read.
              A bad GPS reading could finish a yard you are still on. "Ask me once" is one tap and cannot do that.
            </p>
          )}
          {bad && <p className="text-xs text-red-600">Ring 25–150 m, wait 15–600 s, wait after leaving 30–1800 s.</p>}
          <p className="text-xs text-gray-500">
            The crew's Route Runner has its own arrival rule (75 m, two readings) and is not changed by this.
          </p>
        </>
      )}
    </Section>
  );
}

// ── Quote visits customers can book ────────────────────────────────────
// Owner, Sep 18 2026: the referral page offers real free times. His windows,
// the visit length and gap, the days, and his phone's calendar link all live
// here; the app's own calendar is always counted as busy.
function QuoteVisitsSection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const cur = edit || {
    quote_visit_windows: settings?.quote_visit_windows ?? '06:30-08:00,18:00-20:00',
    quote_visit_minutes: settings?.quote_visit_minutes ?? '20',
    quote_visit_gap_minutes: settings?.quote_visit_gap_minutes ?? '10',
    quote_visit_days: settings?.quote_visit_days ?? '1,2,3,4,5,6',
    quote_visit_horizon_days: settings?.quote_visit_horizon_days ?? '14',
    quote_visit_min_hours_ahead: settings?.quote_visit_min_hours_ahead ?? '3',
    personal_calendar_ics_url: settings?.personal_calendar_ics_url ?? '',
  };
  const set = (k: string, v: string) => setEdit({ ...cur, [k]: v });
  const days = String(cur.quote_visit_days).split(',').map(x => Number(x.trim())).filter(Boolean);
  const toggleDay = (d: number) => set('quote_visit_days', (days.includes(d) ? days.filter(x => x !== d) : [...days, d]).sort().join(','));
  const save = useMutation((u: Record<string, string>) => api.put('/settings', u).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); setEdit(null); toast.success('Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('quote-visits', !!edit, save.isLoading, () => save.mutate(cur));
  const [check, setCheck] = useState<any>(null);
  const checkLink = useMutation(() => api.post('/settings/calendar-link-check', { url: cur.personal_calendar_ics_url }).then(r => r.data), {
    onSuccess: (d: any) => setCheck(d),
    onError: (e: any) => setCheck({ ok: false, message: e.response?.data?.message || 'Could not read that link' }),
  });
  return (
    <Section title="Quote visits customers can book" icon={CalendarClock} id="quote-visits">
      <p className="text-sm text-gray-500">
        The referral page shows real open times for you to come and look at a yard, and books one the moment they pick it (blue on the calendar, on your phone feed with the alarm).
        Busy is everything on the app's calendar — routes, jobs, quote visits — plus whatever is on the phone calendar linked below. Never Sunday.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldRow label="Windows to offer" help="Ranges separated by commas, 24-hour clock.">
          <input className={INPUT} value={cur.quote_visit_windows} onChange={e => set('quote_visit_windows', e.target.value)} placeholder="06:30-08:00,18:00-20:00" />
        </FieldRow>
        <div>
          <p className="text-sm font-medium text-gray-800 mb-1">Days</p>
          <div className="flex flex-wrap gap-1.5">
            {[[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat']].map(([d, l]) => (
              <button key={d} type="button" onClick={() => toggleDay(Number(d))} className={`press px-3 py-1.5 rounded-full border text-sm ${days.includes(Number(d)) ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}>{l}</button>
            ))}
            <span className="px-3 py-1.5 rounded-full border border-dashed border-gray-300 text-sm text-gray-500">Sun — never</span>
          </div>
        </div>
        <FieldRow label="Visit length (minutes)"><input type="number" min="5" className={INPUT} value={cur.quote_visit_minutes} onChange={e => set('quote_visit_minutes', e.target.value)} /></FieldRow>
        <FieldRow label="Gap between visits (minutes)"><input type="number" min="0" className={INPUT} value={cur.quote_visit_gap_minutes} onChange={e => set('quote_visit_gap_minutes', e.target.value)} /></FieldRow>
        <FieldRow label="How far ahead (days)"><input type="number" min="1" max="60" className={INPUT} value={cur.quote_visit_horizon_days} onChange={e => set('quote_visit_horizon_days', e.target.value)} /></FieldRow>
        <FieldRow label="Earliest (hours from now)" help="A slot has to be at least this far away."><input type="number" min="0" className={INPUT} value={cur.quote_visit_min_hours_ahead} onChange={e => set('quote_visit_min_hours_ahead', e.target.value)} /></FieldRow>
      </div>
      <FieldRow label="Your phone's calendar (private link)" help="iPhone: Calendar → tap the (i) by the calendar → Public Calendar → Share Link. Google: calendar Settings → 'Secret address in iCal format'. Paste it here; anything on it blocks a slot.">
        <div className="flex gap-2">
          <input className={INPUT} value={cur.personal_calendar_ics_url} onChange={e => set('personal_calendar_ics_url', e.target.value)} placeholder="webcal://… or https://…/basic.ics" />
          <button type="button" onClick={() => checkLink.mutate()} disabled={checkLink.isLoading || !cur.personal_calendar_ics_url} className="flex-shrink-0 text-sm font-medium text-brand-700 px-3 rounded-lg border border-brand-200 bg-white hover:bg-brand-50 disabled:opacity-50">{checkLink.isLoading ? 'Reading…' : 'Check it'}</button>
        </div>
        {check && (
          <div className={`mt-2 text-xs rounded-lg px-3 py-2 ${check.ok ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            <p>{check.message}</p>
            {check.ok && check.next?.length > 0 && <ul className="mt-1 space-y-0.5">{check.next.map((n: string, i: number) => <li key={i}>· {n}</li>)}</ul>}
          </div>
        )}
      </FieldRow>
    </Section>
  );
}

// ── Lawn walkthroughs ──────────────────────────────────────────────────
// Owner, Oct 20 2026: "I want my lawn to look better button where a professional
// comes out and gives them a runthrough. eg me, base it on my calendar." Who that
// professional is: him unless he hands it to someone with the Bidder or Manager hat.
// The times come from the quote-visit rules above (services/quoteSlots).
function WalkthroughSection() {
  const qc = useQueryClient();
  const { data } = useQuery('walkthrough-settings', () => api.get('/walkthrough/settings').then(r => r.data));
  const [pick, setPick] = useState<string | null>(null);
  const cur = pick ?? (data?.crew_member_id ? String(data.crew_member_id) : '');
  const save = useMutation(() => api.put('/walkthrough/settings', { crew_member_id: cur ? Number(cur) : null }).then(r => r.data), {
    onSuccess: (r: any) => { qc.invalidateQueries('walkthrough-settings'); setPick(null); toast.success(r?.message || 'Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('walkthroughs', pick !== null, save.isLoading, () => save.mutate());
  return (
    <Section title="Lawn walkthroughs" icon={Footprints} id="walkthroughs">
      <p className="text-sm text-gray-500">The free "I want my lawn to look better" walk a customer books from their page, a bid or a bill. It takes a quote-visit slot (the windows above) and lands on the Field page like a quote visit. Who walks it:</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldRow label="Who comes out" help="You, unless you hand it to someone wearing the Bidder or Manager hat.">
          <select className={INPUT} value={cur} onChange={e => setPick(e.target.value)}>
            <option value="">— {data?.default_name || 'Michael'} (the owner) —</option>
            {(data?.people || []).map((p: any) => <option key={p.id} value={String(p.id)}>{p.name}{p.hats?.length ? ` · ${p.hats.join(', ')}` : ''}</option>)}
          </select>
        </FieldRow>
      </div>
    </Section>
  );
}

// ── Service area ───────────────────────────────────────────────────────
// Drawn from the yards on a mowing schedule (owner, Sep 18 2026). Inside the
// edge is free; beyond it, a travel line per mow that steps up with distance.
/** Where the routes start — the home base as a setting (services/homeBase). Owner, Oct 30 2026: "add a starting point
 *  to all the routes and make it the CR 175 location." The address is found on the map by the server before it is saved. */
function HomeBaseBlock({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data: base } = useQuery<any>('home-base', () => api.get('/settings/home-base').then(r => r.data), { staleTime: 60_000 });
  const [edit, setEdit] = useState<{ address: string; name: string } | null>(null);
  const cur = edit || { address: base?.source === 'setting' ? (base?.address || '') : '', name: base?.source === 'setting' ? (base?.name || '') : '' };
  const save = useMutation((u: { address: string; name: string }) => api.put('/settings/home-base', u).then(r => r.data), {
    onSuccess: (d: any) => { qc.invalidateQueries('home-base'); qc.invalidateQueries('service-area'); qc.invalidateQueries('routes'); setEdit(null); toast.success(d?.message || 'Saved', { autoClose: 10000 }); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('home-base', !!edit, save.isLoading, () => save.mutate(cur));
  const where = !base ? '…'
    : base.source === 'setting' ? `${base.label}${base.address ? ` — ${base.address}` : ''}`
    : base.source === 'env' ? 'the pin set in the server settings (no address on file)'
    : '445 Monahans Dr, the old default';
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2 scroll-mt-20" id="home-base">
      <p className="text-sm font-semibold text-gray-900">Where the routes start</p>
      <p className="text-xs text-gray-600">
        Right now: <span className="font-medium text-gray-800">{where}</span>. The truck leaves from here and comes back here: the drive to the first yard and home from the last is paid from it,
        the route clock starts here, Optimize ends the day near it, and the crew's map draws its Start pin here. A route with its own start address on the Routes page keeps that.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="label-sm">Address</label>
          <input type="text" className={INPUT} value={cur.address} onChange={e => setEdit({ ...cur, address: e.target.value })} placeholder="Street address, town, ZIP" />
        </div>
        <div>
          <label className="label-sm">Short name</label>
          <input type="text" className={INPUT} value={cur.name} onChange={e => setEdit({ ...cur, name: e.target.value })} placeholder="CR 175" maxLength={40} />
        </div>
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        {save.isLoading && <span className="text-xs text-gray-600">Finding it on the map…</span>}
        {edit && !save.isLoading && <span className="text-xs text-gray-500">Saves with the section's Save button; the address is found on the map first.</span>}
        {base?.source === 'setting' && (
          <button onClick={() => { if (window.confirm('Go back to the old start point (445 Monahans)?')) save.mutate({ address: '', name: '' }); }} disabled={save.isLoading || readOnly}
            className="text-xs text-gray-500 underline">Clear</button>
        )}
      </div>
    </div>
  );
}

function ServiceAreaSection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { data: area } = useQuery('service-area', () => api.get('/service-area').then(r => r.data), { staleTime: 60_000 });
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const cur = edit || {
    service_area_miles: settings?.service_area_miles ?? '10',
    travel_step_miles: settings?.travel_step_miles ?? '5',
    travel_step_price: settings?.travel_step_price ?? '5',
    travel_enabled: settings?.travel_enabled ?? '1',
    turf_share: settings?.turf_share ?? '0.85',
  };
  const set = (k: string, v: string) => setEdit({ ...cur, [k]: v });
  const save = useMutation((u: Record<string, string>) => api.put('/settings', u).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); qc.invalidateQueries('service-area'); setEdit(null); toast.success('Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('service-area', !!edit, save.isLoading, () => save.mutate(cur));
  const restamp = useMutation(() => api.post('/service-area/restamp').then(r => r.data), {
    onSuccess: (d: any) => { qc.invalidateQueries('service-area'); toast.success(d.message); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not measure the yards'); },
  });
  const turfAll = useMutation(() => api.post('/service-area/turf-restamp').then(r => r.data), {
    onSuccess: (d: any) => { qc.invalidateQueries('customers'); toast.success(d.message, { autoClose: 15000 }); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not size the yards'); },
  });
  const miles = Number(cur.service_area_miles) || 0, step = Number(cur.travel_step_miles) || 5, price = Number(cur.travel_step_price) || 0;
  const example = (out: number) => `$${(Math.ceil(out / step) * price).toFixed(0)}`;
  return (
    <Section title="Service area" icon={MapPin} id="service-area">
      <HomeBaseBlock readOnly={readOnly} />
      <p className="text-sm text-gray-500">
        The area is every yard on a mowing schedule{area?.yards?.length ? ` — ${area.yards.length} of them right now` : ''}, with a ring around each.
        A new yard inside a ring pays nothing extra. Outside, its bid and every mow carry a "Travel" line that steps up the further out it is.
        The charge is set the day a yard is added and never moves on its own; existing customers were never charged. <Link to="/map" className="text-brand-700 underline">See the rings on the map</Link>.
      </p>
      <Toggle checked={cur.travel_enabled !== '0'} onChange={v => set('travel_enabled', v ? '1' : '0')} label="Charge for travel outside the area" sublabel="Off = the form still says how far out a yard is, but nothing is added." />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FieldRow label="Ring around each yard (miles)" help="A new yard this close to any current yard is in the area.">
          <input type="number" min="0" step="1" className={INPUT} value={cur.service_area_miles} onChange={e => set('service_area_miles', e.target.value)} />
        </FieldRow>
        <FieldRow label="Every … miles further out" help="One step of travel charge per this many miles (or part of one) past the ring.">
          <input type="number" min="1" step="1" className={INPUT} value={cur.travel_step_miles} onChange={e => set('travel_step_miles', e.target.value)} />
        </FieldRow>
        <FieldRow label="… adds $ per mow" help="Its own line on the bid and on the month-end bill.">
          <input type="number" min="0" step="1" className={INPUT} value={cur.travel_step_price} onChange={e => set('travel_step_price', e.target.value)} />
        </FieldRow>
      </div>
      <p className="text-xs text-gray-500">
        So a yard {miles + 3} miles from the nearest current yard pays {example(3)} a mow, {miles + 8} miles pays {example(8)}, {miles + 14} miles pays {example(14)}.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FieldRow label="Grass share of the green area" help="The county map gives each lot and how much of it is green (not roof, drive, patio, pool). Grass = green × this; beds and tree rings are the rest. Turf is rounded to 500 sq ft and put on every new yard the moment its address is in.">
          <input type="number" min="0.2" max="1" step="0.05" className={INPUT} value={cur.turf_share} onChange={e => set('turf_share', e.target.value)} />
        </FieldRow>
        <div className="flex items-end">
          <button type="button" onClick={() => turfAll.mutate()} disabled={turfAll.isLoading || readOnly} className="text-sm px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50">
            {turfAll.isLoading ? 'Asking the county map for every yard…' : 'Size every yard again from the county map'}
          </button>
        </div>
      </div>
      {area?.charged?.length > 0 && (
        <div className="text-xs text-gray-600">
          <p className="font-medium text-gray-800 mb-1">Yards carrying a travel line</p>
          <ul className="space-y-0.5">
            {area.charged.slice(0, 12).map((c: any) => (
              <li key={c.id}>{c.customer_name} — {[c.address, c.city].filter(Boolean).join(', ')} · {Math.ceil(c.travel_miles_out)} mi out · ${Number(c.travel_surcharge).toFixed(0)} a mow</li>
            ))}
            {area.charged.length > 12 && <li>… and {area.charged.length - 12} more</li>}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => restamp.mutate()} disabled={restamp.isLoading || readOnly} className="text-sm font-medium text-brand-700 px-3 py-2 rounded-lg border border-brand-200 bg-white hover:bg-brand-50 disabled:opacity-50">
          {restamp.isLoading ? 'Measuring…' : 'Measure yards never measured'}
        </button>
      </div>
    </Section>
  );
}

// ── Treatment defaults ────────────────────────────────────────────────────────
function TreatmentDefaultsSection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const { data: members } = useQuery('members-for-treatments', () => api.get('/crews/members/all').then(r => r.data.members as any[]));
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const cur = edit || {
    treatment_crew_member_id: settings?.treatment_crew_member_id ?? '',
    treatment_default_minutes: settings?.treatment_default_minutes ?? '30',
  };
  const set = (k: string, v: string) => setEdit({ ...cur, [k]: v });
  const save = useMutation((u: Record<string, string>) => api.put('/settings', u).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); setEdit(null); toast.success('Saved'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('treatment-visits', !!edit, save.isLoading, () => save.mutate(cur));
  return (
    <Section title="Treatment visits" icon={Leaf} id="treatment-visits">
      <p className="text-sm text-gray-500">Who program rounds are booked to (the licence holder) and how long a round is planned at. Start a program from the Treatments page.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldRow label="Applicator">
          <select className={INPUT} value={cur.treatment_crew_member_id} onChange={e => set('treatment_crew_member_id', e.target.value)}>
            <option value="">— Nobody yet (assign per program) —</option>
            {(members || []).map((m: any) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Minutes per round"><input type="number" className={INPUT} value={cur.treatment_default_minutes} onChange={e => set('treatment_default_minutes', e.target.value)} /></FieldRow>
      </div>
    </Section>
  );
}

// ── Security ──────────────────────────────────────────────────────────────────
// Owner only. What has been knocking on the door, and the one big switch:
// sign every device out (a lost phone, someone who left, a shared PIN).
const EVENT_LABEL: Record<string, string> = {
  pin_fail: 'Wrong PIN', pin_spike: 'PIN guessing spike', login: 'Login', twilio_rejected: 'Fake Twilio post blocked', sign_out_everywhere: 'Signed everyone out', pins_revealed: 'Login codes viewed',
};
function SecuritySection() {
  const auth: any = useAuth();
  const isOwner = auth?.user?.role === 'admin';
  const qc = useQueryClient();
  const { data } = useQuery('security-events', () => api.get('/auth/security-events').then(r => r.data), { enabled: isOwner, refetchInterval: 60_000 });
  const signOut = useMutation(() => api.post('/auth/sign-out-everywhere').then(r => r.data), {
    onSuccess: (r: any) => {
      if (r?.token) localStorage.setItem('token', r.token);
      toast.success(r?.message || 'Everyone is signed out');
      qc.invalidateQueries('security-events');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not sign everyone out'); },
  });
  if (!isOwner) return null;
  const events: any[] = data?.events || [];
  const fails = events.filter(e => e.kind === 'pin_fail').length;
  const fmt = (ts: string) => fmtTs(ts, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return (
    <Section title="Security" icon={Lock} id="security">
      <p className="text-sm text-gray-500">Wrong PINs are counted per address and across the whole app; a spike texts you. Texts arriving at the Twilio number are checked for Twilio's signature. If a phone is lost or someone leaves, sign every device out here, then change their PIN.</p>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => { if (window.confirm('Sign every device out now? Crew and managers will need to log in again with their PIN. You stay signed in.')) signOut.mutate(); }}
          disabled={signOut.isLoading}
          className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
          {signOut.isLoading ? 'Signing out…' : 'Sign everyone out'}
        </button>
        {data?.tokens_valid_from ? <span className="text-xs text-gray-500">Last done {fmt(new Date(data.tokens_valid_from * 1000).toISOString())}</span> : null}
        <span className="text-xs text-gray-500">{fails ? `${fails} wrong PIN${fails === 1 ? '' : 's'} in the last ${events.length} events` : 'No wrong PINs in the recent log'}</span>
      </div>
      {events.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead><tr className="text-left text-gray-500"><th className="py-1 pr-3">When</th><th className="py-1 pr-3">What</th><th className="py-1 pr-3">From</th><th className="py-1">Detail</th></tr></thead>
            <tbody>
              {events.slice(0, 15).map(e => (
                <tr key={e.id} className="border-t border-gray-100">
                  <td className="py-1 pr-3 whitespace-nowrap text-gray-500">{fmt(e.created_at)}</td>
                  <td className={`py-1 pr-3 font-medium ${e.kind === 'pin_fail' || e.kind === 'pin_spike' || e.kind === 'twilio_rejected' ? 'text-red-700' : 'text-gray-800'}`}>{EVENT_LABEL[e.kind] || e.kind}</td>
                  <td className="py-1 pr-3 text-gray-500 font-mono">{e.ip || '—'}</td>
                  <td className="py-1 text-gray-600">{e.detail || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ── Inspections ───────────────────────────────────────────────────────────────
// The owner's quality rule: a random handful of the week's lawns get checked
// against the crew checklist; enough perfect lawns (3 of 5) and the crew that
// mowed that week shares a bonus ($590) through crew pay.
function InspectionsSection() {
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const auth: any = useAuth();
  const role = auth?.user?.role;
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const cur = edit || {
    inspections_per_week: settings?.inspections_per_week ?? '5',
    inspection_pass_needed: settings?.inspection_pass_needed ?? '3',
    inspection_bonus: settings?.inspection_bonus ?? '590',
  };
  const set = (k: string, v: string) => setEdit({ ...cur, [k]: v });
  const save = useMutation((u: Record<string, string>) => api.put('/settings', u).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('app-settings'); setEdit(null); toast.success('Saved — applies from next week'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save'); },
  });
  useSectionSave('inspections', !!edit, save.isLoading, () => save.mutate(cur));
  const { data: weeksData } = useQuery('inspection-weeks', () => api.get('/inspections/weeks').then(r => r.data), { enabled: role === 'admin' || role === 'manager' || role === 'viewer' });
  const close = useMutation((id: number) => api.post(`/inspections/weeks/${id}/close`), {
    onSuccess: (r: any) => { if (r.status !== 202) toast.success(r.data?.message || 'Closed'); qc.invalidateQueries('inspection-weeks'); qc.invalidateQueries('crew-pay'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not close the week'); },
  });
  const fmt = (d: string) => new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const weeks: any[] = weeksData?.weeks || [];
  return (
    <Section title="Inspections" icon={ClipboardList} id="inspections">
      <p className="text-sm text-gray-500">Each week the app picks lawns at random from what was mowed. A Manager (or you) checks them against the crew's own list — Manager check → Lawns to inspect. Enough perfect lawns and the crew that mowed that week splits the bonus in Crew Pay. Give someone the Manager hat on the Employees page.</p>
      <div className="grid grid-cols-3 gap-4">
        <FieldRow label="Lawns per week"><input type="number" min={1} max={20} className={INPUT} value={cur.inspections_per_week} onChange={e => set('inspections_per_week', e.target.value)} /></FieldRow>
        <FieldRow label="Perfect lawns to pass"><input type="number" min={1} className={INPUT} value={cur.inspection_pass_needed} onChange={e => set('inspection_pass_needed', e.target.value)} /></FieldRow>
        <FieldRow label="Crew bonus $"><input type="number" min={0} className={INPUT} value={cur.inspection_bonus} onChange={e => set('inspection_bonus', e.target.value)} /></FieldRow>
      </div>
      {weeks.length > 0 && (
        <div className="overflow-x-auto mt-2">
          <table className="min-w-full text-xs">
            <thead><tr className="text-left text-gray-500"><th className="py-1 pr-3">Week</th><th className="py-1 pr-3">Checked</th><th className="py-1 pr-3">Perfect</th><th className="py-1 pr-3">Result</th><th className="py-1"></th></tr></thead>
            <tbody>
              {weeks.map((w: any) => (
                <tr key={w.id} className="border-t border-gray-100">
                  <td className="py-1.5 pr-3 whitespace-nowrap">{fmt(w.week_start)} – {fmt(w.week_end)}</td>
                  <td className="py-1.5 pr-3">{w.summary.done} of {w.sample_size}</td>
                  <td className="py-1.5 pr-3">{w.summary.perfect} (need {w.pass_needed})</td>
                  <td className="py-1.5 pr-3">
                    {w.status === 'closed'
                      ? (w.passed ? <span className="text-emerald-700 font-medium">Passed · ${Number(w.bonus_paid || 0).toFixed(2)} paid</span> : <span className="text-red-700">Did not pass</span>)
                      : (w.summary.passing ? <span className="text-emerald-700">Passing so far</span> : w.summary.still_possible ? <span className="text-gray-600">Open</span> : <span className="text-red-700">Can't pass</span>)}
                  </td>
                  <td className="py-1.5 text-right">
                    {w.status === 'open' && role === 'admin' && (
                      <button onClick={() => { if (window.confirm(w.summary.passing ? `Close this week as PASSED and add the $${w.bonus_amount} bonus to crew pay?` : 'Close this week without a bonus?')) close.mutate(w.id); }}
                        disabled={close.isLoading} className="text-brand-700 hover:underline">Close week</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ── Mowing-day texts ──────────────────────────────────────────────────────────
// Owner, Oct 8 2026: "start working on auto send eta texts to clients on routes
// eg, your next and morning text of coming by to mow today". This is the morning
// half (server/services/dayTexts.js): one text per customer with a mow on the
// day's routes. It ships Off.
//
// Nothing on this screen can send the customers' texts — only the clock does —
// so the worst a wrong tap here can do is change tomorrow morning. The switch
// and the time save the moment they are tapped (no Save button to forget on a
// rain morning): what the screen shows is what the sender will do. Turning it
// On asks first. What each choice does, today's sentence and every line of the
// list are the server's own words, so the screen cannot drift from the sender.
// A customer's phone number never reaches this page: the last four digits only.
type DayTextRow = {
  customer_id: number; name: string; phone_ending: string | null;
  // 'not_sent': the morning ended before their turn (switched off part way, or the two hours ran out).
  status: 'would_send' | 'left_out' | 'not_sent' | 'claimed' | 'sent' | 'failed';
  line: string;                    // the exact words, or "Cannot be texted: <why>", "Not sent: <why>", "Did not go: <why>"
  note: string | null; sent_at_label: string | null;
};
type DayTextPreview = {
  day: string; day_label: string; is_today: boolean; mode: DayTextMode; from: 'now' | 'this_morning'; headline: string;
  // 'ran' = the list is what happened this morning; 'past' = no run today and the send time has gone by, so the
  // list is only the day as it stands now (finished mows read "left out") and Tomorrow is the list worth opening.
  stage: 'ran' | 'before' | 'sending' | 'past' | 'later_day';
  list_title: string; list_note: string;                       // what the opened list is a list OF, in the server's words
  problem: null | { at_label: string | null; message: string };   // what the app itself said when the morning's run threw
  run: null | { status: string; mode: string | null; at_label: string | null };
  counts: { would_send: number; left_out: number; not_sent: number; claimed: number; sent: number; failed: number };
  customers: DayTextRow[];
};

/** '07:30' → '7:30 AM'. */
function clock12(hm: string): string {
  const h = Number(hm.slice(0, 2));
  return `${h % 12 === 0 ? 12 : h % 12}:${hm.slice(3, 5)} ${h < 12 ? 'AM' : 'PM'}`;
}

/** Every quarter hour he may pick, plus the saved time when it is not on one. */
function sendTimeChoices(s: DayTextSettings): string[] {
  const mins = (hm: string) => Number(hm.slice(0, 2)) * 60 + Number(hm.slice(3, 5));
  const out: string[] = [];
  for (let m = mins(s.earliest); m <= mins(s.latest); m += 15) out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  if (!out.includes(s.send_time)) out.push(s.send_time);
  return out.sort();
}

/** The people with a text ready: will they get it, would they, or would they have? The list's own day and run decide. */
function readyWords(p: DayTextPreview, startsTomorrow: boolean): { title: string; short: string } {
  if (p.from === 'this_morning') return { title: 'Would have been texted', short: 'would have been texted' };
  if (p.mode === 'on' && !(p.is_today && (startsTomorrow || p.stage === 'past'))) return { title: p.is_today ? 'Gets the text today' : 'Gets the text', short: p.is_today ? 'to text today' : 'to text' };
  return { title: 'Would get the text', short: 'would get the text' };
}

/** "4 texted · 1 did not go · 9 not sent · 2 left out" — each number is its own thing; what went wrong first, so it is never missed. */
function dayTextTally(p: DayTextPreview, startsTomorrow: boolean): string {
  const c = p.counts;
  return [
    c.failed ? `${c.failed} did not go` : '',
    c.claimed ? `${c.claimed} not sure` : '',
    c.not_sent ? `${c.not_sent} not sent` : '',
    c.sent ? `${c.sent} texted` : '',
    c.would_send ? `${c.would_send} ${readyWords(p, startsTomorrow).short}` : '',
    c.left_out ? `${c.left_out} left out` : '',
  ].filter(Boolean).join(' · ');
}

const OUTLINE_BTN = 'press inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50';

// ── Rain delay (owner, Oct 29 2026, a rainy 7 AM: "change the morning text this morning … add a way where I can do
// it in the app … rain delay, waiting till 11:30 to make a final decision … if not today, Saturday is a makeup day").
// server/services/rainDelay.js: the words, who gets it (the morning text's own list, one per phone) and what went today
// are all the server's. Sending is his tap here and nowhere else; a second send asks "again?".
type RainRun = { at_label: string | null; sent: number; failed: number; left_out: number; names: string[]; words: string | null; line: string | null };
type RainView = {
  day: string; day_label: string; at_hm: string;
  decide_by: string; decide_by_label: string; makeup: string; makeup_words: string | null; makeup_choices: { value: string; label: string }[];
  body: string; go_body: string; example: string;
  count: { would_send: number; left_out: number }; names: string[];
  left_out: { customer_id: number; name: string; reason: string }[];
  morning: { status: string; at_label: string | null } | null;
  sent: { delay: RainRun | null; go: RainRun | null } | null;
};
/** Quarter hours from 8 AM to 4 PM for "we decide by". */
const DECIDE_CHOICES = Array.from({ length: 33 }, (_, i) => { const m = 8 * 60 + i * 15; return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; });

function RainDelayBlock() {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { isPractice } = useAuth();
  const [open, setOpen] = useState(false);
  const [decideBy, setDecideBy] = useState('11:30');
  const [makeup, setMakeup] = useState<string | null>(null);     // null: the server's default (Saturday)
  const [words, setWords] = useState<string | null>(null);       // null: the server's words for these choices
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const view = useQuery<RainView>(['rain-delay', decideBy, makeup],
    () => api.get('/day-texts/rain', { params: { decide_by: decideBy, ...(makeup ? { makeup } : {}) } }).then(r => r.data),
    { enabled: open, keepPreviousData: true, retry: false });
  const v = view.data;
  const send = useMutation((b: { kind: 'delay' | 'go'; again: boolean }) =>
    api.post('/day-texts/rain', { kind: b.kind, decide_by: decideBy, makeup: makeup || v?.makeup, words: b.kind === 'delay' ? words : null, again: b.again }).then(r => r.data), {
    onSuccess: (d: any) => { setResult({ ok: true, message: d.message }); setWords(null); qc.invalidateQueries('rain-delay'); qc.invalidateQueries('day-texts-preview'); qc.invalidateQueries('day-texts-settings'); },
    onError: (e: any) => { setResult({ ok: false, message: e?.response?.data?.message || 'Could not send that' }); },
  });
  const n = v?.count.would_send || 0;
  const body = words ?? v?.body ?? '';
  const ask = (kind: 'delay' | 'go') => {
    if (!v || readOnly) return;
    const again = kind === 'delay' ? !!v.sent?.delay : !!v.sent?.go;
    const what = kind === 'delay' ? 'the rain-delay text' : 'the "rain cleared, we\'re coming" text';
    if (!window.confirm(`Send ${what} to ${n} customer${n === 1 ? '' : 's'} on today's list${again ? ' AGAIN' : ''}?\n\n${v.names.slice(0, 8).join(', ')}${v.names.length > 8 ? ` and ${v.names.length - 8} more` : ''}${isPractice ? '\n\n(Practice: no phone is texted.)' : ''}`)) return;
    setResult(null);
    send.mutate({ kind, again });
  };
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-black uppercase tracking-tight text-sky-900">Raining this morning?</p>
          <p className="text-xs text-sky-900/80">Tell today's customers we are waiting on the weather, when you decide, and the makeup day. Sent before the morning text, it takes its place; after, it goes as a second text.</p>
        </div>
        <button type="button" className={OUTLINE_BTN} onClick={() => setOpen(o => !o)}>{open ? 'Close' : 'Rain delay…'}</button>
      </div>
      {open && view.isLoading && !v && <p className="text-sm text-gray-500">Loading today's list…</p>}
      {open && view.isError && <p className="text-sm text-red-700">Could not load today's list. Close and open it again.</p>}
      {open && v && (
        <div className="space-y-3">
          {(v.sent?.delay || v.sent?.go) && (
            <div className="rounded-md bg-white border border-sky-200 px-3 py-2 text-sm text-gray-800 space-y-1">
              {v.sent?.delay?.line && <p>{v.sent.delay.line}</p>}
              {v.sent?.go?.line && <p>{v.sent.go.line}</p>}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldRow label="We decide by" help="Austin time">
              <select className={INPUT} value={decideBy} disabled={readOnly || send.isLoading} onChange={e => { setDecideBy(e.target.value); setWords(null); }}>
                {DECIDE_CHOICES.map(t => <option key={t} value={t}>{clock12(t)}</option>)}
              </select>
            </FieldRow>
            <FieldRow label="Makeup day" help="If we cannot mow today">
              <select className={INPUT} value={makeup || v.makeup} disabled={readOnly || send.isLoading} onChange={e => { setMakeup(e.target.value); setWords(null); }}>
                {v.makeup_choices.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </FieldRow>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">What the customer reads</p>
              {words != null && <button type="button" className="text-xs font-semibold text-sky-800 underline" onClick={() => setWords(null)}>Use the standard words</button>}
            </div>
            <p className="mt-1 text-xs text-gray-600">Each text starts "KeepAustinMowed: Hi {'{'}first name{'}'}," and then:</p>
            <textarea value={body} onChange={e => setWords(e.target.value.slice(0, 480))} rows={4} maxLength={480} disabled={readOnly || send.isLoading}
              className={`${INPUT} mt-1 w-full text-sm`} />
            <p className="mt-1 inline-block rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 text-sm text-gray-900">KeepAustinMowed: Hi Terry, {body}</p>
          </div>
          <div className="text-xs text-gray-700">
            {v.morning
              ? <p>The morning text already {v.morning.status === 'sent' ? `went at ${v.morning.at_label}` : `ran (${v.morning.status})`} — this goes as a second text.</p>
              : <p>The ordinary "we'll be by today" text will not go today once this is sent.</p>}
            {v.left_out.length > 0 && <p className="mt-1">{v.left_out.length} left out: {v.left_out.slice(0, 6).map(l => `${l.name} — ${l.reason}`).join('; ')}{v.left_out.length > 6 ? '; …' : ''}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={SAVE_BTN} disabled={readOnly || send.isLoading || n === 0} onClick={() => ask('delay')}>
              {send.isLoading ? 'Sending…' : v.sent?.delay ? `Send the rain delay again (${n})` : `Send the rain delay to ${n} customer${n === 1 ? '' : 's'}`}
            </button>
            {v.sent?.delay && (
              <button type="button" className={OUTLINE_BTN} disabled={readOnly || send.isLoading || n === 0} onClick={() => ask('go')}>
                {v.sent?.go ? 'Send "we\'re coming" again' : `Rain cleared — we're coming (${n})`}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-600">Moving the day to {v.makeup_words || 'another day'} instead? Reschedule the route on the Routes page — that texts them the new day.</p>
          {result && (
            <div className={`rounded-md px-3 py-2 text-sm ${result.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>{result.message}</div>
          )}
        </div>
      )}
    </div>
  );
}
// The name over each of the section's two blocks ("The morning text", "You're next").
const BLOCK_TITLE = 'text-base font-black uppercase tracking-tight text-gray-900';

function MowingDayTextsSection() {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { isPractice } = useAuth();
  const { data: s, isLoading } = useDayTextSettings();
  const [open, setOpen] = useState(false);
  const [which, setWhich] = useState<'today' | 'tomorrow'>('today');
  const [sample, setSample] = useState<{ ok: boolean; message: string; text?: string } | null>(null);

  // Today's list is read as soon as the section shows: its first sentence says what happened this morning.
  // "Today" is the server's own Austin day; tomorrow is asked for by date.
  const todayList = useQuery<DayTextPreview>(['day-texts-preview', 'today'],
    () => api.get('/day-texts/preview').then(r => r.data), { retry: false });
  const tomorrowList = useQuery<DayTextPreview>(['day-texts-preview', 'tomorrow'],
    () => api.get('/day-texts/preview', { params: { date: isoPlusDays(businessToday(), 1) } }).then(r => r.data),
    { retry: false, enabled: open && which === 'tomorrow' });

  const save = useMutation((u: { mode?: DayTextMode; send_time?: string }) =>
    api.put('/day-texts/settings', u).then(r => r.data as { message: string; settings: DayTextSettings }), {
    onSuccess: (d) => { qc.setQueryData('day-texts-settings', d.settings); qc.invalidateQueries('day-texts-preview'); toast.success(d.message, { autoClose: 8000 }); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  // The body is empty on purpose: the server texts the owner's own phone and reads no number from anyone.
  const sendSample = useMutation(() => api.post('/day-texts/sample').then(r => r.data as { message: string; text: string }), {
    onSuccess: (d) => setSample({ ok: true, message: d.message, text: d.text }),
    onError: (e: any) => setSample({ ok: false, message: e?.response?.data?.message || 'Could not send the sample' }),
  });

  if (isLoading) return <Section title="Mowing-day texts" icon={MessageSquare} id="mowing-day-texts"><p className="text-sm text-gray-500">Loading…</p></Section>;
  if (!s) return <Section title="Mowing-day texts" icon={MessageSquare} id="mowing-day-texts"><p className="text-sm text-red-700">Could not load this. Refresh the page to try again.</p></Section>;

  const busy = save.isLoading;
  const pickMode = (m: DayTextMode) => {
    if (m === s.mode || busy || readOnly) return;
    // On is the only choice that reaches customers, so it is the only one that asks.
    if (m === 'on' && !window.confirm(`Turn the mowing-day texts on?\n\nStarting tomorrow morning at ${s.send_time_label}, each customer with a mow on that day's routes gets one text. No morning text goes today.`)) return;
    save.mutate({ mode: m });
  };

  const today = todayList.data;
  // Amber when the morning needs a look: it did not go, it ended part way, a text was refused or its end is unknown,
  // the run threw — or it ran and there was nobody to text although there was mowing.
  const bad = !!today && (['missed', 'partial', 'stopped', 'no_texting'].includes(today.run?.status || '')
    || today.counts.failed > 0 || today.counts.claimed > 0 || today.counts.not_sent > 0 || !!today.problem
    || (today.run?.status === 'nobody' && today.run?.mode === 'on' && today.counts.left_out > 0));
  // Today's own sentence. With the texts off and nothing run it is only "Off. No morning text goes.", which the choice above already says.
  const showToday = !!today && (s.mode !== 'off' || !!today.run);
  const tally = today ? dayTextTally(today, s.starts_tomorrow) : '';
  // Once the morning has run, the list is what happened — and the button says so.
  const ranToday = today?.stage === 'ran';
  // No run today and the send time has gone by: today's list is only the day as it stands, so it opens on Tomorrow.
  const openOn: 'today' | 'tomorrow' = today?.stage === 'past' ? 'tomorrow' : 'today';

  const list = which === 'today' ? todayList : tomorrowList;
  const p = list.data;
  const groups: { status: DayTextRow['status']; title: string; tone: string }[] = p ? [
    { status: 'failed', title: 'Did not go', tone: 'text-red-700' },
    { status: 'claimed', title: 'Not sure it went', tone: 'text-gray-800' },
    { status: 'not_sent', title: 'Not sent', tone: 'text-red-700' },
    { status: 'sent', title: 'Texted', tone: 'text-gray-800' },
    { status: 'would_send', title: readyWords(p, s.starts_tomorrow).title, tone: 'text-gray-800' },
    { status: 'left_out', title: 'Left out', tone: 'text-amber-800' },
  ] : [];

  return (
    <Section title="Mowing-day texts" icon={MessageSquare} id="mowing-day-texts">
      {/* Two texts, two switches: each block is named, so the Off / Rehearsal / On under it is never taken for the other's. */}
      <div>
        {s.next && <h4 className={`${BLOCK_TITLE} mb-1`}>The morning text</h4>}
        <p className="text-sm text-gray-600">
          On the morning of a mow, each customer on that day's routes gets one text saying we'll be by to mow today.
          Businesses, and anyone who replied STOP, has these texts switched off or has no phone, are left out.
        </p>
      </div>
      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">What the customer reads</p>
        <p className="mt-1 inline-block rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 text-sm text-gray-900">{s.example_text}</p>
        <p className="mt-1 text-xs text-gray-500">It says "today" and no time, unless the stop has a promised time. A customer with more than one yard that day gets one text naming the addresses. Two customer records with the same phone number get one text between them.</p>
      </div>

      <fieldset disabled={readOnly || busy} className="space-y-2">
        <legend className="sr-only">Mowing-day texts: off, rehearsal or on</legend>
        {s.modes.map(m => {
          const on = s.mode === m.value;
          return (
            <label key={m.value} className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${on ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white'} ${readOnly ? 'opacity-60' : 'cursor-pointer'}`}>
              <input type="radio" name="mow-today-texts" className="mt-0.5 h-4 w-4 accent-brand-600" checked={on} disabled={readOnly || busy} onChange={() => pickMode(m.value)} />
              <span>
                <span className="block text-sm font-semibold text-gray-900">{m.label}</span>
                <span className="block text-xs text-gray-600 mt-0.5">{m.help}</span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <FieldRow label="Send time" help="Austin time. If the app is not running within two hours of it, no morning text goes that day and you are told.">
        <select aria-label="Send time" className={`${INPUT} max-w-[10rem] disabled:opacity-60`} value={s.send_time} disabled={readOnly || busy}
          onChange={e => { if (e.target.value !== s.send_time) save.mutate({ send_time: e.target.value }); }}>
          {sendTimeChoices(s).map(t => <option key={t} value={t}>{clock12(t)}</option>)}
        </select>
      </FieldRow>

      {showToday && today && (
        <div className={`rounded-md border px-3 py-2 text-sm ${bad ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>
          {/* The server's own sentence, true for the state it is in: "On. Today's texts go at 7:30 AM." / "…went at 7:31 AM: 12 texted, 2 left out." / "…did not go - <why>." */}
          <p>{today.headline}</p>
          {today.problem && (
            <p className="mt-1 text-xs break-words">What the app said{today.problem.at_label ? ` at ${today.problem.at_label}` : ''}: {today.problem.message}</p>
          )}
        </div>
      )}

      {/* Raining? The rain-delay text to today's customers, on his tap (server/services/rainDelay.js). */}
      <RainDelayBlock />

      <div className="flex flex-wrap items-center gap-2">
        {/* The one filled button. It only reads, so a view-only login can open it too. */}
        <button type="button" className={SAVE_BTN}
          onClick={() => { if (!open) { setWhich(openOn); todayList.refetch(); } setOpen(v => !v); }}>
          {open ? 'Hide the list' : ranToday ? 'What happened this morning' : 'Who gets a text today'}
        </button>
        <button type="button" onClick={() => { setSample(null); sendSample.mutate(); }} disabled={sendSample.isLoading || readOnly} className={OUTLINE_BTN}>
          {sendSample.isLoading ? 'Sending…' : 'Send me a sample'}
        </button>
        {/* After the send time on a day with no run, today's numbers are only "as it stands now" — they are said inside the list, under that heading. */}
        {!open && tally && today?.stage !== 'past' && <span className="text-xs text-gray-500">{ranToday ? 'This morning' : 'Today'}: {tally}</span>}
      </div>
      <p className="text-xs text-gray-500">The sample goes to your own phone{s.owner_phone_ending ? ` ending ${s.owner_phone_ending}` : ''} and to nobody else.</p>
      {sample && (
        <div className={`rounded-md px-3 py-2 text-sm ${sample.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {/* The server's answer says by itself when this is the practice app and no phone was texted. */}
          <p>{sample.message}</p>
          {sample.text && <p className="mt-1 text-gray-800">{isPractice ? 'It would say' : 'It says'}: {sample.text}</p>}
        </div>
      )}

      {open && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50">
            {(['today', 'tomorrow'] as const).map(d => (
              <button key={d} type="button" onClick={() => setWhich(d)} aria-pressed={which === d}
                className={`px-3 py-1 rounded-full border text-xs ${which === d ? 'border-gray-900 text-gray-900 font-semibold bg-white' : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'}`}>
                {d === 'today' ? 'Today' : 'Tomorrow'}
              </button>
            ))}
            {p && <span className="text-xs text-gray-600">{p.day_label}{dayTextTally(p, s.starts_tomorrow) ? ` · ${dayTextTally(p, s.starts_tomorrow)}` : ''}</span>}
          </div>
          {list.isLoading && <p className="px-3 py-4 text-sm text-gray-500">Loading…</p>}
          {list.isError && <p className="px-3 py-4 text-sm text-red-700">Could not load the list. Close it and open it again.</p>}
          {p && (
            <>
              {/* What this list is a list OF — who gets it, who would, or what happened this morning — always said first. */}
              <div className="px-3 py-2">
                <p className="text-sm font-semibold text-gray-900">{p.list_title}</p>
                <p className="text-xs text-gray-600 mt-0.5">{p.list_note}</p>
              </div>
              {p.customers.length === 0 && (
                <p className="px-3 py-3 text-sm text-gray-500">
                  {p.from === 'now' ? `No mowing on ${p.day_label}, so there is no morning text to send.` : 'Nobody was on this morning\'s list.'}
                </p>
              )}
              {groups.map(g => {
                const rows = p.customers.filter(r => r.status === g.status);
                if (!rows.length) return null;
                return (
                  <div key={g.status}>
                    <p className="px-3 py-1.5 border-y border-gray-100 bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-600">{g.title} ({rows.length})</p>
                    <ul className="divide-y divide-gray-100">
                      {rows.map(r => (
                        <li key={r.customer_id} className="px-3 py-2">
                          <p className="text-sm">
                            <Link to={`/customers/${r.customer_id}`} className="font-semibold text-gray-900 hover:underline">{r.name}</Link>
                            {r.phone_ending && <span className="text-gray-500"> · phone ending {r.phone_ending}</span>}
                            {r.sent_at_label && <span className="text-emerald-700"> · sent {r.sent_at_label}</span>}
                          </p>
                          <p className={`text-sm mt-0.5 break-words ${g.tone}`}>{r.line}</p>
                          {r.note && <p className="text-xs text-amber-800 mt-0.5">{r.note}</p>}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {s.next && <YoureNextBlock n={s.next} />}
      {/* The server could not read this block's settings. It says so: "Off" could be untrue while texts are going. */}
      {!s.next && s.next_failed && (
        <div className="border-t border-gray-200 pt-4" id="youre-next-texts">
          <h4 className={`${BLOCK_TITLE} mb-1`}>You're next</h4>
          <p className="text-sm text-red-700">Could not load this part. Refresh the page to see whether it is on.</p>
        </div>
      )}

      {s.done && <AfterMowBlock d={s.done} />}
      {!s.done && s.done_failed && (
        <div className="border-t border-gray-200 pt-4" id="after-mow-texts">
          <h4 className={`${BLOCK_TITLE} mb-1`}>After the mow: photo + referral offer</h4>
          <p className="text-sm text-red-700">Could not load this part. Refresh the page to see whether it is on.</p>
        </div>
      )}

      {s.report && <VisitReportBlock r={s.report} />}
      {!s.report && s.report_failed && (
        <div className="border-t border-gray-200 pt-4" id="visit-report-texts">
          <h4 className={`${BLOCK_TITLE} mb-1`}>Visit photo report to the customer</h4>
          <p className="text-sm text-red-700">Could not load this part. Refresh the page to see whether it is on.</p>
        </div>
      )}

      {/* The one text HE gets from these: a stop skipped after its customer was told we were coming. Said here so it is not a surprise. */}
      {s.told_skipped && (
        <div className="border-t border-gray-200 pt-4 space-y-2" id="told-then-skipped">
          <p className="text-sm text-gray-600">{s.told_skipped.about}</p>
          {s.told_skipped.today.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50">
              <p className="px-3 py-1.5 border-b border-amber-200 text-xs font-bold uppercase tracking-wide text-amber-900">Skipped today after being told ({s.told_skipped.today.length})</p>
              <ul className="divide-y divide-amber-100">
                {s.told_skipped.today.map(t => (
                  <li key={t.id} className="px-3 py-2 text-sm">
                    <Link to={`/customers/${t.customer_id}`} className="font-semibold text-gray-900 hover:underline">{t.name}</Link>
                    {t.address && <span className="text-gray-600"> · {t.address}</span>}
                    <p className={`mt-0.5 break-words ${t.owner_texted === false ? 'text-red-700' : 'text-gray-800'}`}>{t.line}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* One box on that grid stops both texts (services/textPrefs: mow_today and mow_next sit in the same kind). */}
      <p className={`text-xs text-gray-500 ${s.next || s.told_skipped ? 'border-t border-gray-200 pt-4' : ''}`}>
        To stop {s.next || s.done || s.report ? 'these texts' : 'it'} for one customer, untick "On the way / done" for them on <Link to="/customers/texts" className="text-brand-700 underline">Who gets which texts</Link>. A customer who replies STOP is taken off every text from the app.
      </p>
    </Section>
  );
}

// ── The visit photo report — the fourth block of Mowing-day texts ───────────
// Owner, Nov 1 2026: "Visit photo reports for every client (not just
// commercial): after a job is marked done, send the customer the crew's after
// photos plus 'Not happy with anything? Reply within 48 hours and we'll come
// back free.'" When a crew member taps Finish on ANY job, that customer gets
// one text with every photo the crew put on the visit and the promise
// (server/services/visitReport.js, kind 'visit_report'). It ships Off.
// Rehearsal texts each report to his own phone, never a customer. With it On
// the after-mow text stands down (its offer rides on the report when it is On).
// Like the other blocks, its buttons are outlined; the section's one filled
// button stays the morning list's.
function VisitReportBlock({ r }: { r: VisitReportSettings }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { isPractice } = useAuth();
  const [sample, setSample] = useState<{ ok: boolean; message: string; text?: string; photos?: number } | null>(null);
  const [open, setOpen] = useState(false);

  const save = useMutation((u: { report_mode: DayTextMode }) =>
    api.put('/day-texts/settings', u).then(res => res.data as { message: string; settings: DayTextSettings }), {
    onSuccess: (res) => { qc.setQueryData('day-texts-settings', res.settings); toast.success(res.message, { autoClose: 8000 }); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  // The body is empty on purpose: the server texts the owner's own phone and reads no number from anyone.
  const sendSample = useMutation(() => api.post('/day-texts/report/sample').then(res => res.data as { message: string; text: string; photos: number }), {
    onSuccess: (res) => setSample({ ok: true, message: res.message, text: res.text, photos: res.photos }),
    onError: (e: any) => setSample({ ok: false, message: e?.response?.data?.message || 'Could not send the test' }),
  });

  const busy = save.isLoading;
  const pickMode = (m: DayTextMode) => {
    if (m === r.mode || busy || readOnly) return;
    if (m === 'on' && !window.confirm(`Turn the visit photo report on?\n\nIt can start today. From the next Finish a crew member taps on any job, that customer gets one text with the crew's photos of the visit and "Not happy with anything? Reply within ${r.promise_hours} hours and we'll come back free." Businesses, yards left part-done, quote visits, to-dos, skips, and stops finished from the office or your own Field page never get one. The after-mow text stands down for these visits.`)) return;
    save.mutate({ report_mode: m });
  };

  const c = r.counts;
  const anyToday = c.sent + c.would_send + c.waiting + c.held_back + c.failed > 0;
  const showToday = r.mode !== 'off' || anyToday;
  const bad = c.failed > 0;
  const tally = [
    c.failed ? `${c.failed} did not go` : '', c.sent ? `${c.sent} sent` : '', c.would_send ? `${c.would_send} sent to you` : '',
    c.waiting ? `${c.waiting} waiting` : '', c.held_back ? `${c.held_back} held back` : '',
  ].filter(Boolean).join(' · ');
  const TONE: Record<VisitReportRow['status'], string> = { sent: 'text-emerald-700', would_send: 'text-gray-600', waiting: 'text-amber-800', left_out: 'text-amber-800', failed: 'text-red-700' };

  return (
    <div className="border-t border-gray-200 pt-4 space-y-4" id="visit-report-texts">
      <div>
        <h4 className={`${BLOCK_TITLE} mb-1`}>Visit photo report to the customer</h4>
        <p className="text-sm text-gray-600">{r.about.join(' ')}</p>
        <p className="text-sm text-gray-800 font-medium mt-1.5">{r.sets_off}</p>
      </div>

      {!r.photos_persistent && (
        <ul className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 space-y-0.5">
          <li>Photos on this server are wiped when the app is updated, so the pictures in an old text would stop opening. The words still go.</li>
        </ul>
      )}

      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">What the customer reads</p>
        <p className="mt-1 inline-block rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 text-sm text-gray-900">{r.mow_done_mode === 'on' ? r.example_text_with_offer : r.example_text}</p>
        <p className="mt-1 text-xs text-gray-500">
          With it: every photo the crew put on that visit, up to ten. A customer with no usable first name reads "{r.example_text_no_name}".
          {r.mow_done_mode === 'on'
            ? ' After the mow is On, so its refer-a-friend offer and their own link ride on the end, and the after-mow text itself stands down - one text per visit.'
            : ' Turn After the mow On too and its refer-a-friend offer and their own link ride on the end - still one text per visit.'}
        </p>
      </div>

      <fieldset disabled={readOnly || busy} className="space-y-2">
        <legend className="sr-only">Visit photo report: off, rehearsal or on</legend>
        {r.modes.map(m => {
          const on = r.mode === m.value;
          return (
            <label key={m.value} className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${on ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white'} ${readOnly ? 'opacity-60' : 'cursor-pointer'}`}>
              <input type="radio" name="visit-report-texts" className="mt-0.5 h-4 w-4 accent-brand-600" checked={on} disabled={readOnly || busy} onChange={() => pickMode(m.value)} />
              <span>
                <span className="block text-sm font-semibold text-gray-900">{m.label}</span>
                <span className="block text-xs text-gray-600 mt-0.5">{m.help}</span>
              </span>
            </label>
          );
        })}
      </fieldset>

      {showToday && (
        <div className={`rounded-md border px-3 py-2 text-sm ${bad ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>
          <p>{r.today_line}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={OUTLINE_BTN} aria-expanded={open} onClick={() => setOpen(v => !v)}>
          {open ? 'Hide today\'s list' : `Today's visit reports${tally ? ` (${tally})` : ''}`}
        </button>
        <button type="button" onClick={() => { setSample(null); sendSample.mutate(); }} disabled={sendSample.isLoading || readOnly} className={OUTLINE_BTN}
          aria-label="Send me a test of the visit photo report">
          {sendSample.isLoading ? 'Sending…' : 'Send me a test'}
        </button>
      </div>
      <p className="text-xs text-gray-500">The test goes to your own phone{r.owner_phone_ending ? ` ending ${r.owner_phone_ending}` : ''} and to nobody else, with the photos of the newest visit that has some (or the words alone when there is none).</p>
      {sample && (
        <div className={`rounded-md px-3 py-2 text-sm ${sample.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <p>{sample.message}</p>
          {sample.text && <p className="mt-1 text-gray-800">{isPractice ? 'It would say' : 'It says'}: {sample.text}</p>}
        </div>
      )}

      {open && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          {r.today.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-500">
              {r.mode === 'off' ? 'Nothing today. Nothing is written down while this is Off.' : `Nothing yet today. A line shows up here about ${r.wait_seconds} seconds after a crew member taps Finish on a job.`}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {r.today.map(row => (
                <li key={row.id} className="px-3 py-2">
                  <p className="text-sm">
                    <Link to={`/customers/${row.customer_id}`} className="font-semibold text-gray-900 underline-offset-2 hover:underline">{row.name}</Link>
                    {row.phone_ending && <span className="text-gray-500"> · phone ending {row.phone_ending}</span>}
                    {row.job_id && <Link to={`/jobs/${row.job_id}`} className="text-gray-500 underline-offset-2 hover:underline"> · the visit</Link>}
                  </p>
                  <p className={`text-sm mt-0.5 break-words ${TONE[row.status] || 'text-gray-800'}`}>{row.line}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── "After the mow" — the third block of Mowing-day texts ────────────────────
// Owner, Oct 20 2026: "Send a text to client when a mow is done with picture of
// the lawn in a AI template thing and it says refer a friend get 50%, or
// something." When a crew member taps Finish on a mow, that customer gets one
// text that day: the crew's photo of the lawn on a Keep Austin Mowed card (a
// caption the AI writes), the words "your lawn is done", and their own
// refer-a-friend link (server/services/mowDoneText.js, kind 'mow_done'). The
// offer — the percent and the sentence — is edited right here, and the text he
// will send is shown with it. It ships Off. "Send me a test" texts the card to
// his own phone and nobody else's. The section's one filled button stays the
// morning list's; this block's are outlined.
function AfterMowBlock({ d }: { d: DoneTextSettings }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { isPractice } = useAuth();
  const [pct, setPct] = useState(String(d.offer_pct));
  const [words, setWords] = useState(d.offer_words);
  const [sample, setSample] = useState<{ ok: boolean; message: string; text?: string; caption?: string | null; card_url?: string | null; ai_caption?: boolean } | null>(null);
  const [open, setOpen] = useState(false);
  // What the server has: the fields follow it when a save comes back or another tab changed it.
  useEffect(() => { setPct(String(d.offer_pct)); setWords(d.offer_words); }, [d.offer_pct, d.offer_words]);

  const save = useMutation((u: { done_mode?: DayTextMode; done_offer_pct?: string; done_offer_words?: string }) =>
    api.put('/day-texts/settings', u).then(r => r.data as { message: string; settings: DayTextSettings }), {
    onSuccess: (res) => { qc.setQueryData('day-texts-settings', res.settings); toast.success(res.message, { autoClose: 8000 }); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  // The body is empty on purpose: the server texts the owner's own phone and reads no number from anyone.
  const sendSample = useMutation(() => api.post('/day-texts/done/sample').then(r => r.data as { message: string; text: string; caption: string | null; card_url: string | null; ai_caption: boolean }), {
    onSuccess: (res) => setSample({ ok: true, message: res.message, text: res.text, caption: res.caption, card_url: res.card_url, ai_caption: res.ai_caption }),
    onError: (e: any) => setSample({ ok: false, message: e?.response?.data?.message || 'Could not send the test' }),
  });

  const busy = save.isLoading;
  const pickMode = (m: DayTextMode) => {
    if (m === d.mode || busy || readOnly) return;
    if (m === 'on' && !window.confirm('Turn "After the mow" on?\n\nIt can start today. From the next Finish a crew member taps on a mow, that customer gets one text with the picture of their lawn and their refer-a-friend link. Yards left part-done, skips, and stops finished from the office or your own Field page never send one.')) return;
    save.mutate({ done_mode: m });
  };
  const offerChanged = pct.trim() !== String(d.offer_pct) || words.trim() !== d.offer_words;
  const saveOffer = () => { if (!offerChanged || busy || readOnly) return; save.mutate({ done_offer_pct: pct.trim(), done_offer_words: words.trim() }); };
  // The offer as it will read with what is typed now — the same fill the server does — so he sees it before Save.
  const livePct = /^\d{1,3}$/.test(pct.trim()) && Number(pct.trim()) >= 1 && Number(pct.trim()) <= 100 ? pct.trim() : String(d.offer_pct);
  const liveOffer = (words.trim() || d.default_words).replace(/\{pct\}/g, livePct).replace(/[.!\s]+$/, '');
  const liveText = d.example_text.replace(d.offer_line, liveOffer);

  const c = d.counts;
  const anyToday = c.sent + c.would_send + c.waiting + c.held_back + c.failed > 0;
  const showToday = d.mode !== 'off' || anyToday;
  const bad = c.failed > 0;
  const tally = [
    c.failed ? `${c.failed} did not go` : '', c.sent ? `${c.sent} sent` : '', c.would_send ? `${c.would_send} would have gone` : '',
    c.waiting ? `${c.waiting} waiting` : '', c.held_back ? `${c.held_back} held back` : '',
  ].filter(Boolean).join(' · ');
  const TONE: Record<DoneTextRow['status'], string> = { sent: 'text-emerald-700', would_send: 'text-gray-600', waiting: 'text-amber-800', left_out: 'text-amber-800', failed: 'text-red-700' };

  return (
    <div className="border-t border-gray-200 pt-4 space-y-4" id="after-mow-texts">
      <div>
        <h4 className={`${BLOCK_TITLE} mb-1`}>After the mow: photo + referral offer</h4>
        <p className="text-sm text-gray-600">{d.about.join(' ')}</p>
        <p className="text-sm text-gray-800 font-medium mt-1.5">{d.sets_off}</p>
      </div>

      {/* What is and is not in place on this server: said plainly, because a text with no picture is a different text. */}
      {(!d.can_draw || !d.ai || !d.photos_persistent) && (
        <ul className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 space-y-0.5">
          {!d.can_draw && <li>The card drawer is not installed on this server, so the plain photo would go instead of the card.</li>}
          {!d.ai && <li>No AI key is set up, so every card carries the standard caption ("{d.example_caption}").</li>}
          {!d.photos_persistent && <li>Photos on this server are wiped when the app is updated, so the picture in an old text would stop opening. The words still go.</li>}
        </ul>
      )}

      {/* The offer: his to change. The percent and the sentence are settings, "{pct}" is where the number goes. */}
      <fieldset disabled={readOnly || busy} className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 space-y-3">
        <legend className="sr-only">The refer-a-friend offer</legend>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">The offer</p>
        <div className="grid grid-cols-1 sm:grid-cols-[7rem_1fr] gap-3">
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">Percent off</span>
            <div className="relative">
              <input type="number" min="1" max="100" step="1" className={`${INPUT} pr-7`} value={pct} onChange={e => setPct(e.target.value)} aria-label="Percent off" />
              <span className="absolute right-3 top-2 text-sm text-gray-500">%</span>
            </div>
          </label>
          <label className="block">
            <span className="block text-xs text-gray-600 mb-1">The sentence (write {'{pct}'} where the number goes)</span>
            <input type="text" className={INPUT} value={words} maxLength={120} onChange={e => setWords(e.target.value)} aria-label="The offer sentence" />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={OUTLINE_BTN} onClick={saveOffer} disabled={!offerChanged || busy || readOnly}>{busy ? 'Saving…' : 'Save the offer'}</button>
          {(words.trim() !== d.default_words || String(d.offer_pct) !== String(d.default_pct) || pct.trim() !== String(d.default_pct)) && (
            <button type="button" className="text-sm text-gray-600 underline" onClick={() => { setPct(String(d.default_pct)); setWords(d.default_words); }} disabled={busy || readOnly}>Back to the usual words</button>
          )}
        </div>
      </fieldset>

      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">What the customer reads</p>
        <p className="mt-1 inline-block rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 text-sm text-gray-900">{liveText}</p>
        <p className="mt-1 text-xs text-gray-500">
          That is one of the ways it can read: the opener, the hook before the offer and the sign-off change from text to text, and the words nod to the season ({d.season || 'the season'} right now). The picture is their lawn on a Keep Austin Mowed card: a magenta-to-coral frame, a "Freshly mowed · {'the day'}" stamp, the crew's first names ("by Daniel &amp; Sean"), a one-line caption the AI writes (or "{d.example_caption}"), and the offer. The link is their own refer-a-friend page. With no photo from the crew the card goes without the picture.
        </p>
        {!!d.example_texts?.length && (
          <details className="mt-1">
            <summary className="cursor-pointer text-xs text-gray-600 underline">Two more ways it can read</summary>
            <ul className="mt-1 space-y-1">
              {d.example_texts.map((t, i) => <li key={i} className="inline-block rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 text-sm text-gray-900">{t.replace(d.offer_line, liveOffer)}</li>)}
            </ul>
          </details>
        )}
      </div>

      <fieldset disabled={readOnly || busy} className="space-y-2">
        <legend className="sr-only">After-mow texts: off, rehearsal or on</legend>
        {d.modes.map(m => {
          const on = d.mode === m.value;
          return (
            <label key={m.value} className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${on ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white'} ${readOnly ? 'opacity-60' : 'cursor-pointer'}`}>
              <input type="radio" name="mow-done-texts" className="mt-0.5 h-4 w-4 accent-brand-600" checked={on} disabled={readOnly || busy} onChange={() => pickMode(m.value)} />
              <span>
                <span className="block text-sm font-semibold text-gray-900">{m.label}</span>
                <span className="block text-xs text-gray-600 mt-0.5">{m.help}</span>
              </span>
            </label>
          );
        })}
      </fieldset>

      {showToday && (
        <div className={`rounded-md border px-3 py-2 text-sm ${bad ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>
          <p>{d.today_line}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={OUTLINE_BTN} aria-expanded={open} onClick={() => setOpen(v => !v)}>
          {open ? 'Hide today\'s list' : `Today's after-mow texts${tally ? ` (${tally})` : ''}`}
        </button>
        <button type="button" onClick={() => { setSample(null); sendSample.mutate(); }} disabled={sendSample.isLoading || readOnly} className={OUTLINE_BTN}
          aria-label="Send me a test of the after-mow text">
          {sendSample.isLoading ? 'Sending…' : 'Send me a test'}
        </button>
      </div>
      <p className="text-xs text-gray-500">The test goes to your own phone{d.owner_phone_ending ? ` ending ${d.owner_phone_ending}` : ''} and to nobody else, with a card made from a recent crew photo (or a drawn lawn when there is none).</p>
      {sample && (
        <div className={`rounded-md px-3 py-2 text-sm ${sample.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <p>{sample.message}</p>
          {sample.text && <p className="mt-1 text-gray-800">{isPractice ? 'It would say' : 'It says'}: {sample.text}</p>}
          {sample.caption && <p className="mt-1 text-gray-800">The card's caption{sample.ai_caption ? ' (written by the AI)' : ''}: {sample.caption}</p>}
          {sample.card_url && <a href={sample.card_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-brand-700 underline">Open the card</a>}
        </div>
      )}

      {open && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          {d.today.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-500">
              {d.mode === 'off' ? 'Nothing today. Nothing is written down while this is Off.' : `Nothing yet today. A line shows up here about ${d.wait_seconds} seconds after a crew member taps Finish on a mow.`}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {d.today.map(r => (
                <li key={r.id} className="px-3 py-2">
                  <p className="text-sm">
                    <Link to={`/customers/${r.customer_id}`} className="font-semibold text-gray-900 underline-offset-2 hover:underline">{r.name}</Link>
                    {r.phone_ending && <span className="text-gray-500"> · phone ending {r.phone_ending}</span>}
                    {r.has_picture && <span className="text-gray-500"> · with the picture</span>}
                  </p>
                  <p className={`text-sm mt-0.5 break-words ${TONE[r.status] || 'text-gray-800'}`}>{r.line}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── "You're next" — the second block of Mowing-day texts ─────────────────────
// The other half of the owner's ask ("eg, your next"): when a crew member taps
// Finish on a yard, the customer at the next stop gets one text saying the
// crew is heading over (server/services/dayTexts.js, kind 'mow_next'). A Skip
// never sends one, and neither does a stop marked done from the office or
// finished on his own Field page.
//
// Nothing on this screen can send one — only that live Finish does, and then
// only after the server has waited and looked at the route again. A text that
// lands after the crew has come and gone is worse than no text, so the screen
// is built around Rehearsal: no you're-next text is sent, to customers or to
// him, and the list shows every text that WOULD have gone with where it
// landed: before the crew started there (and by how many minutes), after they
// had already started, or at a stop that was then skipped or never started.
// Each of those is its own line over the list — that is how he judges the
// timing before On. On the day he moves the switch from Rehearsal to On the
// list has both kinds of row; the texts that went and the ones that only would
// have are never counted in the same line.
//
// Unlike the morning text, On is not "from tomorrow": it is one text per
// Finish, so it can start with the very next yard — the box that asks says so.
// As in the block above, the sentences, the choices and every line of the list
// are the server's own words, and a phone number is only ever its last four.
//
// Its two buttons are outlined. The section has ONE filled button, the morning
// list's (owner, Oct 7 2026: "too many accept buttons").
type NextTextEvent = {
  id: number; job_id: number | null; customer_id: number;
  name: string; phone_ending: string | null; address: string | null;
  after_name: string | null;                // whose yard the crew had just finished or skipped
  // 'would_send' = Rehearsal (no you're-next text was sent); 'claimed' = started sending and never heard how it ended.
  status: 'sent' | 'would_send' | 'held_back' | 'failed' | 'claimed';
  at_label: string | null;                  // "10:02 AM", Austin time
  line: string | null;                      // the exact words, or "Held back: <why>", or "Did not go: <why>"
  note: string | null;
  // Where a text (or would-be text) landed against the crew's Start there — null on a held-back row.
  landed?: NextLanded | null;
  // "crew started there 6 min later" / "landed 3 min after the crew had already started there" (a rehearsal row:
  // "would have landed …"; a not-sure row: "if it went, it landed …") / "that stop was skipped" / "the crew has not
  // started there" — the server's words for that group.
  started_label: string | null;
};
type NextLanded = 'before' | 'after' | 'skipped' | 'moved' | 'no_start' | 'not_started';
// A line over the list is about ONE kind of row: texts that went, ones that only would have (Rehearsal), or ones
// whose send was started and never heard from ('claimed', group 'unsure').
type NextTimingGroup = { kind?: 'sent' | 'would_send' | 'claimed'; group: NextLanded | 'unsure'; count: number; line: string };
type NextTextList = {
  day: string; day_label: string; is_today: boolean; mode: DayTextMode; headline: string;
  list_title: string; list_note: string;    // "What would have gone today" in Rehearsal — the server's words
  counts: NextTextCounts;
  timing_line: string | null;               // every group's sentence joined (an older server sends only this)
  // One sentence per group: "4 texts landed before the crew started there, about 2 minutes ahead (…)." ·
  // "3 texts landed after the crew had already started there." · "1 text went to a stop that was then skipped."
  // Rehearsal rows have their own: "3 texts would have landed before the crew started there, about 5 minutes ahead."
  timing_groups?: NextTimingGroup[];
  // Stops where nothing was set off, one line per route: "It stayed quiet at 12 stops on Thursday A: the yard before
  // each was marked done from the office or your Field page, not by a crew member's Finish." / "… was skipped, not finished."
  quiet_lines?: { kind: 'office' | 'after_skip'; route_id: number | null; route_name: string | null; count: number; line: string }[];
  events: NextTextEvent[];                  // oldest first
};
// The tone of each group, on its line over the list and under its row: after the crew had started is the one to see first.
const NEXT_LANDED_TONE: Record<NextLanded | 'unsure', string> = {
  before: 'text-gray-900', after: 'text-red-700', skipped: 'text-amber-800', not_started: 'text-amber-800', moved: 'text-gray-600', no_start: 'text-gray-600', unsure: 'text-amber-800',
};

/** "1 did not go · 1 not sure · 4 sent · 2 held back" — what went wrong first, as in the morning tally. */
function nextTextTally(c: NextTextCounts): string {
  return [
    c.failed ? `${c.failed} did not go` : '',
    c.claimed ? `${c.claimed} not sure` : '',
    c.sent ? `${c.sent} sent` : '',
    c.would_send ? `${c.would_send} would have gone` : '',
    c.held_back ? `${c.held_back} held back` : '',
    c.office ? `${c.office} quiet after an office or Field-page finish` : '',
    c.after_skip ? `${c.after_skip} quiet after a skip` : '',
  ].filter(Boolean).join(' · ');
}

/** What a row says after the name, and in what tone. A held-back row and one that did not go say so in their own line. */
const NEXT_EVENT_WORD: Record<NextTextEvent['status'], { word: string; wordTone: string; lineTone: string }> = {
  sent: { word: 'sent', wordTone: 'text-emerald-700', lineTone: 'text-gray-800' },
  would_send: { word: 'would have gone', wordTone: 'text-gray-600', lineTone: 'text-gray-800' },
  claimed: { word: 'not sure it went', wordTone: 'text-amber-800', lineTone: 'text-gray-800' },
  held_back: { word: '', wordTone: '', lineTone: 'text-amber-800' },
  failed: { word: '', wordTone: '', lineTone: 'text-red-700' },
};

function YoureNextBlock({ n }: { n: NextTextSettings }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { isPractice } = useAuth();
  const [open, setOpen] = useState(false);
  const [which, setWhich] = useState<'today' | 'yesterday'>('today');
  const [sample, setSample] = useState<{ ok: boolean; message: string; text?: string } | null>(null);

  // The list is only asked for once it is opened; under the switch sits the sentence the settings came with.
  // While today's list is open it is read again each minute: rows appear as the crew works, and "crew started
  // there N min later" is filled in by the server at the Start tap. "Today" is the server's own Austin day; yesterday is asked
  // for by date — the morning after a rehearsal, yesterday is the list he came to read.
  const todayList = useQuery<NextTextList>(['day-texts-next', 'today'],
    () => api.get('/day-texts/next').then(r => r.data),
    { retry: false, enabled: open && which === 'today', refetchInterval: open && which === 'today' ? 60_000 : false });
  const yesterdayList = useQuery<NextTextList>(['day-texts-next', 'yesterday'],
    () => api.get('/day-texts/next', { params: { date: isoPlusDays(businessToday(), -1) } }).then(r => r.data),
    { retry: false, enabled: open && which === 'yesterday' });

  const save = useMutation((m: DayTextMode) =>
    api.put('/day-texts/settings', { next_mode: m }).then(r => r.data as { message: string; settings: DayTextSettings }), {
    onSuccess: (d) => { qc.setQueryData('day-texts-settings', d.settings); qc.invalidateQueries('day-texts-next'); toast.success(d.message, { autoClose: 8000 }); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  // The body is empty on purpose: the server texts the owner's own phone and reads no number from anyone.
  const sendSample = useMutation(() => api.post('/day-texts/next/sample').then(r => r.data as { message: string; text: string }), {
    onSuccess: (d) => setSample({ ok: true, message: d.message, text: d.text }),
    onError: (e: any) => setSample({ ok: false, message: e?.response?.data?.message || 'Could not send the sample' }),
  });

  const busy = save.isLoading;
  const pickMode = (m: DayTextMode) => {
    if (m === n.mode || busy || readOnly) return;
    // On is the only choice that reaches customers, so it is the only one that asks — and it says it can start today.
    if (m === 'on' && !window.confirm('Turn "You\'re next" on?\n\nIt can start today. From the next Finish a crew member taps on a route, the customer at the next stop gets one text saying the crew is heading over. A Skip never sends one, and neither does a stop marked done from the office or finished on your own Field page.')) return;
    save.mutate(m);
  };

  // Today's own sentence and numbers: the opened list's when it was read under this same choice (it is the fresher
  // of the two), else the ones the settings came with. Never a sentence about a choice the switch is no longer on.
  const fresh = todayList.data && todayList.data.mode === n.mode ? todayList.data : null;
  const counts = fresh ? fresh.counts : n.counts;
  const todayLine = fresh ? fresh.headline : n.today_line;
  const anyToday = counts.sent + counts.would_send + counts.held_back + counts.failed + counts.claimed + (counts.office || 0) + (counts.after_skip || 0) > 0;
  // With it off and nothing written down the sentence is only "Off. No you're-next text goes.", which the choice already says.
  const showToday = n.mode !== 'off' || anyToday;
  // Amber when a text was refused or its end is unknown: those are the rows to read first.
  const bad = counts.failed > 0 || counts.claimed > 0;
  // In Rehearsal the list is what WOULD have gone, and the button that opens it says so.
  const rehearsed = counts.sent + counts.failed + counts.claimed === 0 && (n.mode === 'rehearsal' || counts.would_send > 0);
  const listButton = rehearsed ? 'What would have gone today' : 'Today\'s you\'re-next texts';

  const list = which === 'today' ? todayList : yesterdayList;
  const p = list.data;

  return (
    <div className="border-t border-gray-200 pt-4 space-y-4" id="youre-next-texts">
      <div>
        <h4 className={`${BLOCK_TITLE} mb-1`}>You're next</h4>
        <p className="text-sm text-gray-600">{n.about.join(' ')}</p>
        {/* Whose tap counts, and what never does — so a Skip, an office tick or his own Field page setting nothing off is not a surprise. */}
        {n.sets_off && <p className="text-sm text-gray-800 font-medium mt-1.5">{n.sets_off}</p>}
      </div>
      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">What the customer reads</p>
        <p className="mt-1 inline-block rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 text-sm text-gray-900">{n.example_text}</p>
        <p className="mt-1 text-xs text-gray-500">It gives no minutes and no clock time. With no address on file it says "heading your way now". The first stop of a route does not get one, because there is no yard before it.</p>
      </div>

      <fieldset disabled={readOnly || busy} className="space-y-2">
        <legend className="sr-only">You're next texts: off, rehearsal or on</legend>
        {n.modes.map(m => {
          const on = n.mode === m.value;
          return (
            <label key={m.value} className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${on ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white'} ${readOnly ? 'opacity-60' : 'cursor-pointer'}`}>
              <input type="radio" name="mow-next-texts" className="mt-0.5 h-4 w-4 accent-brand-600" checked={on} disabled={readOnly || busy} onChange={() => pickMode(m.value)} />
              <span>
                <span className="block text-sm font-semibold text-gray-900">{m.label}</span>
                <span className="block text-xs text-gray-600 mt-0.5">{m.help}</span>
              </span>
            </label>
          );
        })}
      </fieldset>

      {showToday && (
        <div className={`rounded-md border px-3 py-2 text-sm ${bad ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>
          {/* The server's own sentence: "Rehearsal. 4 would have gone, 2 held back today. No you're-next text was sent, to customers or to you." */}
          <p>{todayLine}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {/* Both outlined — the section's one filled button is the morning list's. This one only reads, so a view-only login can open it too. */}
        <button type="button" className={OUTLINE_BTN} aria-expanded={open}
          onClick={() => { if (!open) setWhich('today'); setOpen(v => !v); }}>
          {open ? 'Hide the you\'re-next list' : listButton}
        </button>
        <button type="button" onClick={() => { setSample(null); sendSample.mutate(); }} disabled={sendSample.isLoading || readOnly} className={OUTLINE_BTN}
          aria-label="Send me a sample of the you're next text">
          {sendSample.isLoading ? 'Sending…' : 'Send me a sample'}
        </button>
      </div>
      <p className="text-xs text-gray-500">The sample goes to your own phone{n.owner_phone_ending ? ` ending ${n.owner_phone_ending}` : ''} and to nobody else.</p>
      {sample && (
        <div className={`rounded-md px-3 py-2 text-sm ${sample.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {/* The server's answer says by itself when this is the practice app and no phone was texted. */}
          <p>{sample.message}</p>
          {sample.text && <p className="mt-1 text-gray-800">{isPractice ? 'It would say' : 'It says'}: {sample.text}</p>}
        </div>
      )}

      {open && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50">
            {(['today', 'yesterday'] as const).map(d => (
              <button key={d} type="button" onClick={() => setWhich(d)} aria-pressed={which === d}
                className={`px-3 py-1 rounded-full border text-xs ${which === d ? 'border-gray-900 text-gray-900 font-semibold bg-white' : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'}`}>
                {d === 'today' ? 'Today' : 'Yesterday'}
              </button>
            ))}
            {p && <span className="text-xs text-gray-600">{p.day_label}{nextTextTally(p.counts) ? ` · ${nextTextTally(p.counts)}` : ''}</span>}
          </div>
          {list.isLoading && <p className="px-3 py-4 text-sm text-gray-500">Loading…</p>}
          {list.isError && <p className="px-3 py-4 text-sm text-red-700">Could not load the list. Close it and open it again.</p>}
          {p && (
            <>
              {/* What this list is a list OF — texts that went, or in Rehearsal what WOULD have gone — always said first. */}
              <div className="px-3 py-2">
                <p className="text-sm font-semibold text-gray-900">{p.list_title}</p>
                <p className="text-xs text-gray-600 mt-0.5">{p.list_note}</p>
                {/* What he judges the timing by, over the day — each outcome its own line, never folded into another:
                    landed before the crew started (and how far ahead), landed after they had already started, went to
                    a stop that was then skipped, went to a stop not started. Texts that went and rehearsal rows that
                    only would have are separate lines too (the server's words say which), so the key carries both. */}
                {p.timing_groups && p.timing_groups.length > 0 ? (
                  <ul className="mt-1.5 space-y-0.5" aria-label="How the timing went">
                    {p.timing_groups.map(g => (
                      <li key={`${g.kind || 'all'}-${g.group}`} className={`text-sm ${g.group === 'before' || g.group === 'after' ? 'font-semibold' : ''} ${NEXT_LANDED_TONE[g.group] || 'text-gray-900'}`}>{g.line}</li>
                    ))}
                  </ul>
                ) : p.timing_line ? <p className="text-sm text-gray-900 mt-1.5">{p.timing_line}</p> : null}
              </div>
              {p.events.length === 0 && !(p.quiet_lines && p.quiet_lines.length) && (
                <p className="px-3 py-3 border-t border-gray-100 text-sm text-gray-500">
                  {!p.is_today ? `Nothing was written down on ${p.day_label}.`
                    : p.mode === 'off' ? 'Nothing today. Nothing is written down while this is Off.'
                    : `Nothing yet today. A line shows up here about ${n.hold_seconds} seconds after a crew member taps Finish on a route.`}
                </p>
              )}
              {p.events.length > 0 && (
                <ul className="divide-y divide-gray-100 border-t border-gray-100">
                  {p.events.map(e => {
                    const w = NEXT_EVENT_WORD[e.status] || NEXT_EVENT_WORD.would_send;
                    // Under the words: which yard (a held-back row has no text to name it) and whose yard came before.
                    const where = [e.status === 'held_back' || e.status === 'failed' ? e.address : null, e.after_name ? `Yard before: ${e.after_name}` : null].filter(Boolean).join(' · ');
                    return (
                      <li key={e.id} className="px-3 py-2">
                        <p className="text-sm">
                          {e.at_label && <span className="font-semibold text-gray-900 tabular-nums">{e.at_label} · </span>}
                          <Link to={`/customers/${e.customer_id}`} className="font-semibold text-gray-900 hover:underline">{e.name}</Link>
                          {e.phone_ending && <span className="text-gray-500"> · phone ending {e.phone_ending}</span>}
                          {w.word && <span className={w.wordTone}> · {w.word}</span>}
                        </p>
                        {e.line && <p className={`text-sm mt-0.5 break-words ${w.lineTone}`}>{e.line}</p>}
                        {(e.started_label || where) && (
                          <p className="text-xs text-gray-600 mt-0.5">
                            {/* The server's words, with a capital: "Crew started there 6 min later", "Landed 3 min after the
                                crew had already started there", "That stop was skipped", "The crew has not started there". */}
                            {e.started_label && <span className={`font-semibold ${e.landed ? NEXT_LANDED_TONE[e.landed] || 'text-gray-900' : 'text-gray-900'}`}>{e.started_label.charAt(0).toUpperCase() + e.started_label.slice(1)}</span>}
                            {e.started_label && where ? ' · ' : ''}{where}
                          </p>
                        )}
                        {e.note && <p className="text-xs text-amber-800 mt-0.5">{e.note}</p>}
                      </li>
                    );
                  })}
                </ul>
              )}
              {/* Stops where nothing was set off — the yard before was marked done from the office or his Field page,
                  or skipped: one line per route, so "Complete the rest" or a rain day of skips does not bury the texts above. */}
              {p.quiet_lines && p.quiet_lines.length > 0 && (
                <ul className="border-t border-gray-100 bg-gray-50 px-3 py-2 space-y-0.5">
                  {p.quiet_lines.map(o => <li key={`${o.kind}-${o.route_id ?? 'none'}`} className="text-xs text-gray-600">{o.line}</li>)}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
